import { useState, useMemo, useCallback, useRef, useEffect, useLayoutEffect, type ReactNode } from "react";
import { createPortal } from "react-dom";
import type { TranslateFn } from "../../i18n";
import type { FitAreaInfo, NormalizedSection } from "../../types";
import { buildFitInfo } from "../../sizing/fit-compute";
import { useElapsedSeconds } from "../utils/useElapsed";
import type { ViewState, SizeGuide, SizingResult, SizeGuideField } from "../types";
import type { StyleMatchSelection } from "../../api-client";
import type { MeasurementLines, BodyLandmarks } from "../../pose-detect";
import { detectMeasurementLines } from "../../pose-detect";
// SIZE_CONVERSIONS removed — backend owns all international size data now.
// The SDK is a thin renderer; conversion tables live in sizing.service.ts.
import { SparkleIcon, CameraIcon, UploadIcon, GarmentIcon, ArrowLeftIcon, UserIcon, ShoppingBagIcon, ExternalLinkIcon, CheckIcon } from "../icons";
import garmentJacketImg from "../assets/icon-garment-jacket.png";
import garmentVestImg from "../assets/icon-garment-vest.png";
import garmentPantsImg from "../assets/icon-garment-pants.png";

type ProfileCompletionCtaConfig = {
  onClick: () => void;
};

function ProfileCompletionCta({
  onClick,
  isMobile,
  placement,
  t,
}: ProfileCompletionCtaConfig & { isMobile?: boolean; placement?: "header"; t: TranslateFn }) {
  const title = t("Save your fit profile");
  const body = t("Reuse these measurements on future products.");
  const action = t("Save Profile");
  const isHeader = placement === "header" && !isMobile;

  return (
    <button
      type="button"
      className="ps-profile-completion-cta"
      onClick={onClick}
      style={{
        width: "100%",
        border: "1px solid rgba(33, 84, 239, 0.16)",
        background: "linear-gradient(135deg, #FFFFFF 0%, rgba(33,84,239,0.055) 100%)",
        borderRadius: isMobile ? 12 : "0.7vw",
        padding: isMobile ? "13px 14px" : isHeader ? "clamp(8px, 0.55vw, 10px) clamp(10px, 0.7vw, 14px)" : "0.5vw 0.7vw",
        marginTop: isMobile ? 12 : isHeader ? 0 : "0.48vw",
        cursor: "pointer",
        fontFamily: "inherit",
        textAlign: "left",
        display: "flex",
        flexDirection: isMobile ? "column" : "row",
        alignItems: isMobile ? "stretch" : "center",
        justifyContent: "space-between",
        gap: isMobile ? 10 : isHeader ? "0.62vw" : "0.7vw",
        boxShadow: "0 12px 28px -22px rgba(33,84,239,0.52)",
      }}
    >
      <span style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : isHeader ? "0.5vw" : "0.6vw", minWidth: 0, width: isMobile || isHeader ? "100%" : "auto" }}>
        <span
          aria-hidden="true"
          style={{
            width: isMobile ? 32 : isHeader ? "clamp(26px, 1.65vw, 30px)" : "1.55vw",
            height: isMobile ? 32 : isHeader ? "clamp(26px, 1.65vw, 30px)" : "1.55vw",
            borderRadius: "50%",
            background: "var(--ps-accent)",
            color: "#fff",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          <UserIcon size={isMobile ? 16 : isHeader ? 16 : 14} />
        </span>
        <span style={{ minWidth: 0 }}>
          <span style={{ display: "block", color: "var(--ps-text-primary)", fontSize: isMobile ? 14 : isHeader ? "clamp(12px, 0.72vw, 14px)" : "0.68vw", fontWeight: 800, lineHeight: 1.15 }}>
            {title}
          </span>
          <span style={{ display: "block", color: "var(--ps-text-secondary)", fontSize: isMobile ? 12 : isHeader ? "clamp(10px, 0.52vw, 11px)" : "0.56vw", lineHeight: 1.22, marginTop: isMobile ? 3 : "0.1vw" }}>
            {body}
          </span>
        </span>
      </span>
      <span
        style={{
          color: "var(--ps-accent)",
          background: isMobile ? "rgba(33,84,239,0.08)" : "transparent",
          border: isMobile ? "1px solid rgba(33,84,239,0.14)" : 0,
          borderRadius: isMobile ? 999 : 0,
          padding: isMobile ? "7px 10px" : 0,
          fontSize: isMobile ? 12 : isHeader ? "clamp(10px, 0.56vw, 11px)" : "0.62vw",
          fontWeight: 800,
          whiteSpace: "nowrap",
          flexShrink: 0,
          alignSelf: isMobile ? "flex-start" : "center",
          marginLeft: isMobile ? 42 : 0,
          lineHeight: 1,
        }}
      >
        {action} &rarr;
      </span>
    </button>
  );
}

function ResultImageLoadingOverlay({ t }: { t: TranslateFn }) {
  return (
    <div className="ps-result-image-loading" role="status" aria-live="polite">
      <span className="ps-result-image-loading-spinner" aria-hidden="true" />
      <span>{t("Loading try-on result")}</span>
    </div>
  );
}

function TryOnExperienceFeedback({
  t,
  isMobile,
  onSubmit,
}: {
  t: TranslateFn;
  isMobile?: boolean;
  onSubmit?: (input: { rating?: number; note?: string }) => Promise<void>;
}) {
  const [rating, setRating] = useState(0);
  const [note, setNote] = useState("");
  const [expanded, setExpanded] = useState(false);
  const [closing, setClosing] = useState(false);
  const [status, setStatus] = useState<"idle" | "submitting" | "sent" | "error">("idle");
  const noteRef = useRef<HTMLTextAreaElement | null>(null);
  const closeTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const canEdit = status !== "submitting" && status !== "sent";
  const canSubmit = (rating > 0 || note.trim().length > 0) && canEdit;

  const resizeNote = useCallback((node: HTMLTextAreaElement | null) => {
    if (!node) return;
    node.style.height = "auto";
    node.style.height = `${Math.min(node.scrollHeight, isMobile ? 86 : 78)}px`;
  }, [isMobile]);

  useEffect(() => {
    resizeNote(noteRef.current);
  }, [note, resizeNote]);

  useEffect(() => () => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
  }, []);

  useEffect(() => {
    if (!isMobile || !expanded || typeof document === "undefined") return;
    document.body.classList.add("ps-tryon-mobile-feedback-open");
    return () => {
      document.body.classList.remove("ps-tryon-mobile-feedback-open");
    };
  }, [expanded, isMobile]);

  const openFeedback = useCallback(() => {
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    closeTimerRef.current = null;
    setClosing(false);
    setExpanded(true);
  }, []);

  const closeFeedback = useCallback(() => {
    if (!isMobile) {
      setExpanded(false);
      return;
    }
    if (closeTimerRef.current) window.clearTimeout(closeTimerRef.current);
    setClosing(true);
    closeTimerRef.current = window.setTimeout(() => {
      setExpanded(false);
      setClosing(false);
      closeTimerRef.current = null;
    }, 180);
  }, [isMobile]);

  const submitFeedback = useCallback(async () => {
    if (!canSubmit) return;
    setStatus("submitting");
    try {
      await onSubmit?.({ rating: rating || undefined, note: note.trim() || undefined });
      setStatus("sent");
    } catch {
      setStatus("error");
    }
  }, [canSubmit, note, onSubmit, rating]);

  if (!expanded) {
    return (
      <button
        type="button"
        className={`ps-tryon-feedback-toggle${isMobile ? " ps-mobile" : ""}`}
        onClick={openFeedback}
        aria-expanded="false"
      >
        <svg viewBox="0 0 24 24" aria-hidden="true">
          <path d="M12 3.1l2.7 5.45 6.02.88-4.36 4.25 1.03 6-5.39-2.83-5.39 2.83 1.03-6-4.36-4.25 6.02-.88L12 3.1z" />
        </svg>
        {t("Rate try-on")}
      </button>
    );
  }

  const feedbackPanel = (
    <div className={`ps-tryon-feedback${isMobile ? " ps-mobile" : ""}${closing ? " ps-closing" : ""}`} aria-label={t("Rate your try-on")}>
      <div className="ps-tryon-feedback-top">
        <span className="ps-tryon-feedback-title">{t("Rate your try-on")}</span>
        <button
          type="button"
          className="ps-tryon-feedback-close"
          onClick={closeFeedback}
          aria-label={t("Close")}
        >
          <svg viewBox="0 0 24 24" aria-hidden="true">
            <path d="M6 6l12 12M18 6L6 18" />
          </svg>
        </button>
        <div className="ps-tryon-feedback-stars" role="radiogroup" aria-label={t("Rating")}>
          {[1, 2, 3, 4, 5].map((value) => (
            <button
              key={value}
              type="button"
              className={`ps-tryon-feedback-star${value <= rating ? " is-active" : ""}`}
              onClick={() => {
                if (!canEdit) return;
                setRating(value);
                setStatus("idle");
              }}
              disabled={!canEdit}
              role="radio"
              aria-checked={rating === value}
              aria-label={`${value} ${t("stars")}`}
            >
              <svg viewBox="0 0 24 24" aria-hidden="true">
                <path d="M12 3.1l2.7 5.45 6.02.88-4.36 4.25 1.03 6-5.39-2.83-5.39 2.83 1.03-6-4.36-4.25 6.02-.88L12 3.1z" />
              </svg>
            </button>
          ))}
        </div>
      </div>
      <div className="ps-tryon-feedback-form">
        <textarea
          ref={noteRef}
          className="ps-tryon-feedback-note"
          value={note}
          onChange={(event) => {
            if (!canEdit) return;
            setNote(event.target.value);
            setStatus("idle");
            resizeNote(event.currentTarget);
          }}
          disabled={!canEdit}
          placeholder={t("Optional note")}
          maxLength={1000}
          rows={1}
        />
        <button
          type="button"
          className="ps-tryon-feedback-submit"
          onClick={submitFeedback}
          disabled={!canSubmit}
          aria-label={t("Send feedback")}
        >
          {status === "submitting" ? t("Sending") : status === "sent" ? t("Sent") : t("Send")}
        </button>
      </div>
      {status === "error" && <span className="ps-tryon-feedback-status">{t("Couldn't send. Try again.")}</span>}
    </div>
  );

  if (isMobile) {
    return (
      <>
        <button
          type="button"
          className={`ps-tryon-feedback-backdrop ps-mobile${closing ? " ps-closing" : ""}`}
          onClick={closeFeedback}
          aria-label={t("Close")}
        />
        {feedbackPanel}
      </>
    );
  }

  return feedbackPanel;
}

function ResultImageActionBar({
  showLines,
  canShowFit,
  onToggleFit,
  onOpenImage,
  onDownload,
  t,
}: {
  showLines: boolean;
  canShowFit?: boolean;
  onToggleFit?: () => void;
  onOpenImage?: () => void;
  onDownload?: () => void;
  t: TranslateFn;
}) {
  if (!canShowFit && !onOpenImage && !onDownload) return null;
  return (
    <div className="ps-result-image-action-bar">
      {canShowFit && onToggleFit && (
        <button
          type="button"
          className={`ps-result-image-action ps-result-image-action-fit${showLines ? " is-active" : ""}`}
          aria-pressed={showLines}
          onClick={(event) => {
            event.currentTarget.blur();
            onToggleFit();
          }}
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <line x1="4" y1="9" x2="20" y2="9" />
            <line x1="4" y1="15" x2="20" y2="15" />
            <circle cx="7" cy="9" r="2" />
            <circle cx="17" cy="15" r="2" />
          </svg>
          {showLines ? t("Hide Fit") : t("Show Fit")}
        </button>
      )}
      {onOpenImage && (
        <button type="button" className="ps-result-image-action" onClick={onOpenImage}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M8 3H5a2 2 0 0 0-2 2v3" />
            <path d="M16 3h3a2 2 0 0 1 2 2v3" />
            <path d="M8 21H5a2 2 0 0 1-2-2v-3" />
            <path d="M16 21h3a2 2 0 0 0 2-2v-3" />
          </svg>
          {t("Full Size")}
        </button>
      )}
      {onDownload && (
        <button type="button" className="ps-result-image-action" onClick={onDownload}>
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.3" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
            <path d="M7 10l5 5 5-5" />
            <path d="M12 15V3" />
          </svg>
          {t("Download")}
        </button>
      )}
    </div>
  );
}

function MobileChoiceSelect({
  value,
  options,
  onChange,
  ariaLabel,
}: {
  value: string;
  options: Array<{ value: string; label: string }>;
  onChange: (value: string) => void;
  ariaLabel: string;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement | null>(null);
  const selected = options.find((option) => option.value === value) || options[0];

  useEffect(() => {
    if (!isOpen) return;
    const handlePointerDown = (event: PointerEvent) => {
      if (!rootRef.current || rootRef.current.contains(event.target as Node)) return;
      setIsOpen(false);
    };
    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isOpen]);

  return (
    <div className="ps-mobile-choice-select" data-open={isOpen ? "true" : "false"} ref={rootRef}>
      <button
        type="button"
        className="ps-mobile-choice-trigger"
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-label={ariaLabel}
        onClick={() => setIsOpen((open) => !open)}
      >
        <span>{selected?.label || ""}</span>
        <svg viewBox="0 0 20 20" aria-hidden="true">
          <path d="M5.5 7.5 10 12l4.5-4.5" />
        </svg>
      </button>
      {isOpen && (
        <div className="ps-mobile-choice-menu" role="listbox" aria-label={ariaLabel}>
          {options.map((option) => (
            <button
              key={option.value || "__standard"}
              type="button"
              role="option"
              aria-selected={option.value === value}
              className={`ps-mobile-choice-option${option.value === value ? " ps-selected" : ""}`}
              onClick={() => {
                onChange(option.value);
                setIsOpen(false);
              }}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// Local garment icon picker for the result cards. The tuxedo product stores
// blue Cloudinary PNGs in sizeGuide.sectionImages — those render the section
// images bundled with the SDK chart data. For products without sectionImages,
// we map the common section names (jacket / pants / vest / coat / etc.) to
// our built-in line-art PNGs so every card gets a consistent visual anchor
// instead of the generic SVG fallback.
function garmentIconForSection(name: string): string | null {
  const n = name.toLowerCase();
  if (n.includes("jacket") || n.includes("blazer") || n.includes("coat")) return garmentJacketImg as unknown as string;
  if (n.includes("pant") || n.includes("trouser") || n.includes("suit pants")) return garmentPantsImg as unknown as string;
  if (n.includes("vest") || n.includes("waistcoat") || n.includes("gilet")) return garmentVestImg as unknown as string;
  return null;
}

type OutfitDemoItem = {
  title: string;
  note: string;
  image?: string;
  href?: string;
};

type OutfitDemoCategory = {
  key: string;
  label: string;
  guidance: string;
  items: OutfitDemoItem[];
};

type OutfitDemoLook = {
  id: string;
  index: string;
  title: string;
  subtitle: string;
  score: string;
  categories: OutfitDemoCategory[];
};

function OutfitRecommendationDemo({
  productImage,
  productTitle,
  productImages,
  carouselItems,
  isMobile,
  t,
}: {
  productImage: string;
  productTitle: string;
  productImages?: string[];
  carouselItems?: ProductPhotoCarouselItem[];
  isMobile?: boolean;
  t: TranslateFn;
}) {
  const imagePool = useMemo(() => {
    const seen = new Set<string>();
    const entries: ProductPhotoCarouselItem[] = [];
    const push = (item: ProductPhotoCarouselItem) => {
      if (!item.image || seen.has(item.image)) return;
      seen.add(item.image);
      entries.push(item);
    };
    push({ image: productImage, title: productTitle });
    carouselItems?.forEach(push);
    productImages?.forEach((image) => push({ image, title: productTitle }));
    return entries;
  }, [carouselItems, productImage, productImages, productTitle]);

  const imageAt = useCallback((index: number, fallbackSection: string): ProductPhotoCarouselItem => {
    if (imagePool.length > index) return imagePool[index];
    const icon = garmentIconForSection(fallbackSection);
    return {
      image: icon || productImage,
      title: productTitle,
    };
  }, [imagePool, productImage, productTitle]);

  const looks = useMemo<OutfitDemoLook[]>(() => {
    const currentProduct = productTitle || t("Current product");
    const topA = imageAt(1, "jacket");
    const topB = imageAt(2, "vest");
    const bottomA = imageAt(3, "pants");
    const shoeA = imageAt(4, "shoe");
    const accessoryA = imageAt(5, "watch");
    const altA = imageAt(6, "jacket");
    const altB = imageAt(7, "pants");
    const altC = imageAt(8, "shoe");

    return [
      {
        id: "simple",
        index: "01",
        title: t("Simple outfit"),
        subtitle: t("Clean matching pieces built around this product."),
        score: t("best match"),
        categories: [
          {
            key: "anchor",
            label: t("Selected product"),
            guidance: t("Keep this as the anchor piece."),
            items: [
              { title: currentProduct, note: t("Current PDP item"), image: productImage },
            ],
          },
          {
            key: "top",
            label: t("Top layer"),
            guidance: t("A simple top keeps the outfit sharp."),
            items: [
              { title: topA.title || t("White shirt"), note: t("Clean base layer"), image: topA.image, href: topA.href },
              { title: topB.title || t("Soft overshirt"), note: t("Relaxed alternate"), image: topB.image, href: topB.href },
            ],
          },
          {
            key: "bottom",
            label: t("Bottom"),
            guidance: t("Balanced shape for the full look."),
            items: [
              { title: bottomA.title || t("Wide-leg trouser"), note: t("Easy proportion"), image: bottomA.image, href: bottomA.href },
              { title: altB.title || t("Straight denim"), note: t("Casual swap"), image: altB.image, href: altB.href },
            ],
          },
          {
            key: "shoes",
            label: t("Shoes"),
            guidance: t("Low contrast keeps it wearable."),
            items: [
              { title: shoeA.title || t("White sneakers"), note: t("Works with everything"), image: shoeA.image, href: shoeA.href },
              { title: altC.title || t("Minimal loafer"), note: t("Sharper finish"), image: altC.image, href: altC.href },
            ],
          },
          {
            key: "accessory",
            label: t("Accessory"),
            guidance: t("One detail completes the set."),
            items: [
              { title: accessoryA.title || t("Minimal watch"), note: t("Quiet detail"), image: accessoryA.image, href: accessoryA.href },
              { title: altA.title || t("Silver chain"), note: t("Streetwear accent"), image: altA.image, href: altA.href },
            ],
          },
        ],
      },
      {
        id: "relaxed",
        index: "02",
        title: t("Relaxed streetwear"),
        subtitle: t("Soft volume, neutral color, stronger silhouette."),
        score: t("size match"),
        categories: [
          {
            key: "base",
            label: t("Base"),
            guidance: t("Neutral tone for a clean base."),
            items: [
              { title: topB.title || t("Oversized T-shirt"), note: t("Relaxed drape"), image: topB.image, href: topB.href },
              { title: topA.title || t("Cotton shirt"), note: t("Layered option"), image: topA.image, href: topA.href },
            ],
          },
          {
            key: "trouser",
            label: t("Trouser"),
            guidance: t("Wide leg keeps the mood current."),
            items: [
              { title: bottomA.title || t("Wide-leg trouser"), note: t("Comfort and shape"), image: bottomA.image, href: bottomA.href },
              { title: altB.title || t("Baggy denim"), note: t("Weekend alternate"), image: altB.image, href: altB.href },
            ],
          },
          {
            key: "shoe",
            label: t("Shoe"),
            guidance: t("Simple footwear grounds the outfit."),
            items: [
              { title: shoeA.title || t("White sneakers"), note: t("Timeless staple"), image: shoeA.image, href: shoeA.href },
              { title: altC.title || t("Chunky sneaker"), note: t("More street"), image: altC.image, href: altC.href },
            ],
          },
          {
            key: "finish",
            label: t("Finish"),
            guidance: t("Small details lift the look."),
            items: [
              { title: accessoryA.title || t("Sunglasses"), note: t("Adds attitude"), image: accessoryA.image, href: accessoryA.href },
              { title: altA.title || t("Silver chain"), note: t("Subtle effect"), image: altA.image, href: altA.href },
            ],
          },
        ],
      },
    ];
  }, [imageAt, productImage, productTitle, t]);

  const [selectedItems, setSelectedItems] = useState<Record<string, number>>({});

  return (
    <section className={`ps-outfit-demo${isMobile ? " ps-mobile" : ""}`} aria-label={t("Matching outfits")}>
      <div className="ps-outfit-demo-head">
        <span className="ps-outfit-demo-kicker">{t("Style match")}</span>
        <h3>{t("Outfits for your fit")}</h3>
        <p>{t("Matched to your size profile and the product you selected.")}</p>
      </div>
      <div className="ps-outfit-demo-list">
        {looks.map((look) => (
          <article className="ps-outfit-look" key={look.id}>
            <div className="ps-outfit-look-head">
              <span className="ps-outfit-look-index">{look.index}</span>
              <div className="ps-outfit-look-copy">
                <h4>{look.title}</h4>
                <p>{look.subtitle}</p>
              </div>
              <span className="ps-outfit-look-score">{look.score}</span>
            </div>
            <div className="ps-outfit-category-list">
              {look.categories.map((category) => {
                const selectedIndex = selectedItems[`${look.id}:${category.key}`] ?? 0;
                return (
                  <div className="ps-outfit-category" key={category.key}>
                    <div className="ps-outfit-category-meta">
                      <span>{category.label}</span>
                      <small>{category.guidance}</small>
                    </div>
                    <div className="ps-outfit-item-strip" role="list" aria-label={category.label}>
                      {category.items.map((item, itemIndex) => {
                        const isSelected = itemIndex === selectedIndex;
                        const content = (
                          <>
                            <span className="ps-outfit-item-media">
                              {item.image && <img src={item.image} alt="" draggable={false} />}
                            </span>
                            <span className="ps-outfit-item-copy">
                              <strong>{item.title}</strong>
                              <small>{item.note}</small>
                            </span>
                          </>
                        );
                        const className = `ps-outfit-item${isSelected ? " is-selected" : ""}`;
                        if (item.href) {
                          return (
                            <a
                              key={`${category.key}-${itemIndex}`}
                              href={item.href}
                              target="_blank"
                              rel="noopener noreferrer"
                              className={className}
                              role="listitem"
                              onClick={() => setSelectedItems((current) => ({ ...current, [`${look.id}:${category.key}`]: itemIndex }))}
                            >
                              {content}
                            </a>
                          );
                        }
                        return (
                          <button
                            key={`${category.key}-${itemIndex}`}
                            type="button"
                            className={className}
                            role="listitem"
                            onClick={() => setSelectedItems((current) => ({ ...current, [`${look.id}:${category.key}`]: itemIndex }))}
                          >
                            {content}
                          </button>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          </article>
        ))}
      </div>
    </section>
  );
}

// Strip chart-axis prefixes ("MISSY 12 / Standard" → "12", "Missy 12" → "12")
// for the size-pill display. The button's underlying value (`s`) keeps the
// full label so allSizes lookups + active-state matching still work.
function formatPillLabel(s: string): string {
  if (!s) return s;
  // Drop any " / suffix" segments (e.g. "/ Standard", "/ Length 61").
  const head = s.split("/")[0]?.trim() || s;
  // Take the last whitespace-separated token of the head — leaves "12", "1X",
  // "XL" untouched while stripping "MISSY", "PLUS", etc.
  const tokens = head.split(/\s+/);
  return tokens[tokens.length - 1] || s;
}

function buildOutfitSizeOptions(suggestedSize: string): string[] {
  const clean = formatPillLabel(String(suggestedSize || "").trim());
  if (!clean) return [];

  const numeric = clean.match(/^(\d+)(?:\.0)?$/);
  if (numeric) {
    const value = Number(numeric[1]);
    return [-1, 0, 1, 2, 3].map((offset) => String(Math.max(1, value + offset)));
  }

  const letterSizes = ["XXS", "XS", "S", "M", "L", "XL", "XXL", "3XL", "4XL"];
  const index = letterSizes.findIndex((size) => size === clean.toUpperCase());
  if (index >= 0) {
    const start = Math.max(0, Math.min(letterSizes.length - 5, index - 2));
    return letterSizes.slice(start, start + 5);
  }

  return [clean];
}

function normalizeShoeGender(gender?: string | null): "male" | "female" | null {
  if (gender === "male" || gender === "female") return gender;
  return null;
}

function formatShoeSizeLabel(sizeLabel: string, gender?: string | null): string {
  const label = String(sizeLabel || "").trim();
  if (!label) return label;
  const normalizedGender = normalizeShoeGender(gender);
  const prefix = normalizedGender === "female" ? "W" : normalizedGender === "male" ? "M" : "";
  const wanted = prefix
    ? label.match(new RegExp(`(?:^|[\\s/])${prefix}\\s*([0-9]+(?:\\.5)?[A-Z]?)`, "i"))
    : null;
  if (wanted?.[1]) return `${prefix} ${wanted[1]}`;
  if (/^[WM]\s*[0-9]+(?:\.5)?[A-Z]?$/i.test(label)) {
    const existingPrefix = label.slice(0, 1).toUpperCase();
    return prefix && existingPrefix !== prefix ? label : `${existingPrefix} ${label.slice(1).trim()}`;
  }
  if (prefix && /^[0-9]+(?:\.5)?[A-Z]?$/i.test(label)) return `${prefix} ${label}`;
  return label;
}

// ── Skeleton overlay — dots + lines during processing ──
const SKELETON_CONNECTIONS: [keyof BodyLandmarks, keyof BodyLandmarks][] = [
  ["nose", "leftShoulder"], ["nose", "rightShoulder"],
  ["leftShoulder", "rightShoulder"],
  ["leftShoulder", "leftElbow"], ["leftElbow", "leftWrist"],
  ["rightShoulder", "rightElbow"], ["rightElbow", "rightWrist"],
  ["leftShoulder", "leftHip"], ["rightShoulder", "rightHip"],
  ["leftHip", "rightHip"],
  ["leftHip", "leftKnee"], ["leftKnee", "leftAnkle"],
  ["rightHip", "rightKnee"], ["rightKnee", "rightAnkle"],
];

import { useIsMobile } from "../utils/useIsMobile";
import { MobileScanningView } from "./MobileScanningView";
import { MultiSectionMobile } from "./MultiSectionMobile";
import { EngagingTryOnView } from "./EngagingTryOnView";
import { TryOnGenerationBadge } from "./TryOnGenerationBadge";
import { ProductPhotoCarouselCard, type ProductPhotoCarouselItem } from "./ProductPhotoCarouselCard";

// ── Face mesh overlay — mirrors SkeletonOverlay but draws the face points ──
function FaceOverlay({
  landmarks,
  imgWidth,
  imgHeight,
}: {
  landmarks: import("../../face-detect").FaceLandmarks;
  imgWidth: number;
  imgHeight: number;
}) {
  const W = imgWidth;
  const H = imgHeight;
  type Pt = { x: number; y: number };
  const pts: Array<{ key: string; p: Pt; r?: number }> = [
    { key: "forehead", p: landmarks.forehead },
    { key: "chin", p: landmarks.chin },
    { key: "noseTip", p: landmarks.noseTip },
    { key: "noseBridge", p: landmarks.noseBridge },
    { key: "leftInnerEye", p: landmarks.leftInnerEye },
    { key: "rightInnerEye", p: landmarks.rightInnerEye },
    { key: "leftOuterEye", p: landmarks.leftOuterEye },
    { key: "rightOuterEye", p: landmarks.rightOuterEye },
    { key: "leftTragus", p: landmarks.leftTragus },
    { key: "rightTragus", p: landmarks.rightTragus },
    { key: "leftMouth", p: landmarks.leftMouth },
    { key: "rightMouth", p: landmarks.rightMouth },
  ];
  const connections: Array<[Pt, Pt]> = [
    // Face axis
    [landmarks.forehead, landmarks.noseBridge],
    [landmarks.noseBridge, landmarks.noseTip],
    [landmarks.noseTip, landmarks.chin],
    // Horizontal eye line
    [landmarks.leftOuterEye, landmarks.leftInnerEye],
    [landmarks.leftInnerEye, landmarks.rightInnerEye],
    [landmarks.rightInnerEye, landmarks.rightOuterEye],
    // Ears
    [landmarks.leftTragus, landmarks.leftOuterEye],
    [landmarks.rightOuterEye, landmarks.rightTragus],
    // Mouth line
    [landmarks.leftMouth, landmarks.rightMouth],
  ];

  return (
    <svg className="ps-tryon-pose-overlay" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      {/* Connection lines */}
      {connections.map(([a, b], i) => (
        <line
          key={`fl-${i}`}
          x1={a.x * W} y1={a.y * H} x2={b.x * W} y2={b.y * H}
          stroke="rgba(100,210,255,0.55)" strokeWidth="3" strokeLinecap="round"
          opacity="0" style={{ animation: `ps-pose-fade 0.4s ease ${i * 0.05}s forwards` }}
        />
      ))}
      {/* Iris rings — highlight the calibration anchor */}
      {[landmarks.leftIrisCenter, landmarks.rightIrisCenter].map((c, i) => {
        // Estimate iris radius in px from the iris-ring extent.
        const ring = i === 0 ? landmarks.leftIrisRing : landmarks.rightIrisRing;
        const rx = ring?.length
          ? (Math.abs((ring[0]?.x ?? c.x) - (ring[2]?.x ?? c.x)) * W) / 2
          : 6;
        return (
          <circle
            key={`iris-${i}`}
            cx={c.x * W} cy={c.y * H} r={Math.max(6, rx)}
            fill="none" stroke="rgba(255,230,120,0.95)" strokeWidth="2.5"
            opacity="0"
            style={{ animation: `ps-pose-fade 0.3s ease ${0.3 + i * 0.1}s forwards` }}
          />
        );
      })}
      {/* Joint dots — radius is a pure ratio of face span (in viewBox
          units). No absolute-px clamps — the SVG auto-scales with the
          container so a fixed viewBox radius always renders at the same
          visual size on screen regardless of the source image resolution.
          Previous clamp `Math.min(10, …)` in viewBox units made the dots
          appear huge on 512px crops and vanishingly tiny on 4000px ones. */}
      {(() => {
        const faceSpan = Math.max(1, Math.abs(landmarks.rightOuterEye.x - landmarks.leftOuterEye.x) * W);
        const halo = faceSpan * 0.04;
        const core = halo * 0.55;
        return pts.map(({ key, p }, i) => (
          <g key={key}>
            <circle cx={p.x * W} cy={p.y * H} r={halo}
              fill="rgba(100,210,255,0.22)" opacity="0"
              style={{ animation: `ps-pose-fade 0.3s ease ${i * 0.04}s forwards` }} />
            <circle cx={p.x * W} cy={p.y * H} r={core}
              fill="rgba(100,210,255,0.95)" opacity="0"
              style={{ animation: `ps-pose-fade 0.3s ease ${i * 0.04}s forwards` }} />
          </g>
        ));
      })()}
    </svg>
  );
}

// ── Stage cycler — sizing-only stage feedback for the snap flow ──
// Try-on processing uses EngagingTryOnView (rendered by the parent),
// so this component only handles the sizing-detection/measurement phase.
function StageCycler({
  category,
  sizingDone,
  t,
}: {
  category: "body" | "face" | "head" | "foot";
  sizingDone: boolean;
  t: TranslateFn;
}) {
  const sizingStages = category === "head"
    ? [
        { title: t("DETECTING HEAD"),        desc: t("Reading head landmarks from your photo.") },
        { title: t("MAPPING CIRCUMFERENCE"), desc: t("Estimating head width and depth.") },
        { title: t("MATCHING HAT SIZE"),     desc: t("Comparing your circumference to the size chart.") },
        { title: t("FINALIZING RESULT"),     desc: t("Almost done — preparing your suggested size.") },
      ]
    : category === "face"
    ? [
        { title: t("DETECTING FACE"),        desc: t("Identifying 478 face landmarks in your photo.") },
        { title: t("CALIBRATING SCALE"),     desc: t("Using iris size as the pixel-to-mm anchor.") },
        { title: t("MEASURING FRAME"),       desc: t("Mapping bridge, lens width and temple length.") },
        { title: t("MATCHING FRAME SIZE"),   desc: t("Comparing your measurements to the size chart.") },
        { title: t("FINALIZING RESULT"),     desc: t("Almost done — preparing your suggested size.") },
      ]
    : [
        // Body + foot fall through here — broadest set of stages so long
        // Gemini estimates still have fresh text to cycle through.
        { title: t("DETECTING POSE"),     desc: t("Identifying body landmarks from your photo.") },
        { title: t("SCANNING FRAME"),     desc: t("Our AI is mapping your proportions.") },
        { title: t("ANALYZING BODY"),     desc: t("Measuring shoulders, chest, waist and hips.") },
        { title: t("MATCHING SIZE"),      desc: t("Comparing your measurements to the size guide.") },
        { title: t("FINALIZING RESULT"),  desc: t("Almost done — preparing your suggested size.") },
      ];

  // Time-based stage cycling synced to the SDK's 6 s minVisible window.
  // Backend now returns sizing in ~200 ms (sizingDone flips true early)
  // but the parent keeps `sizingLoading` true for the full 6 s so this
  // panel stays mounted. Stages are evenly distributed across
  // (TOTAL_MS - LAST_HOLD_MS); the last stage holds for the final 1 s
  // before the result card appears.
  const TOTAL_MS = 6000;
  const LAST_HOLD_MS = 1000;
  const startRef = useRef<number>(Date.now());
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const stepMs = (TOTAL_MS - LAST_HOLD_MS) / Math.max(1, sizingStages.length - 1);
    const id = setInterval(() => {
      const elapsed = Date.now() - startRef.current;
      const next = Math.min(sizingStages.length - 1, Math.floor(elapsed / stepMs));
      setIdx((prev) => (prev === next ? prev : next));
      if (next >= sizingStages.length - 1) clearInterval(id);
    }, 100);
    return () => clearInterval(id);
  }, [sizingStages.length]);

  const current = sizingStages[idx] ?? sizingStages[0]!;
  return (
    <div className="ps-msc-stage" style={{ alignSelf: "center", marginTop: "auto", marginBottom: "auto" }}>
      <div className="ps-msc-stage-slot" key={`s-${idx}`}>
        <div className="ps-msc-stage-title">{current.title}</div>
        <div className="ps-msc-stage-desc">{current.desc}</div>
      </div>
    </div>
  );
}

function SkeletonOverlay({ landmarks, imgWidth, imgHeight }: { landmarks: BodyLandmarks; imgWidth: number; imgHeight: number }) {
  const W = imgWidth;
  const H = imgHeight;
  // Derive all sizes from the BODY span (shoulder-to-shoulder in viewBox
  // units). Previous code used hard-coded r=14 / r=8 / strokeWidth=4 which
  // renders as a different DOM-pixel size for every image resolution (tiny
  // on 4000px photos, huge on 500px crops). Scaling from a body-anchored
  // span keeps the overlay visually consistent across any source photo.
  // Fallback: 10% of image width when shoulders aren't available.
  const ls = landmarks.leftShoulder, rs = landmarks.rightShoulder;
  const shoulderSpan = ls && rs && typeof ls.x === "number" && typeof rs.x === "number"
    ? Math.max(1, Math.abs(ls.x - rs.x) * W)
    : W * 0.1;
  const haloR = shoulderSpan * 0.07;
  const coreR = shoulderSpan * 0.035;
  const strokeW = shoulderSpan * 0.025;
  return (
    <svg className="ps-tryon-pose-overlay" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      {/* Connection lines — guard against missing or malformed landmarks
          so a dropped point doesn't render as NaN coordinates. */}
      {SKELETON_CONNECTIONS.map(([a, b], i) => {
        const pa = landmarks[a];
        const pb = landmarks[b];
        if (!pa || !pb || typeof pa !== "object" || typeof pb !== "object" ||
            typeof pa.x !== "number" || typeof pa.y !== "number" ||
            typeof pb.x !== "number" || typeof pb.y !== "number") return null;
        return (
          <line key={`l-${i}`}
            x1={pa.x * W} y1={pa.y * H} x2={pb.x * W} y2={pb.y * H}
            stroke="rgba(100,210,255,0.5)" strokeWidth={strokeW} strokeLinecap="round"
            opacity="0" style={{ animation: `ps-pose-fade 0.4s ease ${i * 0.05}s forwards` }}
          />
        );
      })}
      {/* Joint dots — skip the `imageWidth`/`imageHeight` metadata fields
          the SDK now ships alongside x/y landmarks; they're plain numbers,
          not {x, y}, so rendering them as circles placed NaN dots at (0,0). */}
      {Object.entries(landmarks)
        .filter(([, v]) => v && typeof v === "object" && typeof (v as any).x === "number" && typeof (v as any).y === "number")
        .map(([key, v], i) => (
          <g key={key}>
            <circle cx={(v as any).x * W} cy={(v as any).y * H} r={haloR}
              fill="rgba(100,210,255,0.25)" opacity="0"
              style={{ animation: `ps-pose-fade 0.3s ease ${i * 0.04}s forwards` }}
            />
            <circle cx={(v as any).x * W} cy={(v as any).y * H} r={coreR}
              fill="rgba(100,210,255,0.9)" opacity="0"
              style={{ animation: `ps-pose-fade 0.3s ease ${i * 0.04}s forwards` }}
            />
          </g>
        ))}
    </svg>
  );
}

// ── Regen scanning overlay — shown on the left-side try-on image during
// "Try It On" regeneration (post-try-on size change). Reuses the existing
// .ps-tryon-scan-overlay / .ps-tryon-scan-line CSS classes from styles.ts
// so the visual matches the initial-try-on scan. ──
function RegenScanOverlay({ active }: { active: boolean }) {
  if (!active) return null;
  return (
    <>
      <div className="ps-tryon-scan-overlay" />
      <div className="ps-tryon-scan-line" />
    </>
  );
}

const FALLBACK_MEASUREMENT_LINES: MeasurementLines = {
  chest: { y: 0.34, x1: 0.28, x2: 0.72 },
  waist: { y: 0.5, x1: 0.33, x2: 0.67 },
  hips: { y: 0.62, x1: 0.29, x2: 0.71 },
};

// ── Measurement overlay — simple curved lines with big labels ──
function MeasurementOverlay({ lines, fitRows, show, imgWidth, imgHeight }: {
  lines: MeasurementLines;
  fitRows: Array<{ area: string; userNum: number; chartLabel: string; fit: string; isLength?: boolean }>;
  show: boolean;
  imgWidth: number;
  imgHeight: number;
}) {
  if (!show) return null;

  const fitColor = (fit: string) => {
    if (fit === "good") return "#4ade80";
    if (fit.includes("tight")) return "#ef4444";
    return "#f59e0b";
  };

  const areas: Array<{ key: string; line: { y: number; x1: number; x2: number }; label: string }> = [
    { key: "chest", line: lines.chest, label: "Chest" },
    { key: "waist", line: lines.waist, label: "Waist" },
    { key: "hips", line: lines.hips, label: "Hips" },
  ];

  const W = imgWidth;
  const H = imgHeight;
  // Anchor dot / stroke / font sizes to the widest measurement line on
  // screen (usually hips) so dots stay visually consistent across source
  // image resolutions. The old `Math.max(7, …)` clamp mixed viewBox units
  // with min-pixels and let dots blow up on low-res crops. Using the body
  // span as the reference keeps everything proportional.
  const spans = areas
    .map(a => (a.line && typeof a.line.x1 === "number" && typeof a.line.x2 === "number") ? Math.abs(a.line.x2 - a.line.x1) : 0)
    .filter(s => s > 0);
  const maxSpanNorm = spans.length ? Math.max(...spans) : 0.2;
  const bodySpanPx = Math.max(1, maxSpanNorm * W);
  const strokeW = bodySpanPx * 0.018;
  const dotR = bodySpanPx * 0.025;
  const fontSize = bodySpanPx * 0.1;
  const subFontSize = bodySpanPx * 0.08;
  const scale = bodySpanPx / 400;

  return (
    <svg className="ps-tryon-pose-overlay" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="xMidYMid meet">
      {areas.map(({ key, line, label }, i) => {
        // Pose detection occasionally returns undefined lines when run on a
        // product image (no person) or a low-quality user photo. Without this
        // guard the multiplication below yields NaN for cx/cy and React logs
        // `Received NaN for the cx attribute`; browsers also render one
        // stray dot at (0, 0), which looked like a landmark glued to the
        // top-left of the uploaded image.
        if (!line || typeof line.x1 !== "number" || typeof line.x2 !== "number" || typeof line.y !== "number") return null;

        // Sideways stance guard: when the user is turned (profile / 3-quarter),
        // MediaPipe still reports both shoulder/hip keypoints but they collapse
        // toward the same x — the "line" becomes a meaningless stub or
        // horizontal slash that doesn't trace the body contour. Skip any line
        // whose span is less than 8% of the largest span in the set (hips
        // usually) so only meaningful frontal measurements render.
        const lineSpan = Math.abs(line.x2 - line.x1);
        if (maxSpanNorm > 0 && lineSpan < maxSpanNorm * 0.35) return null;
        if (lineSpan * W < 20) return null;
        const x1 = line.x1 * W;
        const x2 = line.x2 * W;
        const cy = line.y * H;
        const width = x2 - x1;
        // Ellipse half-height: ~12% of width gives a natural body-wrap
        // perspective (chest/waist/hips read like a 3D band, not a flat
        // line). curveDepth still drives a few legacy fallbacks below.
        const ellipseRy = width * 0.12;
        const curveDepth = ellipseRy;
        const midX = (x1 + x2) / 2;
        const fitRow = fitRows.find(r => {
          const a = r.area.toLowerCase().trim();
          if (a === key) return true;
          if (key === "chest" && (a.includes("bust") || a.includes("chest"))) return true;
          if (key === "waist" && a.includes("waist")) return true;
          if (key === "hips" && (a.includes("hip") || a === "hips")) return true;
          return false;
        });
        // Skip lines for measurements the chart doesn't have. When a
        // shirt size guide doesn't include hips, we shouldn't paint a
        // hips wrap-around on the body — the on-image lines should stay
        // in lockstep with the size table on the right.
        if (!fitRow) return null;
        // White lines for visibility against the dark try-on overlay.
        // The fit-color signal moves to the LABEL (Tight/Long/Fit chip)
        // instead of the line itself — the wrap-around curve reads clearly
        // either way and white plays well with a Zara-style dotted look.
        const color = "rgba(255,255,255,0.95)";
        const delay = i * 0.35;

        // Front + back arcs of a 3D ellipse wrap. Front arc curves DOWN
        // in front of the body; back arc curves UP behind the body. Both
        // are dashed; the back arc renders at lower opacity so it reads
        // as "behind". Together they form a Zara-style dotted band.
        const frontArc = `M ${x1} ${cy} Q ${midX} ${cy + ellipseRy} ${x2} ${cy}`;
        const backArc = `M ${x1} ${cy} Q ${midX} ${cy - ellipseRy} ${x2} ${cy}`;
        const lineLen = width * 1.05;

        // Label can overflow the viewBox on the right when the body sits
        // close to the image edge — flip it to the LEFT side of the line
        // when there isn't enough room on the right.
        const labelText = fitRow
          ? (fitRow.fit === "good"
              ? "✓ Fit"
              : fitRow.isLength
                ? (fitRow.fit.includes("short") || fitRow.fit.includes("tight") ? "Short" : "Long")
                : (fitRow.fit.includes("tight") ? "Tight" : "Loose"))
          : label;
        // Big, legible label. Fit / Tight / Long chip needs to read
        // clearly on small phone screens — bumped from a 14–22 px range
        // up to a 26–48 px range. The text gets a soft black stroke
        // outline (paintOrder: stroke) below so it stays readable on
        // any background.
        const labelFont = Math.max(26, Math.round(48 * scale));
        // Box dimensions — pad text inside a pill, leave some room for the
        // arrow that connects the wrap-around line to the box.
        const labelWidthEst = labelText.length * labelFont * 0.62;
        const boxPadX = Math.max(10, Math.round(14 * scale));
        const boxPadY = Math.max(6, Math.round(10 * scale));
        const boxW = labelWidthEst + boxPadX * 2;
        const boxH = labelFont + boxPadY * 2;
        // Distance from the body's line endpoint to the start of the
        // arrow (longer than before so the box doesn't hug the body).
        const armLen = Math.max(28, Math.round(50 * scale));
        const rightSpace = W - x2 - boxW - armLen - 12 * scale;
        const flipLeft = rightSpace < 0;
        const tickX1 = flipLeft ? x1 : x2;
        const tickX2 = flipLeft ? x1 - armLen : x2 + armLen;
        const boxX = flipLeft ? tickX2 - boxW : tickX2;
        const boxY = cy - boxH / 2;
        const boxFill = fitRow ? fitColor(fitRow.fit) : "rgba(255,255,255,0.95)";
        const arrowHeadW = Math.max(6, Math.round(10 * scale));

        // Dashed dot pattern (Zara-style 3D wrap). Dashes scale with
        // body size so a 600 px-wide model and a 1200 px-wide model both
        // get visually similar dot density.
        const dashLen = Math.max(6, Math.round(8 * scale));
        const gapLen = Math.max(5, Math.round(7 * scale));
        return (
          <g key={key}>
            {/* Back arc (curves UP behind the body) — wrapped in a g
                with opacity 0.4 so the arc reads as the rear half of
                the 3D wrap (visually behind the body). */}
            <g opacity="0.45">
              <path
                d={backArc}
                fill="none" stroke={color} strokeWidth={strokeW} strokeLinecap="round"
                strokeDasharray={`${dashLen} ${gapLen}`}
                opacity="0"
                style={{ animation: `ps-pose-fade 0.45s ease ${delay}s forwards` }}
              />
            </g>
            {/* Front arc (curves DOWN in front of the body) — full
                opacity so it reads as the front half of the wrap. */}
            <path
              d={frontArc}
              fill="none" stroke={color} strokeWidth={strokeW} strokeLinecap="round"
              strokeDasharray={`${dashLen} ${gapLen}`}
              opacity="0"
              style={{ animation: `ps-pose-fade 0.45s ease ${delay + 0.05}s forwards` }}
            />
            {/* Endpoint dots — sit at the equator where front meets back */}
            <circle cx={x1} cy={cy} r={dotR} fill={color} opacity="0"
              style={{ animation: `ps-pose-fade 0.3s ease ${delay + 0.4}s forwards` }} />
            <circle cx={x2} cy={cy} r={dotR} fill={color} opacity="0"
              style={{ animation: `ps-pose-fade 0.3s ease ${delay + 0.4}s forwards` }} />
            {/* Label group — connector line, arrowhead, and a coloured
                pill containing the fit chip. Flips left when there isn't
                room on the right. */}
            <g opacity="0" style={{ animation: `ps-pose-fade 0.4s ease ${delay + 0.5}s forwards` }}>
              {/* Connector line from the wrap endpoint to the pill */}
              <line
                x1={tickX1} y1={cy} x2={tickX2} y2={cy}
                stroke={color} strokeWidth={Math.max(2, 2.5 * scale)} strokeLinecap="round"
              />
              {/* Arrowhead pointing at the body */}
              <polygon
                points={
                  flipLeft
                    ? `${tickX2 - arrowHeadW},${cy - arrowHeadW * 0.6} ${tickX2},${cy} ${tickX2 - arrowHeadW},${cy + arrowHeadW * 0.6}`
                    : `${tickX2 + arrowHeadW},${cy - arrowHeadW * 0.6} ${tickX2},${cy} ${tickX2 + arrowHeadW},${cy + arrowHeadW * 0.6}`
                }
                fill={color}
              />
              {/* Pill background — coloured per fit */}
              <rect
                x={boxX} y={boxY}
                width={boxW} height={boxH}
                rx={boxH / 2} ry={boxH / 2}
                fill={boxFill}
                stroke="rgba(0,0,0,0.18)" strokeWidth={1}
              />
              {/* Label text inside the pill */}
              <text
                x={boxX + boxW / 2} y={cy + 1 * scale}
                fill="#FFFFFF"
                fontSize={labelFont} fontWeight="800"
                fontFamily="system-ui, -apple-system, sans-serif"
                dominantBaseline="middle"
                textAnchor="middle"
                stroke="rgba(0,0,0,0.35)" strokeWidth={Math.max(2, scale * 2)} paintOrder="stroke"
              >
                {labelText}
              </text>
            </g>
          </g>
        );
      })}
    </svg>
  );
}

// ── Helpers shared by single-piece and multi-section views ──
const cleanNumFn = (s: string | null | undefined) => {
  if (s == null) return "";
  const ns = s.replace(/[^\d.\-\u2013]/g, " ").trim().split(/[\s\-\u2013]+/).filter(Boolean).map(n => parseFloat(n)).filter(n => !isNaN(n));
  if (ns.length === 1) return `${ns[0]}`;
  if (ns.length > 1) return ns[0] === ns[1] ? `${ns[0]}` : `${ns[0]}\u2013${ns[1]}`;
  return s;
};
const pNumFn = (s: string | null | undefined) => { if (s == null) return 0; const n = parseFloat(s.replace(/[^\d.]/g, "")); return isNaN(n) ? 0 : n; };
const pRangeFn = (s: string | null | undefined) => {
  if (s == null) return { min: 0, max: 0 };
  const ns = s.replace(/[^\d.\-\u2013]/g, " ").trim().split(/[\s\-\u2013]+/).filter(Boolean).map(Number).filter(n => !isNaN(n));
  return ns.length ? { min: Math.min(...ns), max: Math.max(...ns) } : { min: 0, max: 0 };
};
const cellValFn = (row: unknown, colIdx: number, header?: string): string => {
  if (!row) return "";
  if (Array.isArray(row)) return (row[colIdx] ?? "").toString().trim();
  const obj = row as Record<string, string>;
  if (header && obj[header] !== undefined) return obj[header].toString().trim();
  if (header) {
    const lh = header.toLowerCase();
    for (const k of Object.keys(obj)) {
      if (k.toLowerCase() === lh) return obj[k].toString().trim();
    }
  }
  return "";
};

const fitLabelFn = (fit: string, t: TranslateFn) =>
  fit === "good" ? t("within range") : fit === "too-tight" ? t("too tight") : fit === "tight" ? t("tight") : fit === "a-bit-tight" ? t("a bit tight") : fit === "too-loose" ? t("too loose") : fit === "loose" ? t("loose") : t("a bit loose");

const accessoryFitLabelFn = (fit: string, t: TranslateFn) =>
  fit === "good" ? t("within range") : fitLabelFn(fit, t);

const lengthFitLabelFn = (fit: string, t: TranslateFn) => {
  // Accept both the legacy circumference-style keys (tight/loose) and the
  // directional keys (short/long) emitted by the local fit recomputation.
  // Gate on direct-match first so "short" / "too-short" / "a-bit-short"
  // don't fall through to the default "a bit long" branch.
  if (fit === "good") return t("within range");
  if (fit === "too-short" || fit === "too-tight") return t("too short");
  if (fit === "short"    || fit === "tight")      return t("short");
  if (fit === "a-bit-short" || fit === "a-bit-tight") return t("a bit short");
  if (fit === "too-long" || fit === "too-loose")  return t("too long");
  if (fit === "long"     || fit === "loose")      return t("long");
  return t("a bit long");
};

const lengthBadgeTone = (fit: string): "good" | "short" | "long" => {
  if (fit === "good") return "good";
  if (fit.includes("short") || fit.includes("tight")) return "short";
  return "long";
};

// ── Helpers: unit display conversion ──
type DisplayUnit = "cm" | "in" | "mm";

// Convert a numeric measurement from one unit system to another for display.
// "mm" is a pass-through on both sides — eyewear + headwear charts are native
// mm and the SDK doesn't offer a unit toggle for them.
function convertNum(n: number, from: DisplayUnit, to: DisplayUnit): number {
  if (from === to || !n || isNaN(n)) return n;
  if (from === "mm" || to === "mm") return n;
  return from === "cm" ? Math.round((n / 2.54) * 10) / 10 : Math.round((n * 2.54) * 10) / 10;
}
// Convert a chart-range string (e.g. "88.5-94.0" or "36") from one unit to another.
function convertLabel(label: string, from: DisplayUnit, to: DisplayUnit): string {
  if (from === to) return label;
  if (from === "mm" || to === "mm") return label;
  const conv = (n: number) => convertNum(n, from, to);
  const rangeMatch = label.match(/^(\d+\.?\d*)\s*[-–]\s*(\d+\.?\d*)$/);
  if (rangeMatch) return `${conv(parseFloat(rangeMatch[1]))}-${conv(parseFloat(rangeMatch[2]))}`;
  if (/^\d+\.?\d*$/.test(label.trim())) return String(conv(parseFloat(label)));
  return label;
}

function isFootLengthMeasurement(name: string): boolean {
  return /foot\s*length|footlength|cm\s*\/\s*jp|jp\s*\/\s*cm|mondo|mondopoint/i.test(name || "");
}

// ── Helpers: section classification ──
const isLengthSection = (name: string) => /length|lunghezza|longueur|länge|largo/i.test(name);
const SIZE_SYSTEM_SECTION_RE = /^(standard|default|regular|short|long|tall|petite|international|intl|italy|italian|it|eu|europe|france|french|fr|uk|us|usa|japan|jp|china|cn|korea|kr|numeric|letter|alpha|size|sizes)$/i;
const GARMENT_SECTION_RE = /\b(jacket|blazer|coat|pants?|trousers?|shorts|vest|waistcoat|gilet|shirt|tee|t-shirt|top|bottom|skirt|dress|suit|bodice|sleeve)\b/i;

function cleanSectionName(name: string): string {
  return String(name || "").replace(/\s*[—–-]\s*.*/g, "").replace(/\s+/g, " ").trim();
}

function isGarmentCardSectionName(name: string): boolean {
  const clean = cleanSectionName(name);
  if (!clean || isLengthSection(clean) || SIZE_SYSTEM_SECTION_RE.test(clean.toLowerCase())) return false;
  return GARMENT_SECTION_RE.test(clean);
}

function hasMultiGarmentSections(sectionNames: string[]): boolean {
  const cardNames = sectionNames.filter((name) => !isLengthSection(name));
  if (cardNames.length <= 1) return false;
  return cardNames.filter(isGarmentCardSectionName).length >= 2;
}

/** Try to match a length section to its parent garment (e.g. "Jacket Length" → "Jacket Size" or "Jacket") */
function findLengthForSection(sectionName: string, lengthEntries: Array<{ name: string; section: NormalizedSection; secResult: { recommendedSize: string; matchDetails: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }> } }>) {
  // Extract base garment word from the card name (e.g. "Jacket Size" → "jacket", "Suit Pants" → "suit pants")
  const base = sectionName.toLowerCase().replace(/\s*(size|sizing|—.*|–.*|\(.*?\))\s*/gi, "").trim();
  return lengthEntries.find(e => {
    const lb = e.name.toLowerCase().replace(/\s*(length|lunghezza|longueur|länge|largo|—.*|–.*|\(.*?\))\s*/gi, "").trim();
    return lb === base || base.includes(lb) || lb.includes(base);
  }) || null;
}

// ── Detail view for a single section (shown after clicking a big card) ──
function SectionDetailView({
  sectionName, section, sectionResult, userMeasurements, unitLbl, chartUnit, lengthEntry, onBack, t,
  productImage, productTitle, isMobile: isMobileProp,
  isTryOnImage, showLines, onToggleLines, overlayNode, renderOverlayWithFit, onActiveFitChange, onImageLoad,
  onOpenImage, onDownload,
  onFeedbackSubmit,
  onTryOn, tryOnProcessing, tryOnStartedAt, backLabel, internationalSizes, continueLabel, onContinue, resultActionNode,
  renderRaw = false, sectionFound, allSizes,
  isFootwear = false,
  userGender,
  profileCompletionCta,
  outfitRecommendationNode,
  outfitPiece,
  onPendingCustomSizeChange,
  onDisplayUnitChange,
}: {
  sectionName: string;
  section: NormalizedSection;
  sectionResult: { recommendedSize: string; matchDetails: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }>; guideOnly?: boolean; oneSize?: boolean; sizeLabel?: string; availableSizes?: string[] } | undefined;
  userMeasurements: Record<string, number>;
  unitLbl: string;
  /** The unit that matchDetails values are actually in (from the backend).
   *  When it differs from the display unit (unitLbl), values are converted. */
  chartUnit?: DisplayUnit;
  lengthEntry: { name: string; section: NormalizedSection; secResult: { recommendedSize: string; matchDetails: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }> } } | null;
  onBack: () => void;
  t: TranslateFn;
  productImage?: string;
  productTitle?: string;
  isMobile?: boolean;
  /** True when productImage is the try-on result (enables overlay + toggle). */
  isTryOnImage?: boolean;
  showLines?: boolean;
  onToggleLines?: () => void;
  overlayNode?: React.ReactNode;
  /** Render-prop variant of overlayNode. When provided, gets called with
   *  the per-row fit data SectionDetailView computed for the user's
   *  currently-selected size pill. The MeasurementOverlay otherwise reads
   *  stale suggested-size fits, so the "Show Fit" annotations on the
   *  photo never update when the user picks a different pill. */
  renderOverlayWithFit?: (fitRows: Array<{ area: string; userNum: number; chartLabel: string; fit: string }>) => React.ReactNode;
  /** Surfaces SectionDetailView's recomputed per-size fits to the parent so
   *  an EXTERNAL MeasurementOverlay (desktop v2 layout, outside this view)
   *  can update its annotations on size-pill click. Mirror of
   *  renderOverlayWithFit for layouts where the overlay can't be a child. */
  onActiveFitChange?: (fitRows: Array<{ area: string; userNum: number; chartLabel: string; fit: string }>) => void;
  onImageLoad?: (e: React.SyntheticEvent<HTMLImageElement>) => void;
  onOpenImage?: () => void;
  onDownload?: () => void;
  onFeedbackSubmit?: (input: { rating?: number; note?: string }) => Promise<void>;
  /** Called when the user wants to launch the try-on flow from the detail
   *  view footer. When provided, the footer's primary CTA becomes "Try It
   *  On" instead of the meaningless "Continue" stub. */
  onTryOn?: () => void;
  tryOnProcessing?: boolean;
  /** Wall-clock ms when the in-flight Try It On started. null when idle.
   *  Drives the "Generating try-on… 12s" elapsed-time display. */
  tryOnStartedAt?: number | null;
  /** Override the back button label (default: "Back"). */
  backLabel?: string;
  /** Override the "Continue →" fallback CTA label (shown when onTryOn is undefined). */
  continueLabel?: string;
  onContinue?: () => void;
  resultActionNode?: ReactNode;
  /** International size labels for the suggested size (e.g.
   *  `{ Italy: "50", France: "46", Europe: "50", UK: "40", US: "40", ... }`).
   *  When provided + non-empty, the detail view renders a country dropdown
   *  that relabels the big suggested-size display to that country's
   *  notation. Body measurements and fit calculations are unaffected. */
  internationalSizes?: Record<string, string>;
  /** Explicit "no valid fit" signal from the backend. When false, the big
   *  size label is swapped with a friendly "no size available" message and
   *  the country selector is suppressed (there's nothing to translate). */
  sectionFound?: boolean;
  /** Accessory mode: backend already returns matchDetail strings in the user's
   *  unit (`"54.5 cm"`, `"54 cm"`, `"fit: tight"`). Render them verbatim —
   *  do NOT re-look up chart values, re-parse, or re-convert. Body garments
   *  leave this off so the existing SDK math still runs. */
  renderRaw?: boolean;
  /** Shoe mode: show only foot length. Shoe sizes are used to infer the
   *  length, but they should not appear as W/M or US/UK/EU result rows. */
  isFootwear?: boolean;
  /** Shopper/model gender used for footwear display labels. */
  userGender?: string;
  profileCompletionCta?: ProfileCompletionCtaConfig | null;
  outfitRecommendationNode?: ReactNode;
  /** Selected Mix & Match product. Enables the dedicated three-column
   *  desktop detail treatment while keeping the normal size renderer intact. */
  outfitPiece?: StyleMatchSelection | null;
  /** Changes the shopper-facing body measurement unit on the result table. */
  onDisplayUnitChange?: (unit: "cm" | "in") => void;
  onPendingCustomSizeChange?: (sectionName: string, override: null | {
    sectionName: string;
    selectedSize: string;
    selectedLength?: string;
    displayLabel: string;
    matchDetails?: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }>;
  }) => void;
  /** Per-size lookup from the backend's matcher: every row × every measurement,
   *  pre-classified. When the user clicks a non-suggested pill we read
   *  `allSizes[displaySize][measurement]` to swap the cell + fit pill — pure
   *  lookup, no math. Optional for back-compat with older backends. */
  allSizes?: Record<string, Record<string, { chartRange: string; fit: string }>>;
}) {
  const guideOnly = !!(sectionResult?.guideOnly || sectionResult?.oneSize);
  const outfitIsFootwear = String(outfitPiece?.slot || "").toLowerCase() === "shoe";
  const effectiveIsFootwear = isFootwear || outfitIsFootwear;
  let recSize = outfitPiece?.recommendedSize?.trim() || sectionResult?.recommendedSize || "";
	  const [selectedSize, setSelectedSize] = useState<string | null>(null);
		  const [shoeDisplayUnit, setShoeDisplayUnit] = useState<"cm" | "in">("cm");
		  const tryOnImageRef = useRef<HTMLImageElement | null>(null);
		  const [loadedTryOnImageUrl, setLoadedTryOnImageUrl] = useState<string | null>(null);
		  // Live counter for the Try It On button label (e.g. "Generating try-on… 12s").
		  const tryOnElapsedS = useElapsedSeconds(tryOnStartedAt ?? null);
		  const isCompactMobileContinue = !!isMobileProp && !onTryOn && !!continueLabel;
		  useEffect(() => {
		    if (!isTryOnImage || !productImage) setLoadedTryOnImageUrl(null);
		  }, [isTryOnImage, productImage, tryOnProcessing]);
		  useLayoutEffect(() => {
		    if (!isTryOnImage || !productImage || tryOnProcessing) return;
		    const img = tryOnImageRef.current;
		    if (img?.complete && img.naturalWidth > 0) setLoadedTryOnImageUrl(productImage);
		  }, [isTryOnImage, productImage, tryOnProcessing]);
		  const tryOnImageLoading = !!isTryOnImage && !!productImage && !tryOnProcessing && loadedTryOnImageUrl !== productImage;
		  const handleSectionImageLoad = useCallback((event: React.SyntheticEvent<HTMLImageElement>) => {
		    if (isTryOnImage && !tryOnProcessing && productImage) setLoadedTryOnImageUrl(productImage);
		    onImageLoad?.(event);
		  }, [isTryOnImage, onImageLoad, productImage, tryOnProcessing]);

  // Display-unit conversion: backend may return matchDetails values in a
  // different unit (chartUnit) than the user's preferred display unit (unitLbl).
  // For mm charts (eyewear/headwear), mm is the display unit too — no conversion.
  const effectiveUnitLbl = effectiveIsFootwear ? shoeDisplayUnit : unitLbl;
  const unitLblLower = effectiveUnitLbl.toLowerCase();
  const displayUnitId: DisplayUnit = unitLblLower.includes("mm")
    ? "mm"
    : unitLblLower.includes("cm")
      ? "cm"
      : "in";
  const fromUnit: DisplayUnit = chartUnit || displayUnitId;
  const dNum = (n: number) => convertNum(n, fromUnit, displayUnitId);
  const dLabel = (s: string) => convertLabel(s, fromUnit, displayUnitId);
  // Same as dLabel but appends the display unit when the result is a numeric
  // value or numeric range (e.g. "89" → "89 cm", "88-94" → "88-94 cm"). Leaves
  // textual codes ("Short", "Regular") untouched. Used by the Garment Spec
  // column so it mirrors the Your Body column's "<num> <unit>" format.
  const dLabelWithUnit = (s: string): string => {
    const lbl = dLabel(s);
    return /^\d+(?:\.\d+)?(?:\s*[-–]\s*\d+(?:\.\d+)?)?$/.test(lbl.trim())
      ? `${lbl} ${effectiveUnitLbl}`
      : lbl;
  };
  const bodyResultUnitSwitch = onDisplayUnitChange && !renderRaw && !isFootwear && displayUnitId !== "mm" ? (
    <div style={{
      display: "flex",
      alignItems: "center",
      justifyContent: "flex-end",
      gap: isMobileProp ? 8 : "0.4vw",
      margin: isMobileProp ? "0 0 10px" : "0.15vw 0 0.45vw",
    }}>
      <span style={{
        fontSize: isMobileProp ? 11 : "0.6vw",
        fontWeight: 700,
        color: "var(--ps-text-secondary)",
        textTransform: "uppercase",
        letterSpacing: "0.08em",
      }}>
        {t("Units")}
      </span>
      <div className="ps-shoe-unit-toggle" aria-label={t("Measurement units")}>
        {(["cm", "in"] as const).map((unit) => (
          <button
            key={unit}
            type="button"
            className={displayUnitId === unit ? "ps-active" : ""}
            onClick={() => onDisplayUnitChange(unit)}
            aria-pressed={displayUnitId === unit}
          >
            {unit}
          </button>
        ))}
      </div>
    </div>
  ) : null;

  // Country selector for international size relabeling. The list of options
  // is whatever the backend included in `internationalSizes`. The default is
  // null = show the original chart label (e.g. "L"). Picking a country
  // displays the chart row's value for that country (e.g. "50" for Europe).
  // The fit calculations and chart pills are unaffected — the user is just
  // choosing how to LABEL the suggested size.
  const countryOptions = internationalSizes ? Object.keys(internationalSizes) : [];
  const [selectedCountry, setSelectedCountry] = useState<string | null>(null);

  const recLength = lengthEntry?.secResult?.recommendedSize || "";
  const [selectedLength, setSelectedLength] = useState<string | null>(null);
  const lengthSizes = useMemo(() => {
    if (!lengthEntry) return [];
    const sec = lengthEntry.section;
    const colIdx = sec.headers.findIndex(h => /size|taglia|größe|taille/i.test(h.trim()));
    const idx = colIdx >= 0 ? colIdx : 0;
    const hdr = sec.headers[idx] || "Size";
    const raw = sec.rows.map(r => cellValFn(r, idx, hdr)).filter(Boolean);
    return [...new Set(raw)];
  }, [lengthEntry]);

  // Build a { lengthLabel → helperText } map — when the length section is a
  // jacket-length chart it usually has a Height column, so "Short" should
  // render as "Short (5'6"–5'8")". For any chart with a secondary column
  // (height, inseam, arm) that's what we surface in the pill caption.
  // Disabled per UX request — length pills show just the label
  // ("Short", "Regular", "Long") without numeric height ranges beneath.
  // The fit status next to the chart row already tells the user whether
  // their height is in range, so the extra numbers were noise.
  const lengthHelperMap = useMemo<Record<string, string>>(() => ({}), []);

  const sizeColIdx = useMemo(() => {
    const byName = section.headers.findIndex(h => /size|taglia|größe|taille/i.test(h.trim()));
    if (byName >= 0) return byName;
    for (let c = 0; c < section.headers.length; c++) {
      const hdr = section.headers[c];
      if (section.rows.some(r => /^(XXS|XS|S|M|L|XL|XXL|XXXL|ONE SIZE|\d{1,2})$/i.test(cellValFn(r, c, hdr)))) return c;
    }
    return 0;
  }, [section]);

  const sizeHeader = section.headers[sizeColIdx] || "Size";
  const chartSizes = useMemo(() => {
    const raw = section.rows.map(r => cellValFn(r, sizeColIdx, sizeHeader)).filter(Boolean);
    return [...new Set(raw)];
  }, [section, sizeColIdx, sizeHeader]);
  const guideOnlyLabel = sectionResult?.sizeLabel || chartSizes[0] || t("One Size");
  if (guideOnly && !recSize) recSize = guideOnlyLabel;

  // displaySize must combine the user's size pick + the active length
  // variant so the table-lookup `allSizes[displaySize][measurement]` swaps
  // its values when the user toggles Standard vs Extra Length on the
  // wedding gown (and similar charts that encode length as a row-label
  // suffix). Without this, picking Extra Length didn't change any cell.
  const displaySize = useMemo(() => {
    const stripLen = (s: string): string => {
      if (!s) return s;
      const head = (s.split("/")[0] ?? s).trim();
      const tokens = head.split(/\s+/);
      return tokens[tokens.length - 1] || s;
    };
    const sizeBase = selectedSize || recSize;
    if (!sizeBase || !selectedLength || !allSizes) return sizeBase;
    const baseBare = stripLen(sizeBase);
    const lengthLower = selectedLength.toLowerCase().trim();
    const match = Object.keys(allSizes).find(
      k => stripLen(k) === baseBare && k.toLowerCase().includes(lengthLower),
    );
    return match || sizeBase;
  }, [selectedSize, recSize, selectedLength, allSizes]);
  const isRecommended = displaySize === recSize;

  // When user manually picks a non-suggested size, assess its fit quality
  // so we can show a more accurate label than the binary "Not Suggested".
  // "Your Selection" = user manually picked, fits well (all perfect/good)
  // "Not Suggested" = user manually picked, has at least one poor fit result
  const altSizeLabel = useMemo(() => {
    if (isRecommended) return null;
    const details = sectionResult?.matchDetails ?? [];
    const BAD_FIT = /tight|loose|large|small|very/i;
    const hasBadFit = details.some(d => BAD_FIT.test(d.fit || ""));
    return hasBadFit ? t("Not Suggested") : t("Your Selection");
  }, [isRecommended, sectionResult, t]);

  // Pull a Fit / Silhouette / Category prefix out of the chart row so the
  // big "Suggested Size" label can read "Missy 12" instead of "12" when
  // the chart encodes a fit axis as a separate column. Mirror logic lives
  // in buildSilhouetteContext so the prompt agrees with the UI.
  const sizeFitPrefix: string = (() => {
    if (renderRaw || isFootwear) return "";
    const target = displaySize?.toString().trim();
    if (!target || !section.headers?.length || !section.rows?.length) return "";
    const row = section.rows.find(r => cellValFn(r, sizeColIdx, sizeHeader).toString().trim() === target);
    if (!row) return "";
    for (let i = 1; i < section.headers.length; i++) {
      const h = section.headers[i] || "";
      const v = cellValFn(row, i, h);
      if (/^(fit|silhouette|category|body[\s_]?type)$/i.test(h) && v != null && String(v).trim()) {
        return String(v).trim();
      }
    }
    return "";
  })();

  // displayLabel is what the user SEES on the big size text. Country-localized
  // when a country is picked AND we're still on the suggested size; falls
  // back to the raw chart label otherwise. We deliberately disable the
  // country relabel when the user clicks a non-suggested pill (selectedSize
  // !== recSize) — the international map only knows about the suggested
  // row, so showing it for a different size would be misleading.
  const rawDisplaySizeLabel: string =
    selectedCountry && isRecommended && internationalSizes && internationalSizes[selectedCountry]
      ? internationalSizes[selectedCountry]
      : sizeFitPrefix
        ? `${sizeFitPrefix} ${displaySize}`
        : displaySize;
  const displaySizeLabel = effectiveIsFootwear
    ? formatShoeSizeLabel(rawDisplaySizeLabel, userGender)
    : rawDisplaySizeLabel;
  const formatVisibleSizeLabel = (size: string): string => (
    effectiveIsFootwear ? formatShoeSizeLabel(size, userGender) : formatPillLabel(size)
  );

  // Build a per-column unit map ONCE per section. Each column's unit comes
  // from (1) the header marker `(cm)`/`(in)` if present, or (2) the suffix on
  // the first non-empty cell value (`87CM`, `34.3 in`). This is the only
  // reliable way to disambiguate asymmetric vendor charts where the cm column
  // is bare (`Bust`) and only the inch column is marked (`Bust (in)`).
  const columnUnits = useMemo<Array<"cm" | "in" | null>>(() => {
    const units: Array<"cm" | "in" | null> = [];
    for (let i = 0; i < section.headers.length; i++) {
      const h = (section.headers[i] || "").toLowerCase();
      if (/\(cm\)|centimeter/i.test(h)) { units.push("cm"); continue; }
      if (/\(in\)|\(inch|inch/i.test(h)) { units.push("in"); continue; }
      // Header is bare — infer from first non-empty cell suffix
      let inferred: "cm" | "in" | null = null;
      for (const row of section.rows) {
        const cell = String(cellValFn(row, i, section.headers[i]) || "").trim();
        if (!cell) continue;
        if (/cm\b|centimeter/i.test(cell)) { inferred = "cm"; break; }
        if (/in\b|inch|"$/i.test(cell)) { inferred = "in"; break; }
        // Cell is bare numeric — no unit info, give up on this row and try next
      }
      units.push(inferred);
    }
    return units;
  }, [section]);

  const chartRangeFor = useCallback((measurement: string, size: string) => {
    const b = measurement.toLowerCase().trim();
    const preferCm = effectiveUnitLbl === "cm" || effectiveUnitLbl === "";
    const wantUnit: "cm" | "in" = preferCm ? "cm" : "in";

    // Collect every column whose header refers to this measurement.
    // No exact-match shortcut — that's exactly the bug we're fixing.
    const candidates: number[] = [];
    for (let i = 0; i < section.headers.length; i++) {
      const a = (section.headers[i] || "").toLowerCase().trim();
      if (a === b) { candidates.push(i); continue; }
      const aBare = a.replace(/\s*\(.*?\)\s*/g, "").trim();
      if (aBare === b || a.includes(b) || b.includes(aBare)) {
        candidates.push(i);
      }
    }
    if (candidates.length === 0) return null;

    // Pick the candidate whose unit matches the user's preferred unit. If
    // multiple match, take the first; if none match, take any candidate.
    let mc = candidates.find((i) => columnUnits[i] === wantUnit);
    if (mc == null) mc = candidates[0];

    const mHeader = section.headers[mc];
    // Lenient size-row lookup: exact match first, then case-insensitive / trim,
    // then starts-with (covers tuxedo rows like "40R" when the user picked "40"
    // and backend-suggested is the same short form).
    const sizeNorm = size.trim().toLowerCase();
    let row = section.rows.find(r => cellValFn(r, sizeColIdx, sizeHeader) === size);
    if (!row) row = section.rows.find(r => cellValFn(r, sizeColIdx, sizeHeader).trim().toLowerCase() === sizeNorm);
    if (!row) row = section.rows.find(r => {
      const cell = cellValFn(r, sizeColIdx, sizeHeader).trim().toLowerCase();
      return cell.startsWith(sizeNorm) || sizeNorm.startsWith(cell);
    });
    if (!row) return null;
    const val = cellValFn(row, mc, mHeader);
    if (!val) return null;
    const parsed = pRangeFn(val);

    // If we ended up on a column whose unit doesn't match the user's
    // preference (chart only had one unit), convert to display in the user's
    // unit so the comparison is apples-to-apples.
    const colUnit = columnUnits[mc];
    if (colUnit === "in" && preferCm) {
      const toCm = (v: number) => +(v * 2.54).toFixed(1);
      return { range: String(toCm(parsed.min)) + (parsed.min !== parsed.max ? "–" + String(toCm(parsed.max)) : ""), min: toCm(parsed.min), max: toCm(parsed.max) };
    }
    if (colUnit === "cm" && !preferCm) {
      const toIn = (v: number) => +(v / 2.54).toFixed(1);
      return { range: String(toIn(parsed.min)) + (parsed.min !== parsed.max ? "–" + String(toIn(parsed.max)) : ""), min: toIn(parsed.min), max: toIn(parsed.max) };
    }
    return { range: val, ...parsed };
  }, [section, sizeColIdx, sizeHeader, effectiveUnitLbl, columnUnits]);

  const fitRows = useMemo(() => {
    // Accessory path — backend already formatted userValue/chartRange as
    // unit-suffixed strings in the user's preferred unit. Don't re-parse,
    // don't re-look up the chart, don't re-convert. Just pass the strings
    // through verbatim so the renderer displays exactly what the backend
    // said. Strip the unit parentheses from the measurement label for
    // display polish (e.g. "Hats (cm)" → "Hats").
    if (renderRaw) {
      const raw = sectionResult?.matchDetails || [];
      return raw.map(m => {
        let rawChartRange = m.chartRange;
        let fit = m.fit;
        // When the user picks a non-suggested size, re-look up the chart
        // row for that size so the table reflects what they actually picked
        // (e.g. sunglass XXL → XL changes Lens Width / Bridge / Arm Length).
        // Previously the renderRaw path short-circuited → the table showed
        // the backend's verbatim ranges regardless of user selection.
        if (displaySize !== recSize) {
          const alt = chartRangeFor(m.measurement, displaySize);
          if (alt && (alt.min > 0 || alt.max > 0)) {
            const unitSuffix = (m.chartRange.match(/[a-zA-Z]+\s*$/) || [""])[0];
            rawChartRange = alt.range + (unitSuffix ? ` ${unitSuffix.trim()}` : "");
            // Recompute fit label against the new range. Parse userValue
            // once; if it's unparseable leave the fit untouched.
            const uNum = pNumFn(m.userValue);
            if (uNum > 0) {
              const rMin = alt.min, rMax = alt.max;
              // Same buckets as fit-compute.ts: ½" = "a bit", ½"–2" = full
              // tight/loose, > 2" = too tight/loose. Tolerance for the
              // within-range window is float-noise small.
              const isCm = /\bcm\b/i.test(rawChartRange);
              const aBitTol = isCm ? 1.27 : 0.5;
              const tooFarTol = isCm ? 5.08 : 2.0;
              const tol = Math.max((rMax || rMin) * 0.005, isCm ? 0.25 : 0.1);
              if (uNum >= rMin - tol && uNum <= rMax + tol) fit = "good";
              else if (uNum < rMin) {
                const d = rMin - uNum;
                fit = d > tooFarTol ? "too-loose" : d > aBitTol ? "loose" : "a-bit-loose";
              } else {
                const d = uNum - rMax;
                fit = d > tooFarTol ? "too-tight" : d > aBitTol ? "tight" : "a-bit-tight";
              }
            }
          }
        }
        return {
          area: m.measurement.replace(/\s*\(.*?\)\s*$/, "").trim() || m.measurement,
          rawUserValue: m.userValue,
          rawChartRange,
          fit,
          userNum: 0,
          chartLabel: "",
          isLength: false,
        };
      });
    }
    const mainDetails = sectionResult?.matchDetails || [];
    const lengthDetails = lengthEntry?.secResult?.matchDetails || [];
    // Dedupe by measurement name. The backend now embeds Height into the
    // merged Jacket/Pants section's matchDetails AND preserves the length
    // section separately — without this dedupe the table would render two
    // identical "Height" rows (first from mainDetails, second from
    // lengthDetails). Keep the FIRST occurrence so mainDetails wins; it
    // carries the emit-in-user-unit formatting consistently.
    const seenMeasurements = new Set<string>();
    const details = [...mainDetails, ...lengthDetails].filter(d => {
      const key = String(d?.measurement ?? "").toLowerCase().trim();
      if (!key || seenMeasurements.has(key)) return false;
      seenMeasurements.add(key);
      return true;
    });

    // If a length recommendation exists (either via lengthEntry or inline on the section),
    // create a synthetic length row when no matchDetails cover it.
    const inlineLengthRec: string = (sectionResult as any)?.length || "";
    const inlineHasLengths: boolean = ((sectionResult as any)?.availableLengths?.length || 0) > 0;
    const effectiveRecLength = recLength || inlineLengthRec;
    const hasLengthSource = !!(lengthEntry || inlineHasLengths);

    // Tuxedo jacket / any multi-section with a separate length chart:
    // the backend doesn't include a Height matchDetail in the length
    // section. Without this synthetic row the table would silently drop
    // the length row AND the length pill wouldn't drive any table cell.
    // Inject a Height row so (a) the blueprint has a length line with
    // a status badge, and (b) swapping the pill re-runs fit through the
    // length handler below.
    // Synthetic Height row — only when nothing else provided one. The
    // dedupe pass above means `details` can never contain a Height twice,
    // so a simple "already present?" check is sufficient. We also require
    // the length chart to actually HAVE a Height column; otherwise the
    // synthetic row has no spec to render against (pants charts that only
    // list Inseam per length would produce a blank/bogus Height row).
    const hasHeightRow = details.some(d => d.measurement.toLowerCase() === "height");
    const lengthChartHasHeight = !!lengthEntry?.section?.headers?.some(h => /height|altezza|estatura|\(cm\)/i.test(h));
    if (lengthEntry && lengthChartHasHeight && !hasHeightRow) {
      // Jacket-style: paired length chart with an actual Height column →
      // synthesize a Height row that resolves against the active length
      // pill via the length branch below.
      const userHcm = userMeasurements["height"] || 0;
      details.push({
        measurement: "Height",
        userValue: userHcm ? String(userHcm) : "",
        chartRange: effectiveRecLength || "",
        fit: "good",
      });
    } else if (inlineHasLengths && !lengthEntry && !hasHeightRow) {
      // Vest-style: no paired length chart, just category pills
      // (Regular / Big / Big & Tall). Show the user's height as a
      // reference row — no fit comparison since the vest chart has
      // nothing to compare against.
      const userHcm = userMeasurements["height"] || 0;
      details.push({
        measurement: "Height",
        userValue: userHcm ? String(userHcm) : "",
        chartRange: "",
        fit: "good",
      });
    }

    if (!details.length) return [];

    // Rows resolved against the LENGTH chart (not the main size chart) —
    // any matchDetail the backend shipped with the length section (Inseam
    // for pants, sleeve for some shirts, …) PLUS Height whenever a length
    // entry exists, so the synthetic Height row also re-resolves off the
    // active length pill instead of falling through to the main-chart
    // branch where it picks up the wrong column.
    const isFromLength = new Set<string>(lengthDetails.map(m => m.measurement));
    if (hasLengthSource) isFromLength.add("Height");

    // The length pill's currently-selected value drives length-row lookup.
    // Falls back to the backend's suggested length when the user hasn't
    // overridden it yet.
    const activeLengthLabel = selectedLength || effectiveRecLength;

    const rows = details.map(m => {
      // Length rows (Height, Inseam, etc.) — re-look up against the
      // user's chosen LENGTH category. Three lookup sources, in order:
      //   1. lengthEntry.secResult.allSizes[activeLengthLabel] — paired
      //      length sections (Jacket Length: Short/Regular/Long) ship
      //      their own allSizes keyed by length label.
      //   2. allSizes[activeLengthLabel] — when the section's own chart
      //      is keyed by length category (Vest: Regular/Big/Big & Tall).
      //   3. allSizes[displaySize] — fall back to size-pill key.
      //   4. matchDetails — fall back to the suggested row.
      if (isFromLength.has(m.measurement)) {
        const userNum = userMeasurements[m.measurement.toLowerCase()] || pNumFn(m.userValue);
        const lengthAllSizes = (lengthEntry as any)?.secResult?.allSizes as
          | Record<string, Record<string, { chartRange: string; fit: string }>>
          | undefined;
        const lookupByLengthEntry = activeLengthLabel ? lengthAllSizes?.[activeLengthLabel]?.[m.measurement] : undefined;
        const lookupByLength = activeLengthLabel ? allSizes?.[activeLengthLabel]?.[m.measurement] : undefined;
        const lookupBySize = allSizes?.[displaySize]?.[m.measurement];
        const lookup = lookupByLengthEntry ?? lookupByLength ?? lookupBySize;
        const chartRange = lookup?.chartRange ?? m.chartRange;
        const fit = lookup?.fit ?? m.fit ?? "good";
        return {
          area: m.measurement,
          userNum,
          chartLabel: cleanNumFn(chartRange),
          fit,
          isLength: true,
        };
      }

      // Normal numeric rows (Chest, Waist, etc.).
      //
      // When the user clicks a non-suggested size pill we DON'T recompute
      // the fit — the backend already classified every row × every
      // measurement and shipped the result in `allSizes`. We just look up
      // `allSizes[displaySize][measurement]` and swap in that row's
      // chartRange + fit. Pure lookup, zero math.
      const userNum = userMeasurements[m.measurement.toLowerCase()] || pNumFn(m.userValue);
      const measLower = m.measurement.toLowerCase();
      const isDirectional = /length|inseam|sleeve|hem|rise/.test(measLower);
      const lookup = allSizes?.[displaySize]?.[m.measurement];
      const chartRange = lookup?.chartRange ?? m.chartRange;
      const fit = lookup?.fit ?? m.fit ?? "good";
      return {
        area: m.measurement,
        userNum,
        chartLabel: cleanNumFn(chartRange),
        fit,
        isLength: isDirectional,
      };
    });
    return effectiveIsFootwear ? rows.filter((row) => isFootLengthMeasurement(row.area)) : rows;
  }, [sectionResult, lengthEntry, userMeasurements, renderRaw, allSizes, displaySize, selectedLength, effectiveIsFootwear]);

  // Surface recomputed fits to any parent that owns an EXTERNAL overlay
  // (desktop v2 layout). Without this, the photo's "Show Fit" annotations
  // stay frozen on the suggested size's fits even after the user clicks
  // a different size pill. We flatten to the minimal shape MeasurementOverlay
  // expects.
  const activeFitKeyRef = useRef("");
  useEffect(() => {
    if (!onActiveFitChange) return;
    const nextRows = fitRows.map(r => ({
      area: r.area,
      userNum: r.userNum ?? 0,
      chartLabel: r.chartLabel ?? "",
      fit: r.fit ?? "good",
    }));
    const nextKey = JSON.stringify(nextRows);
    if (activeFitKeyRef.current === nextKey) return;
    activeFitKeyRef.current = nextKey;
    onActiveFitChange(nextRows);
  }, [fitRows, onActiveFitChange]);

  const displayLength = selectedLength || recLength;
  const allowsLengthDisplay = !renderRaw && !isFootwear;
  // Get length and size from the section result (from backend merged data).
  // backendSize/backendLength are the *suggested* values — they never
  // change when the user picks a different pill. displaySize and
  // finalDisplayLength reflect the user's current selection.
  const secAny = sectionResult as any;
  const backendSize = secAny?.size || recSize;
  // Pull length from: (1) explicit section.length (merged multi-section), (2)
  // a separate length section via lengthEntry, (3) as a fallback, the inseam
  // row in this section's own matchDetails. The fallback makes single-section
  // pants render as "48 / 29" even when the backend hasn't explicitly split
  // length into its own field.
  const lengthFromDetails = allowsLengthDisplay
    ? (sectionResult?.matchDetails || []).find((m) =>
      /inseam|length/i.test(m.measurement) && !/neck|arm|sleeve|back|shoulder|temple|lens|bridge|frame|face|head/i.test(m.measurement)
    )
    : undefined;
  const inseamFallback = lengthFromDetails
    ? (lengthFromDetails.chartRange || "").replace(/\s*(cm|in|inches)\s*/i, "").trim()
    : "";
  // For charts that encode length as a row-label suffix (recSize like
  // "MISSY 12 / Standard"), parse the tail when the section result didn't
  // explicitly publish a length field. Lets the Length picker land its
  // initial active state on the correct variant.
  const recLengthFromSuffix = (() => {
    const r = String(recSize || "");
    const slash = r.indexOf("/");
    return slash > 0 ? r.slice(slash + 1).trim() : "";
  })();
  const backendLength = allowsLengthDisplay ? (secAny?.length || recLength || recLengthFromSuffix || inseamFallback) : "";
  const backendAvailableSizes: string[] = secAny?.availableSizes || [];
  const backendAvailableLengths: string[] = secAny?.availableLengths || [];
  const guideOnlySelectableSizes = guideOnly
    ? (backendAvailableSizes.length > 0 ? backendAvailableSizes : chartSizes)
    : [];
  const guideOnlyNote = t("Add them to cart.");
  const finalDisplayLength = allowsLengthDisplay ? (selectedLength || backendLength) : "";
  // Pill labels MUST match `allSizes` keys — that's what the table lookup
  // uses (`allSizes[displaySize][measurement]`). When the backend ships
  // allSizes (which it does for every chart that came through the
  // deterministic engine), use its keys as the source of truth.
  // backendAvailableSizes / chartSizes are fallbacks for legacy paths.
  const allSizesKeys: string[] = allSizes ? Object.keys(allSizes) : [];

  // Reduce the size axis to its bare token ("MISSY 12 / Standard" → "12",
  // "Plus 16W / Extra Length" → "16W", "M" → "M"). The Length variant is
  // its OWN picker (visibleLengths below), so the size pills must dedupe
  // by bare size to avoid rendering "12 / 12 / 10" when both Standard and
  // Extra Length rows exist for size 12.
  const stripLengthVariant = (s: string): string => {
    if (!s) return s;
    const head = (s.split("/")[0] ?? s).trim();
    const tokens = head.split(/\s+/);
    return tokens[tokens.length - 1] || s;
  };

  // Letter-size hierarchy. Numeric sizes (0, 2, 4… 16W, 18W) are sorted
  // numerically by parseFloat with the W rows trailing their non-W
  // counterparts via localeCompare tiebreak.
  const SIZE_LETTER_ORDER: Record<string, number> = {
    XXS: 0, XS: 1, S: 2, M: 3, L: 4, XL: 5,
    XXL: 6, "2XL": 6, XXXL: 7, "3XL": 7, "4XL": 8, "5XL": 9, "6XL": 10,
  };
  const compareSizes = (a: string, b: string): number => {
    const numA = parseFloat(a);
    const numB = parseFloat(b);
    if (!isNaN(numA) && !isNaN(numB)) {
      if (numA !== numB) return numA - numB;
      return a.localeCompare(b);
    }
    const oa = SIZE_LETTER_ORDER[a.toUpperCase()];
    const ob = SIZE_LETTER_ORDER[b.toUpperCase()];
    if (oa != null && ob != null) return oa - ob;
    if (oa != null) return -1;
    if (ob != null) return 1;
    return a.localeCompare(b);
  };

  // Dedupe by bare size token. When more than one length variant exists
  // for the same size, prefer the rep whose key matches the currently
  // selected length so the table lookup stays in sync with the Length
  // picker.
  const dedupedSizes = (() => {
    const lengthLower = String(finalDisplayLength ?? "").toLowerCase().trim();
    const out = new Map<string, string>();
    for (const k of allSizesKeys) {
      const bare = stripLengthVariant(k);
      if (!out.has(bare)) {
        out.set(bare, k);
      } else if (lengthLower && k.toLowerCase().includes(lengthLower)) {
        out.set(bare, k);
      }
    }
    return Array.from(out.entries())
      .sort(([a], [b]) => compareSizes(a, b))
      .map(([, full]) => full);
  })();

  const finalAllSizes = dedupedSizes.length > 0
    ? dedupedSizes
    : backendAvailableSizes.length > 0
      ? backendAvailableSizes
      : chartSizes;

  // Show only 3 sizes around the recommendation: prev / rec / next.
  // Match by bare token because the rep we keep for "12" may not be the
  // exact same full key as recSize ("MISSY 12 / Standard" vs the rep
  // "MISSY 12 / Extra Length" when the user picks the longer length).
  const visibleSizes = (() => {
    if (guideOnly) return [];
    if (finalAllSizes.length <= 3) return finalAllSizes;
    const recBare = stripLengthVariant(recSize || "");
    const idx = finalAllSizes.findIndex(k => stripLengthVariant(k) === recBare);
    if (idx < 0) return finalAllSizes.slice(0, 3);
    const start = Math.max(0, Math.min(finalAllSizes.length - 3, idx - 1));
    return finalAllSizes.slice(start, start + 3);
  })();
  // Derive length variants from row keys when the chart encodes them as a
  // suffix ("MISSY 12 / Standard", "MISSY 12 / Extra Length"). The
  // wedding-gown style charts ship them this way — there's no separate
  // length section, so lengthSizes is empty and backendAvailableLengths is
  // empty too. Without this derivation no Length picker would render.
  const derivedLengths: string[] = (() => {
    const out = new Set<string>();
    for (const k of allSizesKeys) {
      const slash = k.indexOf("/");
      if (slash > 0) {
        const tail = k.slice(slash + 1).trim();
        if (tail) out.add(tail);
      }
    }
    return Array.from(out);
  })();
  const lengthOptions = backendAvailableLengths.length > 0
    ? backendAvailableLengths
    : derivedLengths.length > 0
      ? derivedLengths
      : lengthSizes;
  const visibleLengths = (() => {
    if (guideOnly) return [];
    if (isFootwear) return [];
    if (renderRaw) return [];
    if (lengthOptions.length <= 3) return lengthOptions;
    const idx = lengthOptions.indexOf(backendLength);
    if (idx < 0) return lengthOptions.slice(0, 3);
    const start = Math.max(0, Math.min(lengthOptions.length - 3, idx - 1));
    return lengthOptions.slice(start, start + 3);
  })();
  const guideOnlyTable = guideOnly && section.headers.length > 0 && section.rows.length > 0 ? (
    <div style={{ background: "white", borderRadius: isMobileProp ? 12 : "0.5vw", border: "1px solid rgba(0,0,0,0.06)", overflowX: "auto", overflowY: "hidden", WebkitOverflowScrolling: "touch", marginBottom: isMobileProp ? 14 : "1.2vw" }}>
      <table style={{ width: "max-content", minWidth: "100%", borderCollapse: "collapse" }}>
        <thead>
          <tr>
            {section.headers.map((header, headerIndex) => (
              <th key={`${header}-${headerIndex}`} style={{ textAlign: "left", padding: isMobileProp ? "10px 12px" : "0.5vw 0.6vw", fontSize: isMobileProp ? 11 : "0.6vw", fontWeight: 700, color: "var(--ps-text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid rgba(0,0,0,0.06)", whiteSpace: "nowrap" }}>
                {header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {section.rows.map((row, rowIndex) => (
            <tr key={rowIndex} style={{ borderBottom: rowIndex < section.rows.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none" }}>
              {section.headers.map((header, colIndex) => (
                <td key={`${rowIndex}-${header}-${colIndex}`} style={{ padding: isMobileProp ? "12px" : "0.55vw 0.6vw", fontSize: isMobileProp ? 13 : "0.75vw", color: colIndex === 0 ? "var(--ps-text-primary)" : "var(--ps-text-secondary)", fontWeight: colIndex === 0 ? 700 : 500, whiteSpace: "nowrap" }}>
                  {cellValFn(row, colIndex, header) || "—"}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  ) : null;

  const handleGuideOnlySizeSelect = (size: string) => {
    setSelectedSize(size);
    onPendingCustomSizeChange?.(sectionName, {
      sectionName,
      selectedSize: size,
      displayLabel: size,
      matchDetails: [],
    });
  };

  // (Auto-commit of pill picks removed alongside the regen flow — pills are
  //  now display-only inside the detail view.)

  // ── Mobile branch — completely different layout matching the reference ──
  if (isMobileProp) {
    const cleanSectionName = sectionName.replace(/\s*[—–-]\s*.*/g, "");

    // Description map for each measurement — gives context like the reference
    const measurementDesc = (area: string): string => {
      const a = area.toLowerCase().replace(/\s*\(.*?\)\s*/g, "").trim();
      if (a.includes("chest") || a.includes("bust"))   return t("Full circumference at the widest point.");
      if (a.includes("waist"))                          return t("Circumference at the natural waistline.");
      if (a.includes("hip"))                            return t("Circumference at the fullest point.");
      if (a.includes("shoulder"))                       return t("Shoulder edge to shoulder edge.");
      if (a.includes("sleeve") || a.includes("arm"))    return t("Shoulder seam to cuff edge.");
      if (a.includes("neck") || a.includes("collar"))   return t("Around the base of the neck.");
      if (a.includes("inseam") || a.includes("inside leg")) return t("Inner leg seam length.");
      if (a.includes("thigh"))                          return t("Circumference around the upper leg.");
      if (a.includes("length") || a.includes("height")) return t("Top to bottom length.");
      return "";
    };

    // Stylized fit labels matching the reference's tailoring vocabulary
	    const fitBadgeLabel = (fit: string, isLen: boolean) => {
	      if (isFootwear && fit === "good") return t("BEST MATCH");
	      if (isLen && fit === "good") return t("EDITORIAL LENGTH");
	      if (renderRaw && fit === "good") return t("WITHIN RANGE");
	      if (fit === "good")           return t("WITHIN RANGE");
	      if (fit === "a-bit-tight")    return t("SLIGHTLY TIGHT");
	      if (fit === "a-bit-loose")    return t("SLIGHT EASE");
	      if (fit.includes("tight"))    return t("TIGHT");
	      if (fit.includes("loose"))    return t("LOOSE");
	      return t("FIT");
	    };

	    const mobileDetailActions = (
	      <div className="ps-msd-actions">
	        <button
	          type="button"
	          className="ps-bp-back-btn ps-msd-action-back"
	          onClick={onBack}
	        >
	          <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {backLabel || t("Back")}
	        </button>
	        {onTryOn ? (
	          <button
	            type="button"
	            onClick={onTryOn}
	            disabled={tryOnProcessing}
	            className="ps-msd-primary-cta"
	          >
	            <CameraIcon size={14} />
	            {tryOnProcessing ? `${t("Generating try-on…")}${tryOnElapsedS > 0 ? ` ${tryOnElapsedS}s` : ""}` : t("Try It On")}
	          </button>
	        ) : resultActionNode ? (
	          <div className="ps-tryon-v2-action-group ps-msd-result-action-group">
	            {resultActionNode}
	          </div>
	        ) : (
	          <button
	            type="button"
	            onClick={onBack}
	            className={`ps-msd-primary-cta${isCompactMobileContinue ? " ps-compact" : ""}`}
	          >
	            {continueLabel || t("Continue")} &rarr;
	          </button>
	        )}
	      </div>
	    );

		    return (
		      <div className="ps-msd-root">
	        <div className="ps-msd-scroll">
          {/* Header: back arrow + section name caps */}
          <div className="ps-msd-topbar">
            <button type="button" className="ps-msd-back" onClick={onBack} aria-label={t("Back")}>
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="20" height="20">
                <line x1="19" y1="12" x2="5" y2="12" />
                <polyline points="12 19 5 12 12 5" />
              </svg>
            </button>
            <span className="ps-msd-topbar-title">{cleanSectionName.toUpperCase()} {t("DETAIL")}</span>
            <span className="ps-msd-topbar-spacer" />
          </div>

          {/* Big product / try-on image */}
          {productImage && (
            <>
	              {isTryOnImage && !tryOnProcessing && (
		                <ResultImageActionBar
                  showLines={!!showLines}
                  canShowFit={!isFootwear && !!onToggleLines}
                  onToggleFit={onToggleLines}
                  onOpenImage={onOpenImage}
                  onDownload={onDownload}
                  t={t}
                />
              )}
		              <div className={`ps-msd-image${isTryOnImage ? " ps-tryon" : ""}`}>
		                <img
		                  ref={tryOnImageRef}
		                  key={productImage}
		                  src={productImage}
	                  alt={productTitle || ""}
	                  className={`ps-msd-image-img${tryOnImageLoading ? " ps-result-image-pending" : ""}`}
	                  onLoad={handleSectionImageLoad}
	                  onClick={isTryOnImage && !tryOnImageLoading && onOpenImage ? onOpenImage : undefined}
	                  style={isTryOnImage && !tryOnImageLoading && onOpenImage ? { cursor: "zoom-in" } : undefined}
	                />
	                {tryOnImageLoading && <ResultImageLoadingOverlay t={t} />}
	                {showLines && !tryOnImageLoading && (renderOverlayWithFit
	                  ? renderOverlayWithFit(fitRows.map(r => ({
                      area: r.area,
                      userNum: r.userNum ?? 0,
                      chartLabel: r.chartLabel ?? "",
                      fit: r.fit ?? "good",
                    })))
                  : overlayNode)}
                {tryOnProcessing && tryOnStartedAt != null && (
                  <TryOnGenerationBadge tryOnStartedAt={tryOnStartedAt} t={t} />
                )}
		                {isTryOnImage && !tryOnProcessing && (
		                  <div className="ps-msr-feedback-slot">
                    <TryOnExperienceFeedback t={t} isMobile onSubmit={onFeedbackSubmit} />
                  </div>
                )}
              </div>
            </>
          )}

          {/* No-fit card — replaces the recommendation card when the backend
              reports no row fits the user (body outside every chart row). Hides
              the big fake size label, pills, and measurement table so the user
              isn't misled into trusting an extrapolated recommendation. Try-On
              stays available. Centered in the available vertical space so the
              message reads as the focal point of the panel, not a stray card. */}
          {/* NO-MATCHING-SIZE NOTICE TEMPORARILY DISABLED — was gated on
              `sectionFound === false`. Forced false so the user always sees
              the regular fit card even when backend reports the section
              doesn't match. Restore the original gate to bring it back. */}
          {false && (
            <div style={{
              flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
              minHeight: 240, padding: "24px 16px",
            }}>
              <div className="ps-msd-card" style={{
                textAlign: "center", width: "100%", maxWidth: 420,
                padding: "28px 24px",
              }}>
                <span className="ps-msd-card-eyebrow">{t("NO MATCHING SIZE")}</span>
                <p style={{ fontSize: "15px", fontWeight: 600, color: "var(--ps-text-primary)", margin: "10px 0 6px", lineHeight: 1.4 }}>
                  {t("We couldn't find a size that fits for this product.")}
                </p>
                <p className="ps-msd-card-note" style={{ margin: 0, lineHeight: 1.5 }}>
                  {t("Add them to cart.")}
                </p>
              </div>
            </div>
          )}

          {/* One-size products have a guide, not a recommendation. */}
          {guideOnly ? (
            <div className="ps-msd-card">
              <span className="ps-msd-card-eyebrow">{t("SIZE GUIDE")}</span>
              <div style={{ display: "inline-flex", alignItems: "baseline", gap: "6px", background: "white", border: "1px solid rgba(0,0,0,0.08)", borderRadius: "8px", padding: "4px 12px", marginTop: "6px" }}>
                <span className="ps-msd-card-size">{guideOnlyLabel}</span>
              </div>
              <div className="ps-msd-card-divider" />
              <p className="ps-msd-card-note">
                {guideOnlyNote}
              </p>
            </div>
          ) : sectionFound !== false && (
          <div className="ps-msd-card">
            <span
              className="ps-msd-card-eyebrow"
              style={displaySize !== backendSize ? { color: "#b45309", fontWeight: 700 } : undefined}
            >
              {displaySize === backendSize ? t("SUGGESTED SIZE") : t("YOUR CHOICE · NOT SUGGESTED")}
            </span>
            {/* Stable row: country switcher + size value box side by side */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px", marginTop: "6px", marginBottom: "4px" }}>
              {countryOptions.length > 1 && (
                isMobileProp ? (
                  <span style={{ visibility: isRecommended ? "visible" : "hidden" }}>
                    <MobileChoiceSelect
                      value={selectedCountry || ""}
                      onChange={(next) => setSelectedCountry(next || null)}
                      ariaLabel={t("Display size in country")}
                      options={[
                        { value: "", label: t("Standard") },
                        ...countryOptions.map((c) => ({ value: c, label: c })),
                      ]}
                    />
                  </span>
                ) : (
                  <select
                    value={selectedCountry || ""}
                    onChange={(e) => setSelectedCountry(e.target.value || null)}
                    style={{
                      fontSize: "11px",
                      fontWeight: 600,
                      fontFamily: "inherit",
                      color: "var(--ps-text-secondary)",
                      background: "white",
                      border: "1px solid rgba(0,0,0,0.12)",
                      borderRadius: "6px",
                      padding: "4px 8px",
                      cursor: "pointer",
                      outline: "none",
                      visibility: isRecommended ? "visible" : "hidden",
                    }}
                    aria-label={t("Display size in country")}
                  >
                    <option value="">{t("Standard")}</option>
                    {countryOptions.map((c) => (
                      <option key={c} value={c}>{c}</option>
                    ))}
                  </select>
                )
          )}
          {isFootwear && (
            <div className="ps-shoe-unit-toggle" aria-label={t("Foot length unit")}>
              {(["cm", "in"] as const).map((unit) => (
                <button
                  key={unit}
                  type="button"
                  className={shoeDisplayUnit === unit ? "ps-active" : ""}
                  onClick={() => setShoeDisplayUnit(unit)}
                >
                  {unit}
                </button>
              ))}
            </div>
          )}
          <div style={{
                display: "inline-flex",
                alignItems: "baseline",
                gap: "6px",
                background: "white",
                border: "1px solid rgba(0,0,0,0.08)",
                borderRadius: "8px",
                padding: "4px 12px",
              }}>
                <span className="ps-msd-card-size">
                  {displaySizeLabel}
                  {/* Pants / trousers: append the length (inseam) so the
                      card reads e.g. "52 / 30" instead of just "52". Only
                      when not showing a country-localized label AND when
                      the size label doesn't already encode a customization
                      ("MISSY 12 / Standard" — the "/ Standard" already
                      conveys the length variant; appending the raw 61"/65"
                      would just duplicate). */}
                  {!isFootwear && !selectedCountry && finalDisplayLength && !displaySizeLabel.includes(" / ")
                    ? ` / ${finalDisplayLength}`
                    : ""}
                </span>
                {!isFootwear && finalDisplayLength && (
                  <span className="ps-msd-card-size-meta">{finalDisplayLength}</span>
                )}
                {selectedCountry && isRecommended && (
                  <span className="ps-msd-card-size-meta" style={{ fontSize: "11px", opacity: 0.7 }}>({selectedCountry})</span>
                )}
              </div>
            </div>
          </div>
          )}

	          {guideOnlyTable}

          {guideOnly && guideOnlySelectableSizes.length > 1 && (
            <div className="ps-msd-sizes">
              <span className="ps-msd-sizes-label">{t("SELECT A SIZE TO ADD TO CART")}</span>
              <div className="ps-msd-sizes-pills">
                {guideOnlySelectableSizes.map((size) => {
                  const isActive = selectedSize === size;
                  return (
                    <button
                      key={size}
                      type="button"
                      className={`ps-msd-size-pill${isActive ? " ps-active" : ""}`}
                      onClick={() => handleGuideOnlySizeSelect(size)}
                    >
                      {size}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* MEASUREMENT BLUEPRINT */}
          {!guideOnly && sectionFound !== false && fitRows.length > 0 && (
            <>
              {bodyResultUnitSwitch}
              <div className="ps-msd-blueprint">
                <span className="ps-msd-blueprint-title">{t("MEASUREMENT BLUEPRINT")}</span>
                <div className="ps-msd-blueprint-underline" />
                <div className="ps-msd-rows">
                  {fitRows.map((row, i) => {
                  const isNegative = row.isLength
                    ? (row.fit.includes("short") || row.fit.includes("tight"))
                    : row.fit.includes("tight");
                  const fitClass = row.fit === "good" ? "ps-good"
                    : isNegative ? "ps-tight"
                    : "ps-loose";
                  const desc = measurementDesc(row.area);
                  const idxLbl = String(i + 1).padStart(2, "0");
                  return (
                    <div className="ps-msd-row" key={i}>
                      <div className="ps-msd-row-num">{idxLbl} {row.area.toUpperCase()}</div>
                      {desc && <p className="ps-msd-row-desc">{desc}</p>}
                      <div className="ps-msd-row-cells">
                        <div className="ps-msd-row-cell">
                          <span className="ps-msd-cell-label">{t("USER")}</span>
                          <span className="ps-msd-cell-value">{renderRaw ? ((row as any).rawUserValue || "") : ((isNaN(row.userNum) || row.userNum === 0) ? "" : `${dNum(row.userNum)} ${effectiveUnitLbl}`)}</span>
                        </div>
                        <div className="ps-msd-row-cell ps-right">
                          <span className="ps-msd-cell-label">{t("SIZE")} {displaySizeLabel}</span>
                          <span className="ps-msd-cell-value">{renderRaw ? ((row as any).rawChartRange || "") : dLabelWithUnit(row.chartLabel)}</span>
                        </div>
                      </div>
                      <div className={`ps-msd-row-badge ${fitClass}`}>
                        {renderRaw ? accessoryFitLabelFn(row.fit, t).toUpperCase() : row.isLength ? lengthFitLabelFn(row.fit, t).toUpperCase() : fitBadgeLabel(row.fit, row.isLength)}
                        <svg viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="11" height="11">
                          <path d="M3 8l3.5 3.5L13 5" />
                        </svg>
                      </div>
                    </div>
                  );
                  })}
                </div>
              </div>
            </>
          )}

          {outfitRecommendationNode}

          {/* TRY ANOTHER SIZE picker — only 3 buttons (prev / rec / next) */}
          {!guideOnly && sectionFound !== false && visibleSizes.length > 1 && (
            <div className="ps-msd-sizes">
              <span className="ps-msd-sizes-label">{t("TRY ANOTHER SIZE")}</span>
              <div className="ps-msd-sizes-pills">
                {visibleSizes.map(s => {
                  const isActive = stripLengthVariant(s) === stripLengthVariant(displaySize || "");
                  return (
                    <button key={s} type="button"
                      className={`ps-msd-size-pill${isActive ? " ps-active" : ""}`}
                      onClick={() => setSelectedSize(stripLengthVariant(s) === stripLengthVariant(recSize || "") ? null : s)}
                    >
                      {formatVisibleSizeLabel(s)}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Length picker — only 3 buttons */}
          {!guideOnly && sectionFound !== false && visibleLengths.length > 0 && (
            <div className="ps-msd-sizes">
              <span className="ps-msd-sizes-label">{t("LENGTH ADJUSTMENT")}</span>
              <div className="ps-msd-sizes-pills">
                {visibleLengths.map(s => {
                  const isActive = String(finalDisplayLength ?? "").trim() === String(s ?? "").trim();
                  const helper = lengthHelperMap[String(s).trim()];
                  return (
                    <button key={s} type="button"
                      className={`ps-msd-size-pill${isActive ? " ps-active" : ""}`}
                      onClick={() => setSelectedLength(s === backendLength ? null : s)}
                      style={{ display: "flex", flexDirection: "column", alignItems: "center", lineHeight: 1.15, gap: "0.15vw" }}
                    >
                      <span>{s}</span>
                      {helper && <span style={{ fontSize: "0.55vw", fontWeight: 500, opacity: 0.75 }}>{helper}</span>}
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {/* Footer paragraph (always shows the suggested size, not the user's current pick) */}
          {!guideOnly && sectionFound !== false && (
            <p className="ps-msd-footer-note">
              {isFootwear
                ? <>{t("*Our sizing engine recommends")} {displaySizeLabel} {t("based on your selected brand size and foot length.")}</>
                : <>{t("*Our sizing engine recommends")} {backendSize}{backendLength ? ` (${backendLength})` : ""} {t("based on your measurements and the garment's tailoring chart.")}</>}
            </p>
          )}

	          {profileCompletionCta && (
	            <ProfileCompletionCta onClick={profileCompletionCta.onClick} isMobile={true} t={t} />
	          )}
	        </div>
	        {mobileDetailActions}
		      </div>
		    );
		  }

  if (outfitPiece) {
    const suggestedOutfitSize = formatPillLabel(outfitPiece.recommendedSize || recSize);
    const activeOutfitSize = formatPillLabel(selectedSize || suggestedOutfitSize);
    const outfitSizeOptions = buildOutfitSizeOptions(suggestedOutfitSize);
    const selectOutfitSize = (size: string) => {
      const nextSelected = size === suggestedOutfitSize ? null : size;
      setSelectedSize(nextSelected);
      onPendingCustomSizeChange?.(sectionName, {
        sectionName,
        selectedSize: size,
        displayLabel: size,
        matchDetails: fitRows.map((row) => ({
          measurement: row.area,
          userValue: row.userNum ? `${dNum(row.userNum)} ${effectiveUnitLbl}` : "",
          chartRange: dLabelWithUnit(row.chartLabel),
          fit: row.fit,
        })),
      });
    };
    const changeOutfitUnit = (unit: "cm" | "in") => {
      if (effectiveIsFootwear) setShoeDisplayUnit(unit);
      else onDisplayUnitChange?.(unit);
    };
    const outfitUnit = effectiveIsFootwear ? shoeDisplayUnit : displayUnitId;

    return (
      <div className="ps-outfit-size-detail">
        <div className="ps-outfit-size-detail-scroll">
          <span className="ps-outfit-size-eyebrow">{t("Suggested Size")}</span>

          <section className="ps-outfit-size-recommendation" aria-label={t("Suggested Size")}>
            <div className="ps-outfit-size-value">
              <span>{t("Size")}</span>
              <strong>{activeOutfitSize || "—"}</strong>
            </div>
            <p>
              <span className="ps-outfit-size-check" aria-hidden="true"><CheckIcon size={14} /></span>
              {t("Suggested for you")}
            </p>
          </section>

          <div className="ps-outfit-size-picker-head">
            <span>{t("Select your size")}</span>
            <div className="ps-outfit-size-units" aria-label={t("Measurement units")}>
              {(["cm", "in"] as const).map((unit) => (
                <button
                  key={unit}
                  type="button"
                  className={outfitUnit === unit ? "ps-active" : ""}
                  onClick={() => changeOutfitUnit(unit)}
                  aria-pressed={outfitUnit === unit}
                >
                  {unit}
                </button>
              ))}
            </div>
          </div>

          <div className="ps-outfit-size-options" role="group" aria-label={t("Select your size")}>
            {outfitSizeOptions.map((size) => (
              <button
                key={size}
                type="button"
                className={size === activeOutfitSize ? "ps-active" : ""}
                onClick={() => selectOutfitSize(size)}
                aria-pressed={size === activeOutfitSize}
              >
                {size}
              </button>
            ))}
          </div>

          <section className="ps-outfit-fit-section">
            <h3>{t("Fit Comparison")}</h3>
            <div className="ps-outfit-fit-table-wrap">
              <table className="ps-outfit-fit-table">
                <thead>
                  <tr>
                    <th>{t("Measurement")}</th>
                    <th>{t("Your Body")}</th>
                    <th>{t("Garment Spec")}</th>
                    <th>{t("Fit Result")}</th>
                  </tr>
                </thead>
                <tbody>
                  {fitRows.length > 0 ? fitRows.map((row, index) => (
                    <tr key={`${row.area}-${index}`}>
                      <td>{row.area}</td>
                      <td>{renderRaw ? ((row as any).rawUserValue || "—") : row.userNum ? `${dNum(row.userNum)} ${effectiveUnitLbl}` : "—"}</td>
                      <td>{renderRaw ? ((row as any).rawChartRange || "—") : row.chartLabel ? dLabelWithUnit(row.chartLabel) : "—"}</td>
                      <td>
                        <span className={`ps-outfit-fit-pill ps-${row.fit === "good" ? "good" : row.fit.includes("tight") ? "tight" : "loose"}`}>
                          {renderRaw
                            ? accessoryFitLabelFn(row.fit, t)
                            : row.isLength
                              ? lengthFitLabelFn(row.fit, t)
                              : fitLabelFn(row.fit, t)}
                        </span>
                      </td>
                    </tr>
                  )) : (
                    <tr>
                      <td>{t("Selected size")}</td>
                      <td>—</td>
                      <td>{activeOutfitSize || "—"}</td>
                      <td>—</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </section>

          <div className="ps-outfit-size-note">
            <span aria-hidden="true"><SparkleIcon size={15} /></span>
            <p>
              {t("Size")} {activeOutfitSize || "—"} {t("is suggested for this item based on your fit profile.")}
            </p>
          </div>
        </div>

        <div className="ps-outfit-size-footer">
          <button className="ps-bp-back-btn" onClick={onBack} type="button">
            <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {backLabel || t("Back")}
          </button>
          {resultActionNode}
        </div>
      </div>
    );
  }

  return (
    <div className="ps-tryon-sec-detail" style={{ padding: "1.2vw 0.95vw 1vw", display: "flex", flexDirection: "column", height: "100%", background: "#F8F9FA" }}>
      {/* Content area — scrolls independently so bottom nav stays pinned */}
      <div style={{ flex: 1, minHeight: 0, overflowY: "auto", scrollbarWidth: "thin", scrollbarColor: "rgba(0,0,0,0.04) transparent", display: sectionFound === false ? "flex" : "block", alignItems: sectionFound === false ? "center" : undefined, justifyContent: sectionFound === false ? "center" : undefined }}>
        {false ? (
          /* NO-MATCHING-SIZE per-section card TEMPORARILY DISABLED — was
             gated on `sectionFound === false`. Restore that condition when
             the error UI is needed back. */
          <div style={{
            background: "white", borderRadius: "0.7vw",
            border: "1px solid rgba(0,0,0,0.06)",
            padding: "2vw 2.2vw", textAlign: "center",
            width: "100%", maxWidth: "28vw",
          }}>
            <span style={{ fontSize: "0.7vw", fontWeight: 700, color: "var(--ps-accent)", textTransform: "uppercase", letterSpacing: "0.12em" }}>
              {t("No matching size")}
            </span>
            <p style={{ fontSize: "1vw", fontWeight: 600, color: "var(--ps-text-primary)", margin: "0.8vw 0 0.5vw", lineHeight: 1.4 }}>
              {t("We couldn't find a size that fits for this product.")}
            </p>
            <p style={{ fontSize: "0.72vw", color: "var(--ps-text-secondary)", lineHeight: 1.55, margin: 0 }}>
              {t("Add them to cart.")}
            </p>
          </div>
        ) : (
        <>
        {/* "SUGGESTED SIZE" label — flips to amber "YOUR CHOICE · NOT
            SUGGESTED" when the user picks a size different from the
            backend recommendation. Same treatment the tuxedo section cards
            get when their section has an override. */}
        <span
          style={{
            fontSize: "0.75vw",
            fontWeight: 700,
            color: guideOnly || isRecommended ? "var(--ps-accent)" : "#b45309",
            textTransform: "uppercase",
            letterSpacing: "0.12em",
          }}
        >
          {guideOnly ? t("Size Guide") : isRecommended ? t("Suggested Size") : t("Your Choice · Not Suggested")}
        </span>

        {/* Stable row: country switcher + size value box — country always reserves space to prevent layout shift */}
        <div style={{ display: "flex", alignItems: "center", gap: "0.5vw", marginTop: "0.35vw", marginBottom: "0.4vw" }}>
          {countryOptions.length > 1 && (
            <select
              value={selectedCountry || ""}
              onChange={(e) => setSelectedCountry(e.target.value || null)}
              style={{
                fontSize: "0.7vw",
                fontWeight: 600,
                fontFamily: "inherit",
                color: "var(--ps-text-secondary)",
                background: "white",
                border: "1px solid rgba(0,0,0,0.12)",
                borderRadius: "0.35vw",
                padding: "0.25vw 0.6vw",
                cursor: "pointer",
                outline: "none",
                visibility: isRecommended ? "visible" : "hidden",
              }}
              aria-label={t("Display size in country")}
            >
              <option value="">{t("Standard")}</option>
              {countryOptions.map((c) => (
                <option key={c} value={c}>{c}</option>
              ))}
            </select>
          )}
          {isFootwear && (
            <div className="ps-shoe-unit-toggle" aria-label={t("Foot length unit")}>
              {(["cm", "in"] as const).map((unit) => (
                <button
                  key={unit}
                  type="button"
                  className={shoeDisplayUnit === unit ? "ps-active" : ""}
                  onClick={() => setShoeDisplayUnit(unit)}
                >
                  {unit}
                </button>
              ))}
            </div>
          )}
          {/* Size value box — big number + length inline */}
          <div style={{
            display: "inline-flex",
            alignItems: "baseline",
            gap: "0.4vw",
            background: "white",
            border: "1px solid rgba(0,0,0,0.08)",
            borderRadius: "0.5vw",
            padding: "0.2vw 0.7vw",
          }}>
            {(() => {
              // Long combined-variant labels ("MISSY 12 / Standard") need
              // to drop from 3.5vw → ~1.6vw to fit the size box.
              const isLong = (displaySizeLabel || "").length > 12;
              return (
                <span style={{ fontSize: isLong ? "1.6vw" : "3.5vw", fontWeight: 300, color: "var(--ps-text-primary)", lineHeight: 1, letterSpacing: "-0.02em" }}>
                  {displaySizeLabel}
                </span>
              );
            })()}
            {/* Length suffix is for trouser/jacket/vest where the length
                axis is separate from the size axis. Skip when the size
                label already encodes a customization variant via " / "
                ("MISSY 12 / Standard" → the customization IS the length). */}
            {!guideOnly && !isFootwear && finalDisplayLength && !displaySizeLabel.includes(" / ") && (() => {
              // Label the length with its real dimension name so a pants card
              // reads "40 / Inseam 32" instead of the ambiguous "40 / 32".
              // Jackets render as "40 / Short", vests as "40 / Regular", etc.
              const isNumericLen = /^\d+(\.\d+)?$/.test(String(finalDisplayLength).trim());
              const sectionNameLower = (sectionName || "").toLowerCase();
              const lengthLabel = isNumericLen
                ? (/pant|trouser|inseam/.test(sectionNameLower) ? t("Inseam") : t("Length"))
                : null;
              return (
                <span style={{ fontSize: "1.4vw", fontWeight: 400, color: "var(--ps-text-secondary)", display: "inline-flex", alignItems: "baseline", gap: "0.25vw" }}>
                  <span style={{ fontSize: "0.9vw", fontWeight: 600, color: "var(--ps-text-muted)", letterSpacing: "0.02em" }}>/</span>
                  {lengthLabel && (
                    <span style={{ fontSize: "0.7vw", fontWeight: 600, color: "var(--ps-text-muted)", textTransform: "uppercase", letterSpacing: "0.08em" }}>{lengthLabel}</span>
                  )}
                  <span>{finalDisplayLength}</span>
                </span>
              );
            })()}
            {selectedCountry && isRecommended && (
              <span style={{ fontSize: "0.7vw", color: "var(--ps-text-muted)", marginLeft: "0.2vw" }}>
                ({selectedCountry})
              </span>
            )}
          </div>
        </div>

        {guideOnly && (
          <p style={{ fontSize: "0.75vw", color: "var(--ps-text-secondary)", lineHeight: 1.55, margin: "0 0 1vw" }}>
            {guideOnlyNote}
          </p>
        )}
        {guideOnlyTable}

        {guideOnly && guideOnlySelectableSizes.length > 1 && (
          <div style={{ marginBottom: "0.8vw" }}>
            <span style={{ fontSize: "0.6vw", fontWeight: 700, color: "var(--ps-text-secondary)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: "0.4vw" }}>
              {t("Select a size to add to cart")}
            </span>
            <div style={{ display: "flex", gap: "0.3vw", flexWrap: "wrap" }}>
              {guideOnlySelectableSizes.map((size) => {
                const isActive = selectedSize === size;
                return (
                  <button
                    key={size}
                    type="button"
                    onClick={() => handleGuideOnlySizeSelect(size)}
                    style={{
                      padding: "0.35vw 0.7vw", borderRadius: "0.35vw", fontSize: "0.7vw", fontWeight: 600,
                      border: isActive ? "2px solid var(--ps-accent)" : "2px solid rgba(0,0,0,0.12)",
                      background: isActive ? "var(--ps-accent)" : "transparent",
                      color: isActive ? "white" : "var(--ps-text-primary)",
                      cursor: "pointer", transition: "all 0.2s", fontFamily: "inherit",
                      minWidth: "2vw", textAlign: "center",
                    }}
                  >
                    {size}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Measurement table — white background */}
        {!guideOnly && fitRows.length > 0 && (
          <>
            {bodyResultUnitSwitch}
            <div style={{ background: "white", borderRadius: "0.5vw", border: "1px solid rgba(0,0,0,0.06)", overflow: "hidden", marginBottom: "1.2vw" }}>
              <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr>
                  <th style={{ textAlign: "left", padding: "0.5vw 0.6vw", fontSize: "0.6vw", fontWeight: 600, color: "var(--ps-text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{t("Measurement")}</th>
                  <th style={{ textAlign: "left", padding: "0.5vw 0.6vw", fontSize: "0.6vw", fontWeight: 600, color: "var(--ps-text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{t("Your Body")}</th>
                  <th style={{ textAlign: "left", padding: "0.5vw 0.6vw", fontSize: "0.6vw", fontWeight: 600, color: "var(--ps-text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{t("Garment Spec")}</th>
                  <th style={{ textAlign: "right", padding: "0.5vw 0.6vw", fontSize: "0.6vw", fontWeight: 600, color: "var(--ps-text-secondary)", textTransform: "uppercase", letterSpacing: "0.08em", borderBottom: "1px solid rgba(0,0,0,0.06)" }}>{t("Fit Result")}</th>
                </tr>
              </thead>
              <tbody>
                {fitRows.map((row, i) => {
                  // Length rows use directional tone (short = red, long = orange);
                  // circumference rows use tight = red, loose = orange.
                  const fitColor = row.fit === "good"
                    ? "#2154EF"
                    : row.isLength
                      ? (row.fit.includes("short") || row.fit.includes("tight") ? "#dc2626" : "#d97706")
                      : (row.fit.includes("tight") ? "#dc2626" : "#d97706");
                  const fitBg = row.fit === "good"
                    ? "rgba(33,84,239,0.08)"
                    : row.isLength
                      ? (row.fit.includes("short") || row.fit.includes("tight") ? "rgba(220,38,38,0.08)" : "rgba(217,119,6,0.08)")
                      : (row.fit.includes("tight") ? "rgba(220,38,38,0.08)" : "rgba(217,119,6,0.08)");
                  return (
                    <tr key={i} style={{ borderBottom: i < fitRows.length - 1 ? "1px solid rgba(0,0,0,0.04)" : "none" }}>
                      <td style={{ padding: "0.55vw 0.6vw", fontSize: "0.75vw", fontWeight: 500, color: "var(--ps-text-primary)" }}>{row.area}</td>
                      <td style={{ padding: "0.55vw 0.6vw", fontSize: "0.75vw", color: "var(--ps-text-secondary)" }}>{renderRaw ? ((row as any).rawUserValue || "") : ((isNaN(row.userNum) || row.userNum === 0) ? "" : `${dNum(row.userNum)} ${effectiveUnitLbl}`)}</td>
                      <td style={{ padding: "0.55vw 0.6vw", fontSize: "0.75vw", color: "var(--ps-text-secondary)" }}>{renderRaw ? ((row as any).rawChartRange || "") : dLabelWithUnit(row.chartLabel)}</td>
                      <td style={{ padding: "0.55vw 0.6vw", textAlign: "right" }}>
                        <span style={{ fontSize: "0.6vw", fontWeight: 600, color: fitColor, background: fitBg, borderRadius: "1vw", padding: "0.15vw 0.5vw", whiteSpace: "nowrap" }}>
                          {isFootwear && row.fit === "good" ? t("best match") : renderRaw ? accessoryFitLabelFn(row.fit, t) : row.isLength ? lengthFitLabelFn(row.fit, t) : fitLabelFn(row.fit, t)}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              </table>
            </div>
          </>
        )}

        {/* SELECT SIZE — pill buttons (only 3: prev / rec / next) */}
        {visibleSizes.length > 1 && (
          <div style={{ marginBottom: "0.8vw" }}>
            <span style={{ fontSize: "0.6vw", fontWeight: 700, color: "var(--ps-text-secondary)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: "0.4vw" }}>
              {t("Select")} {sectionName.replace(/\s*[—–-]\s*.*/g, "")} {t("Size")}
            </span>
            <div style={{ display: "flex", gap: "0.3vw", flexWrap: "wrap" }}>
              {visibleSizes.map(s => {
                const isActive = stripLengthVariant(s) === stripLengthVariant(displaySize || "");
                return (
                  <button key={s} onClick={() => setSelectedSize(stripLengthVariant(s) === stripLengthVariant(recSize || "") ? null : s)}
                    style={{
                      padding: "0.35vw 0.7vw", borderRadius: "0.35vw", fontSize: "0.7vw", fontWeight: 600,
                      border: isActive ? "2px solid var(--ps-accent)" : "2px solid rgba(0,0,0,0.12)",
                      background: isActive ? "var(--ps-accent)" : "transparent",
                      color: isActive ? "white" : "var(--ps-text-primary)",
                      cursor: "pointer", transition: "all 0.2s", fontFamily: "inherit",
                      minWidth: "2vw", textAlign: "center",
                    }}
                  >
                    {formatVisibleSizeLabel(s)}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* LENGTH ADJUSTMENT — pill buttons (only 3: prev / rec / next) */}
        {visibleLengths.length > 0 && (
          <div style={{ marginBottom: "0.8vw", marginTop: "0.4vw" }}>
            <span style={{ fontSize: "0.6vw", fontWeight: 700, color: "var(--ps-text-secondary)", textTransform: "uppercase", letterSpacing: "0.1em", display: "block", marginBottom: "0.4vw" }}>
              {t("Length Adjustment")}
            </span>
            <div style={{ display: "flex", gap: "0.3vw", flexWrap: "wrap" }}>
              {visibleLengths.map(s => {
                const isActive = String(finalDisplayLength ?? "").trim() === String(s ?? "").trim();
                const helper = lengthHelperMap[String(s).trim()];
                return (
                  <button key={s} onClick={() => setSelectedLength(s === backendLength ? null : s)}
                    style={{
                      padding: "0.35vw 0.7vw", borderRadius: "0.35vw", fontSize: "0.7vw", fontWeight: 600,
                      border: isActive ? "2px solid var(--ps-accent)" : "2px solid rgba(0,0,0,0.12)",
                      background: isActive ? "var(--ps-accent)" : "transparent",
                      color: isActive ? "white" : "var(--ps-text-primary)",
                      cursor: "pointer", transition: "all 0.2s", fontFamily: "inherit",
                      display: "inline-flex", flexDirection: "column", alignItems: "center", lineHeight: 1.15, gap: "0.1vw",
                    }}
                  >
                    <span>{s}</span>
                    {helper && <span style={{ fontSize: "0.55vw", fontWeight: 500, opacity: 0.75 }}>{helper}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        )}

        {/* Size-apply CTA. Behavior depends on whether try-on already ran:
            - Before try-on: "Use this for Try On" — just stores the pick.
                The next real Try It On click reads it to build fit info +
                silhouette context. Button does NOT hit the network.
            - After try-on: "Try It On" — fires a fresh regen with the
                picked size/length. Network call each click. */}
        {/* Size-pick button removed: the old block fired a fresh Gemini
            try-on the moment the user picked a different size. Per product
            decision (May 2026) try-on is now generated ONCE per submission
            and the size pills are display-only inside the detail view. */}

        </>
        )}
      </div>

      {profileCompletionCta && (
        <ProfileCompletionCta onClick={profileCompletionCta.onClick} t={t} />
      )}

      {/* Bottom bar — always visible, content scrolls above */}
      <div className="ps-tryon-sec-detail-footer" style={{ display: "flex", alignItems: "center", justifyContent: "space-between", paddingTop: "0.6vw", borderTop: "1px solid rgba(0,0,0,0.06)", flexShrink: 0 }}>
        <button className="ps-bp-back-btn" onClick={onBack} type="button" style={{ fontSize: "0.7vw" }}>
          <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {backLabel || t("Back")}
        </button>
        {onTryOn ? (
          <button
            onClick={onTryOn}
            disabled={tryOnProcessing}
            style={{
              padding: "0.45vw 1.2vw", borderRadius: "0.4vw", fontSize: "0.7vw", fontWeight: 600,
              background: "var(--ps-accent)", color: "white", border: "none",
              cursor: tryOnProcessing ? "default" : "pointer",
              opacity: tryOnProcessing ? 0.6 : 1,
              fontFamily: "inherit", display: "flex", alignItems: "center", gap: "0.4vw",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={e => { if (!tryOnProcessing) e.currentTarget.style.opacity = "0.9"; }}
            onMouseLeave={e => { if (!tryOnProcessing) e.currentTarget.style.opacity = "1"; }}
          >
            <CameraIcon size={12} />
            {tryOnProcessing ? `${t("Generating try-on…")}${tryOnElapsedS > 0 ? ` ${tryOnElapsedS}s` : ""}` : t("Try It On")}
          </button>
        ) : resultActionNode ? (
          resultActionNode
        ) : (
          <button onClick={continueLabel ? (onContinue || onBack) : onBack}
            style={{
              padding: "0.45vw 1.2vw", borderRadius: "0.4vw", fontSize: "0.7vw", fontWeight: 600,
              background: "var(--ps-accent)", color: "white", border: "none",
              cursor: "pointer", fontFamily: "inherit", display: "flex", alignItems: "center", gap: "0.3vw",
              transition: "opacity 0.2s",
            }}
            onMouseEnter={e => (e.currentTarget.style.opacity = "0.9")}
            onMouseLeave={e => (e.currentTarget.style.opacity = "1")}
          >
            {continueLabel || t("Continue")} &rarr;
          </button>
        )}
      </div>
    </div>
  );
}

function OutfitPieceProductPanel({
  piece,
  t,
}: {
  piece: StyleMatchSelection;
  t: TranslateFn;
}) {
  const color = piece.color?.trim();
  const displayTitle = piece.garmentType?.trim() || piece.title;
  const detailRows = [
    { label: t("Style"), value: piece.garmentType || piece.title },
    { label: t("Category"), value: piece.slot ? String(piece.slot).replace(/^\w/, (letter) => letter.toUpperCase()) : "" },
    { label: t("Color"), value: color || "" },
    { label: t("SKU"), value: piece.productId || "" },
  ].filter((row) => row.value);

  return (
    <aside className="ps-outfit-product-panel" aria-label={t("Selected try-on piece")}>
      <div className="ps-outfit-product-image-wrap">
        <img src={piece.displayImage || piece.image} alt={piece.title || displayTitle} className="ps-outfit-product-image" />
      </div>

      <div className="ps-outfit-product-copy">
        {color && <span className="ps-outfit-product-color">{color}</span>}
        <h3>{displayTitle}</h3>
        <p>{t("Selected from your try-on pieces")}</p>
        {piece.url ? (
          <a href={piece.url} target="_blank" rel="noopener noreferrer">
            {t("View product")} <ExternalLinkIcon size={14} />
          </a>
        ) : (
          <span className="ps-outfit-product-link-unavailable">{t("Product link unavailable")}</span>
        )}
      </div>

      {detailRows.length > 0 && (
        <dl className="ps-outfit-product-details">
          {detailRows.map((row) => (
            <div key={row.label}>
              <dt>{row.label}</dt>
              <dd>{row.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </aside>
  );
}


export function SizeResultView({
  sizingLoading, sizingResult, sizeGuide, resultImageUrl, productImage, productImages, productCarouselItems, productTitle,
  styleMatchSelections,
  productUrl,
  productMaterial, productDescription,
  sizingUnit, setSizingUnit, setView, handleDownload,
  onContinueShopping,
  isHistoryResult = false,
  onBackToProductPage,
  onAddToBag,
  addToBagLabel,
  continueShoppingLabel,
  backToProductPageLabel,
  onTryOnFeedbackSubmit,
  selectedFile, previewUrl, handleFileSelect, handleRemovePreview, handleTryOnSubmit,
  tryOnProcessing, tryOnStartedAt,
  tryOnErrorMessage,
  bodyLandmarks,
  faceLandmarks = null,
  measurementType = "body",
  isAccessoryProduct = false,
  estimationDone = false,
  activeSection, setActiveSection,
  onResetTryOn, onClose,
  userHeightCm,
	  pendingCustomSizes: pendingCustomSizesProp,
	  onPendingCustomSizeChange,
	  tryOnAvailable = true,
	  showProfileCompletionCta = false,
	  onProfileCompletionCta,
	  portalContainer,
	  t,
	}: {
  estimationDone?: boolean;
  sizingLoading: boolean;
  sizingResult: SizingResult | null;
  sizeGuide: SizeGuide | null;
  resultImageUrl: string | null;
  productImage: string;
  /** Optional gallery of photos for the active color/variant — drives the
   *  auto-cycling carousel card on the single-garment desktop layout while
   *  try-on is processing. */
  productImages?: string[];
  /** Preferred carousel items, typically complete-the-look products. */
  productCarouselItems?: ProductPhotoCarouselItem[];
  /** Selected RAG outfit items used for the multi-garment try-on. */
  styleMatchSelections?: StyleMatchSelection[];
  productTitle: string;
  productUrl?: string;
  /** Garment material/fabric — feeds the "Garment Spotlight" card on
   *  the engaging try-on processing UI. */
  productMaterial?: string;
  /** Garment description — fallback for "Garment Spotlight" when material
   *  is empty. */
  productDescription?: string;
  sizingUnit: string;
  setSizingUnit: (unit: string) => void;
  setView: (v: ViewState) => void;
  handleDownload: () => void;
  onContinueShopping?: () => void;
  isHistoryResult?: boolean;
  onBackToProductPage?: () => void;
  onAddToBag?: () => void | Promise<void>;
  addToBagLabel?: string;
  continueShoppingLabel?: string;
  backToProductPageLabel?: string;
  onTryOnFeedbackSubmit?: (input: { rating?: number; note?: string }) => Promise<void>;
  selectedFile: File | null;
  previewUrl: string | null;
  handleFileSelect: (file: File) => void;
  handleRemovePreview: () => void;
  handleTryOnSubmit: (overrideFile?: File) => void;
  tryOnProcessing?: boolean;
  /** Wall-clock ms when Try It On started — drives elapsed counter. */
  tryOnStartedAt?: number | null;
  /** Try-on failure shown while keeping the successful size result visible. */
  tryOnErrorMessage?: string | null;
  bodyLandmarks?: BodyLandmarks | null;
  faceLandmarks?: import("../../face-detect").FaceLandmarks | null;
  measurementType?: "body" | "face" | "head" | "foot" | "wrist";
  isAccessoryProduct?: boolean;
  activeSection: string | null;
  setActiveSection: (s: string | null) => void;
  /** Clear try-on result + uploaded photo so the user can retry from the photo guide. */
  onResetTryOn?: () => void;
  /** Close the SDK modal entirely (Continue Shopping action). */
  onClose?: () => void;
  /** Active profile's height in cm — used to populate the height row in the fit table. */
  userHeightCm?: number;
  /** Parent-owned per-section override map. Keyed by section name
   *  ("Jacket", "Pants", "Vest"). Empty = all sections on the backend's
   *  recommendation. Each section can hold its own {size, length,
   *  matchDetails, displayLabel} independently. */
  pendingCustomSizes?: Record<string, {
    sectionName: string;
    selectedSize: string;
    selectedLength?: string;
    displayLabel: string;
    matchDetails?: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }>;
  }>;
  /** Setter. Passing `null` clears that section's override. */
  onPendingCustomSizeChange?: (sectionName: string, override: null | {
    sectionName: string;
    selectedSize: string;
    selectedLength?: string;
    displayLabel: string;
    matchDetails?: Array<{ measurement: string; userValue: string; chartRange: string; fit: string }>;
  }) => void;
	  /** When false, the size-result view drops every Try-On affordance and
	   *  shows "Continue Shopping" instead. Set to false when sizing came in
	   *  via the manual measurements path (no photo collected upstream). */
	  tryOnAvailable?: boolean;
	  showProfileCompletionCta?: boolean;
	  onProfileCompletionCta?: () => void;
	  portalContainer?: Element | DocumentFragment;
	  t: TranslateFn;
	}) {
  void productUrl;
  // The backend's recommend response carries the unit that matchDetails
  // values are actually in (it picks columns per chart, in cm or in).
  // resultUnit = what the data is denominated in.
  // unitLbl = user's display preference (always sizingUnit).
  // When they differ, SectionDetailView converts at render time.
  const resultUnitRaw = (sizingResult?.unit || sizingUnit || "").toString().toLowerCase();
  const resultUnit: DisplayUnit = resultUnitRaw === "mm"
    ? "mm"
    : resultUnitRaw === "cm"
      ? "cm"
      : "in";
  const unitLbl = resultUnit === "mm"
    ? t("mm")
    : sizingUnit === "cm" ? t("cm") : t("in");
  const shoeUserGender = normalizeShoeGender((sizingResult as any)?.userGender || (sizingResult as any)?.detectedGender) || undefined;
  const [editVals, setEditVals] = useState<Record<string, string>>({});
  const cleanNum = cleanNumFn;
  const pNum = pNumFn;
  const pRange = pRangeFn;
  const resolvedContinueShoppingLabel = continueShoppingLabel?.trim() || t("Continue Shopping");
  const resolvedAddToBagLabel = addToBagLabel?.trim() || t("Add to Bag");
  const resolvedBackToProductPageLabel = backToProductPageLabel?.trim() || t("Back to product");
  const handleContinueAction = onContinueShopping || onClose;
  const handleBackToProductPageAction = onBackToProductPage || handleContinueAction;
	  const canAddToBag = !!onAddToBag && !isHistoryResult && !!sizingResult && sizingResult.found !== false;
	  const [addToBagState, setAddToBagState] = useState<"idle" | "adding" | "added">("idle");
	  const [addToBagError, setAddToBagError] = useState<string | null>(null);

  useEffect(() => {
    if (addToBagState !== "added") return;
    const timer = window.setTimeout(() => setAddToBagState("idle"), 1500);
    return () => window.clearTimeout(timer);
  }, [addToBagState]);

	  const handleAddToBagClick = useCallback(async () => {
	    if (!onAddToBag || addToBagState !== "idle") return;
	    setAddToBagState("adding");
	    setAddToBagError(null);
	    try {
	      await onAddToBag();
	      setAddToBagState("added");
	    } catch (error) {
	      setAddToBagError(error instanceof Error ? error.message : t("Could not add this item to cart"));
	      setAddToBagState("idle");
	    }
	  }, [addToBagState, onAddToBag, t]);

  const renderShoppingCta = useCallback((options?: { allowAddToBag?: boolean; showContinue?: boolean }) => {
    if (isHistoryResult && handleBackToProductPageAction) {
      return (
        <div className="ps-tryon-v2-action-group ps-tryon-v2-action-group-history">
	          <button
            className="ps-tryon-v2-cta ps-tryon-v2-cta-secondary ps-tryon-v2-cta-compact ps-tryon-v2-history-product-cta"
            style={{ marginTop: 0 }}
            onClick={handleBackToProductPageAction}
            type="button"
          >
            <ExternalLinkIcon size={13} />
            <span>{resolvedBackToProductPageLabel}</span>
          </button>
        </div>
      );
    }

    const allowAddToBag = options?.allowAddToBag !== false;
    const showContinue = options?.showContinue !== false;
    if (allowAddToBag && canAddToBag) {
      return (
        <div className={`ps-tryon-v2-action-group${!showContinue ? " ps-tryon-v2-action-group-single" : ""}`}>
          {showContinue && (
            <button
              className="ps-tryon-v2-cta ps-tryon-v2-cta-secondary"
              style={{ marginTop: 0 }}
              onClick={handleContinueAction}
              type="button"
            >
              {resolvedContinueShoppingLabel} &rarr;
            </button>
          )}
          <button
            className={`ps-tryon-v2-cta ps-tryon-v2-add-bag-cta${addToBagState === "added" ? " is-added" : ""}`}
            style={{ marginTop: 0 }}
            onClick={handleAddToBagClick}
            type="button"
            disabled={addToBagState !== "idle"}
          >
            {addToBagState === "added" ? <CheckIcon size={16} /> : <ShoppingBagIcon size={17} />}
	            <span>{addToBagState === "added" ? t("Added to bag") : addToBagState === "adding" ? t("Adding...") : resolvedAddToBagLabel}</span>
	          </button>
	          {addToBagError && (
	            <div className="ps-tryon-add-bag-error" role="status">
	              {addToBagError}
	            </div>
	          )}
	        </div>
	      );
    }

    if (!showContinue) {
      return <div className="ps-tryon-v2-action-group ps-tryon-v2-action-group-single" aria-hidden="true" />;
    }

    return (
      <button
        className="ps-tryon-v2-cta"
        style={{ marginTop: 0 }}
        onClick={handleContinueAction}
        type="button"
      >
        {resolvedContinueShoppingLabel} &rarr;
      </button>
    );
  }, [
    canAddToBag,
	    addToBagState,
	    addToBagError,
    handleBackToProductPageAction,
    handleAddToBagClick,
    handleContinueAction,
    isHistoryResult,
    resolvedAddToBagLabel,
    resolvedBackToProductPageLabel,
    resolvedContinueShoppingLabel,
    t,
  ]);

  const cellVal = useCallback((row: unknown, colIdx: number, header?: string): string => {
    return cellValFn(row, colIdx, header);
  }, []);

  const sizeColIdx = useMemo(() => {
    if (!sizeGuide?.headers || !sizeGuide?.rows) return -1;
    const rows = sizeGuide.rows;
    const standardIdx = sizeGuide.headers.findIndex(h => {
      const cleaned = h.trim().replace(/\(.*?\)/g, "").trim();
      return /^standard(?:\s+size)?$/i.test(cleaned);
    });
    if (standardIdx >= 0) return standardIdx;

    const suggested = String(sizingResult?.recommendedSize || "").trim();
    if (suggested) {
      const byRecommendedValue = sizeGuide.headers.findIndex((h, c) => {
        const cleaned = h.trim().replace(/\(.*?\)/g, "").trim();
        const sizeLikeHeader = /^(size|sizes|taglia|größe|taille|standard)$/i.test(cleaned);
        const alphaSizeColumn = rows.some(r => /^(XXS|XS|S|M|L|XL|XXL|XXXL|[3-9]XL|ONE SIZE)$/i.test(cellVal(r, c, h)));
        return (sizeLikeHeader || alphaSizeColumn) && rows.some(r => cellVal(r, c, h) === suggested);
      });
      if (byRecommendedValue >= 0) return byRecommendedValue;
    }

    const byName = sizeGuide.headers.findIndex(h => /size|taglia|größe|taille/i.test(h.trim()));
    if (byName >= 0) return byName;
    for (let c = 0; c < sizeGuide.headers.length; c++) {
      const hdr = sizeGuide.headers[c];
      if (sizeGuide.rows.some(r => /^(XXS|XS|S|M|L|XL|XXL|XXXL|[3-9]XL|ONE SIZE|\d{1,2})$/i.test(cellVal(r, c, hdr)))) return c;
    }
    return 0;
  }, [sizeGuide, cellVal, sizingResult?.recommendedSize]);

  const sizeHeader = sizeGuide?.headers?.[sizeColIdx] || t("Size");

  const allSizes = useMemo(() => {
    if (sizeColIdx < 0 || !sizeGuide?.rows) return [];
    return sizeGuide.rows.map(r => cellVal(r, sizeColIdx, sizeHeader)).filter(Boolean);
  }, [sizeGuide, sizeColIdx, sizeHeader, cellVal]);

  // Per-column unit map for the legacy single-garment path. Same logic as
  // SectionDetailView's columnUnits — header marker first, then first
  // non-empty cell suffix as ground truth.
  const sizeGuideColumnUnits = useMemo<Array<"cm" | "in" | null>>(() => {
    if (!sizeGuide?.headers || !sizeGuide?.rows) return [];
    const units: Array<"cm" | "in" | null> = [];
    for (let i = 0; i < sizeGuide.headers.length; i++) {
      const h = (sizeGuide.headers[i] || "").toLowerCase();
      if (/\(cm\)|centimeter/i.test(h)) { units.push("cm"); continue; }
      if (/\(in\)|\(inch|inch/i.test(h)) { units.push("in"); continue; }
      let inferred: "cm" | "in" | null = null;
      for (const row of sizeGuide.rows) {
        const cell = String(cellVal(row, i, sizeGuide.headers[i]) || "").trim();
        if (!cell) continue;
        if (/cm\b|centimeter/i.test(cell)) { inferred = "cm"; break; }
        if (/in\b|inch|"$/i.test(cell)) { inferred = "in"; break; }
      }
      units.push(inferred);
    }
    return units;
  }, [sizeGuide, cellVal]);

  // Chart range lookup for recalculating fit when user picks a different size
  const chartRangeFor = useCallback((measurement: string, size: string) => {
    if (!sizeGuide?.headers || !sizeGuide?.rows || sizeColIdx < 0) return null;
    const b = measurement.toLowerCase().trim();
    const preferCm = unitLbl === "cm" || unitLbl === "";
    const wantUnit: "cm" | "in" = preferCm ? "cm" : "in";

    // Collect all matching columns by name; pick by unit preference. No
    // exact-match shortcut — that's the bug we're fixing.
    const candidates: number[] = [];
    for (let i = 0; i < sizeGuide.headers.length; i++) {
      const a = (sizeGuide.headers[i] || "").toLowerCase().trim();
      if (a === b) { candidates.push(i); continue; }
      const aBare = a.replace(/\s*\(.*?\)\s*/g, "").trim();
      if (aBare === b || a.includes(b) || b.includes(aBare)) {
        candidates.push(i);
      }
    }
    if (candidates.length === 0) return null;
    let mc = candidates.find((i) => sizeGuideColumnUnits[i] === wantUnit);
    if (mc == null) mc = candidates[0];

    const mHeader = sizeGuide.headers[mc];
    const row = sizeGuide.rows.find(r => cellVal(r, sizeColIdx, sizeHeader) === size);
    if (!row) return null;
    const val = cellVal(row, mc, mHeader);
    if (!val) return null;
    const parsed = pRange(val);

    // Convert if the only available column is in the other unit, so the user
    // sees apples-to-apples values next to their input.
    const colUnit = sizeGuideColumnUnits[mc];
    if (colUnit === "in" && preferCm) {
      const toCm = (v: number) => +(v * 2.54).toFixed(1);
      return { range: String(toCm(parsed.min)) + (parsed.min !== parsed.max ? "–" + String(toCm(parsed.max)) : ""), min: toCm(parsed.min), max: toCm(parsed.max) };
    }
    if (colUnit === "cm" && !preferCm) {
      const toIn = (v: number) => +(v / 2.54).toFixed(1);
      return { range: String(toIn(parsed.min)) + (parsed.min !== parsed.max ? "–" + String(toIn(parsed.max)) : ""), min: toIn(parsed.min), max: toIn(parsed.max) };
    }
    return { range: val, ...parsed };
  }, [sizeGuide, sizeColIdx, sizeHeader, unitLbl, cellVal, pRange, sizeGuideColumnUnits]);

  // Backend/Gemini determines best size — no SDK recalculation
  const origSize = sizingResult?.recommendedSize || "";
  const activeSize = origSize;

  // No SDK recalculation — fitRows not used, displayFitRows renders Gemini data directly

  // International size labels come from the backend ONLY. The SDK doesn't
  // own conversion tables — backend provides chart-derived data when the
  // merchant chart has country columns, and falls back to its own table
  // when it doesn't. Either way the SDK just renders.
  const allIntlSizes = useMemo<Record<string, string>>(() => {
    return (sizingResult?.internationalSizes || {}) as Record<string, string>;
  }, [sizingResult]);

  const [resultTab, setResultTab] = useState<"fit" | "chart">("fit");
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [showFullChart, setShowFullChart] = useState(false);
  const [showLines, setShowLines] = useState(false);
  // Mirror of SectionDetailView's recomputed fit rows for the active size
  // pill. Lives here (not inside SectionDetailView) because the desktop v2
  // layout renders the MeasurementOverlay OUTSIDE the SectionDetailView,
  // and we still need the overlay's annotations to refresh whenever the
  // user picks a different size pill inside the detail panel.
  const [activeFitRows, setActiveFitRows] = useState<Array<{ area: string; userNum: number; chartLabel: string; fit: string }>>([]);
  const [showVisualFit, setShowVisualFit] = useState(false);
  // Lightbox state for the try-on result image. Hovering shows a zoom
  // cursor; clicking opens a full-viewport overlay with the image at
  // max size. ESC or click anywhere outside closes it.
  const [zoomImageUrl, setZoomImageUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!zoomImageUrl) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setZoomImageUrl(null); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [zoomImageUrl]);
		  const [poseLines, setPoseLines] = useState<MeasurementLines | null>(null);
		  const [poseReady, setPoseReady] = useState(false);
		  const [imgDims, setImgDims] = useState<{ w: number; h: number }>({ w: 800, h: 1200 });
		  const resultImageRef = useRef<HTMLImageElement | null>(null);
		  const [loadedResultImageUrl, setLoadedResultImageUrl] = useState<string | null>(null);
	  // Visual progression for the desktop snap-steps card uses real
  // milestones now: bodyLandmarks (pose ready) → estimationDone
  // (LLM measurements ready) → sizingDone (final size ready).
  const analyzingDone = estimationDone;

  // Per-section override map. Empty since the size-pick regen flow was
  // removed — every reader naturally falls back to the backend recommendation.
  const pendingCustomSizes = pendingCustomSizesProp ?? {};

  // Track image natural dimensions for SVG overlay alignment
	  const handleImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
	    const el = e.currentTarget;
	    if (el.naturalWidth && el.naturalHeight) {
	      setImgDims({ w: el.naturalWidth, h: el.naturalHeight });
	    }
	  }, []);
		  useEffect(() => {
		    if (!resultImageUrl) setLoadedResultImageUrl(null);
		  }, [resultImageUrl, tryOnProcessing]);
		  useLayoutEffect(() => {
		    if (!resultImageUrl || tryOnProcessing) return;
		    const img = resultImageRef.current;
		    if (img?.complete && img.naturalWidth > 0) setLoadedResultImageUrl(resultImageUrl);
		  }, [resultImageUrl, tryOnProcessing]);
			  const resultImageLoading = !!resultImageUrl && !tryOnProcessing && loadedResultImageUrl !== resultImageUrl;
			  const resultImageInteractive = !!resultImageUrl && !tryOnProcessing && !resultImageLoading;
			  const resultImageAvailable = !!resultImageUrl && !tryOnProcessing;
		  const handleResultAwareImgLoad = useCallback((e: React.SyntheticEvent<HTMLImageElement>) => {
		    if (resultImageUrl && !tryOnProcessing) setLoadedResultImageUrl(resultImageUrl);
		    handleImgLoad(e);
		  }, [handleImgLoad, resultImageUrl, tryOnProcessing]);

  // Run pose detection on the try-on result image
  useEffect(() => {
    if (!resultImageUrl || tryOnProcessing) { setPoseLines(null); setPoseReady(false); return; }
    setPoseReady(false);
    detectMeasurementLines(resultImageUrl)
      .then((lines) => { setPoseLines(lines); setPoseReady(true); })
      .catch(() => { setPoseReady(true); });
  }, [resultImageUrl, tryOnProcessing]);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploadDragOver, setUploadDragOver] = useState(false);
  const uploadInputRef = useRef<HTMLInputElement>(null);
  const [showPhotoGuide, setShowPhotoGuide] = useState(false);
  const [guideFile, setGuideFile] = useState<File | null>(null);
  const [guideDragOver, setGuideDragOver] = useState(false);
  const guideInputRef = useRef<HTMLInputElement>(null);
  const [guidePreviewUrl, setGuidePreviewUrl] = useState<string | null>(null);
  const [poseLoading, setPoseLoading] = useState(false);

  // Create/revoke stable preview URL for guide file
  useEffect(() => {
    if (guideFile) {
      const url = URL.createObjectURL(guideFile);
      setGuidePreviewUrl(url);
      return () => URL.revokeObjectURL(url);
    }
    setGuidePreviewUrl(null);
  }, [guideFile]);

  const displaySize = selectedSize || origSize;
  const isRecommended = displaySize === origSize;

  // Recalculate fit when user selects a different size — deduplicate by measurement name
  const displayFitRows = useMemo(() => {
    if (!sizingResult?.matchDetails?.length) return [];
    // Deduplicate: keep first occurrence of each measurement name (case-insensitive)
    const seen = new Set<string>();
    const unique = sizingResult.matchDetails.filter(m => {
      const key = m.measurement.toLowerCase().replace(/\s*\(.*?\)\s*/g, "").trim();
      if (seen.has(key)) return false;
      seen.add(key);
      return true;
    });
    return unique.map(m => {
      const userNum = pNumFn(m.userValue);
      let chartLabel = m.chartRange;
      let fit: string = m.fit;
      // If user selected a different size, look up new chart range and recalculate fit
      if (displaySize !== origSize) {
        const alt = chartRangeFor(m.measurement, displaySize);
        if (alt && alt.min > 0) {
          chartLabel = cleanNumFn(alt.range);
          const rMin = alt.min, rMax = alt.max;
          const range = rMax - rMin;
          const threshold = range > 0 ? range * 0.5 : rMin * 0.05 || 3;
          const tol = Math.max((rMax || rMin) * 0.005, 0.25);
          if (userNum >= rMin - tol && userNum <= rMax + tol) fit = "good";
          else if (userNum < rMin) { const diff = rMin - userNum; fit = diff > threshold * 2 ? "too-loose" : diff > threshold ? "loose" : "a-bit-loose"; }
          else { const diff = userNum - rMax; fit = diff > threshold * 2 ? "too-tight" : diff > threshold ? "tight" : "a-bit-tight"; }
        }
      }
      return { area: m.measurement, userNum: pNumFn(m.userValue), chartLabel, fit };
    });
  }, [sizingResult, displaySize, origSize, chartRangeFor]);

  const sizeGuideRow = useMemo(() => {
    if (!sizeGuide?.headers || !sizeGuide?.rows || sizeColIdx < 0) return null;
    const row = sizeGuide.rows.find(r => cellVal(r, sizeColIdx, sizeHeader) === displaySize);
    if (!row) return null;
    const entries: Array<{ header: string; value: string }> = [];
    for (let i = 0; i < sizeGuide.headers.length; i++) {
      const h = sizeGuide.headers[i] || "";
      if (i === sizeColIdx) continue;
      const val = cellVal(row, i, h);
      if (val) entries.push({ header: h, value: val });
    }
    return entries;
  }, [sizeGuide, sizeColIdx, sizeHeader, displaySize, cellVal]);

  // REMOVED: userMeasureValues — no client-side measurement editing

  // Render multiple result cards only for true multi-garment charts
  // (Jacket/Pants/Vest, Top/Bottom, etc.). Conversion sections such as
  // Standard/Italy are one product size system, not separate garments.
  const isMultiSection = hasMultiGarmentSections(
    Object.keys(sizingResult?.sections || sizeGuide?.sections || {}),
  );

  // Pre-compute section results — use backend's sections response as source of truth
  const allSectionEntries = useMemo(() => {
    // Build a GLOBAL measurements map first — aggregated across every
    // section's matchDetails plus the top-level result. Body measurements
    // (chest, waist, hips, height, ...) don't change per section — only
    // the garment spec does — so when viewing Jacket Size (chest-only),
    // we still want access to the height value from Jacket Length. Without
    // this, the synthetic "Height (Regular)" row in SectionDetailView has
    // no user value and renders as "—".
    const globalMeasurements: Record<string, number> = {};
    const absorbDetails = (details?: Array<{ measurement: string; userValue?: string }>) => {
      if (!details) return;
      for (const m of details) {
        const key = m.measurement.toLowerCase();
        if (globalMeasurements[key] == null) {
          const n = pNumFn(m.userValue);
          if (n > 0) globalMeasurements[key] = n;
        }
      }
    };
    absorbDetails(sizingResult?.matchDetails);
    if (sizingResult?.sections) {
      for (const sec of Object.values(sizingResult.sections)) absorbDetails(sec.matchDetails);
    }
    if (userHeightCm && userHeightCm > 0 && globalMeasurements["height"] == null) {
      globalMeasurements["height"] = userHeightCm;
    }

    // If backend returned merged sections, use those directly
    if (sizingResult?.sections && Object.keys(sizingResult.sections).length > 0) {
      return Object.entries(sizingResult.sections).map(([name, secResult]) => {
        // Section names are the KEYS in sizeGuide.sections (not a .name
        // property on the value). Match by normalizing both sides —
        // "Jacket" (merged) should find "Jacket Size" in the size guide.
        const nameLc = name.toLowerCase();
        let section: NormalizedSection | undefined = sizeGuide?.sections?.[name];
        if (!section && sizeGuide?.sections) {
          const match = Object.entries(sizeGuide.sections).find(([key]) => {
            const kLc = key.toLowerCase();
            const normalized = kLc.replace(/\s*(size|sizing)\s*/gi, "").trim();
            if (!normalized) return false;
            return nameLc === kLc || nameLc.includes(normalized) || normalized.includes(nameLc);
          });
          if (match) section = match[1] as NormalizedSection;
        }
        if (!section) section = { headers: [], rows: [], requiredFields: [] } as NormalizedSection;

        // Start from the global map so every section has access to all
        // measurements (height, chest, waist etc) — then let the section's
        // own matchDetails override if it has a more specific value.
        const userMeasurements: Record<string, number> = { ...globalMeasurements };
        if (secResult.matchDetails) {
          for (const m of secResult.matchDetails) {
            const n = pNumFn(m.userValue);
            if (n > 0) userMeasurements[m.measurement.toLowerCase()] = n;
          }
        }

        return { name, section, secResult, userMeasurements };
      });
    }

    // Fallback: use raw size guide sections (old behavior)
    if (!isMultiSection || !sizeGuide?.sections) return [];
    return Object.entries(sizeGuide.sections)
      .map(([name, section]) => {
        const secResult = sizingResult?.sections?.[name] || { recommendedSize: sizingResult?.recommendedSize || "", matchDetails: [] };
        const userMeasurements: Record<string, number> = { ...globalMeasurements };
        if (secResult.matchDetails) {
          for (const m of secResult.matchDetails) {
            const n = pNumFn(m.userValue);
            if (n > 0) userMeasurements[m.measurement.toLowerCase()] = n;
          }
        }
        return { name, section, secResult, userMeasurements };
      });
  }, [isMultiSection, sizeGuide, sizingResult, userHeightCm]);

  // Cards = non-length sections; lengths = length sections (shown inline in detail)
  const sectionEntries = useMemo(() => allSectionEntries.filter(e => !isLengthSection(e.name)), [allSectionEntries]);
  const lengthEntries = useMemo(() => allSectionEntries.filter(e => isLengthSection(e.name)), [allSectionEntries]);

  // Produce a combined size label for cards that have a paired length
  // section — e.g. "40" + "Long" → "40L". For non-word length recs (pants
  // inseam "32"), fall through to a "40/32" style. Vests don't have a
  // paired length SECTION but do carry a `length` property on the merged
  // section result (Regular / Big / Big & Tall) — we surface that too so
  // the vest card reads "M / Regular" instead of bare "M".
  const combinedSizeLabel = useCallback((sectionName: string, baseSize: string): string => {
    if (!baseSize) return baseSize;
    const paired = findLengthForSection(sectionName, lengthEntries);
    let lengthRec = paired?.secResult?.recommendedSize?.trim() || "";
    if (!lengthRec) {
      // Fall back to the section's inline `length` (vest's Regular/Big/...)
      const inline = allSectionEntries.find(e => e.name === sectionName)?.secResult as { length?: string } | undefined;
      lengthRec = inline?.length?.trim() || "";
    }
    if (!lengthRec) return baseSize;
    // Word lengths → single-letter suffix: Short→S, Regular→R, Long→L,
    // Extra-Long→XL. Tolerates localized labels by checking the first
    // alpha char; numeric lengths (inseam "32") render as "40/32".
    const lowered = lengthRec.toLowerCase();
    if (/^\d/.test(lengthRec)) {
      // Numeric length = body dimension (inseam for pants, sleeve for some
      // shirts). Label it so "34 / 32" doesn't look like a fraction.
      const isPants = /pant|trouser|inseam/i.test(sectionName);
      const dimension = isPants ? "Inseam" : "Length";
      return `${baseSize} / ${dimension} ${lengthRec}`;
    }
    // When the main size is numeric (jacket chest "40"), a single-letter
    // suffix reads cleanly in menswear notation → "40R" / "40L". When the
    // main size is a letter (vest "M"/"L"), concatenating would produce
    // ambiguous strings like "MR" — render with a slash and the full word
    // instead ("M / Regular"). Extra-Long stays as the "XL" suffix either
    // way because that's the canonical label.
    if (/^x(tra)?[- ]?long/.test(lowered)) return /^\d/.test(baseSize) ? `${baseSize}XL` : `${baseSize} / Extra Long`;
    const baseIsNumeric = /^\d/.test(baseSize);
    if (baseIsNumeric) {
      const first = lowered.charAt(0).toUpperCase();
      return `${baseSize}${first}`;
    }
    // Non-numeric base → "M / Regular", "LT / Big & Tall", etc.
    const prettyLength = lengthRec.replace(/\s+/g, " ").trim();
    return `${baseSize} / ${prettyLength}`;
  }, [lengthEntries, allSectionEntries]);

  // Height line for each card — prefer user's inputted height, fall back
  // to anything we aggregated into the sections' measurement maps.
  const heightLineLabel = useMemo(() => {
    const cm = userHeightCm || 0;
    if (!cm) return "";
    if (unitLbl === "in") {
      const inches = cm / 2.54;
      const ft = Math.floor(inches / 12);
      const rem = Math.round(inches - ft * 12);
      return `${ft}'${rem}"`;
    }
    return `${Math.round(cm)} cm`;
  }, [userHeightCm, unitLbl]);


  // Snap processing: show step cards only while sizing is still in flight.
  // Try-on now fires in parallel with sizing, so tryOnProcessing alone is no
  // longer a reason to hide the cards — once sizingResult arrives we want
  // the right-side cards/chart visible immediately even though try-on is
  // still cooking. The left-side image swap + scan overlay continues to
  // signal "try-on in progress" via the per-card render paths below.
  const hasPhoto = !!previewUrl;
  // Scanning UI stays visible as long as sizingLoading is true. We
  // intentionally apply sizingResult to state the millisecond the backend
  // responds (so the auto-fire useEffect can submit try-on at ~200 ms)
  // but keep sizingLoading on for the minVisible duration so the scanning
  // animation has time to register. Used to also gate on `!sizingResult`,
  // which was causing the scanning UI to flash and the result card to
  // appear instantly.
  const isSnapProcessing = hasPhoto && sizingLoading;
  const isSizingOnly = !hasPhoto && sizingLoading;
  const sizingDone = !!sizingResult;
  const tryOnDone = !!resultImageUrl && !tryOnProcessing;
  const allDone = hasPhoto ? (sizingDone && tryOnDone) : sizingDone;
  const isMobile = useIsMobile();
  const profileCompletionCta = showProfileCompletionCta && onProfileCompletionCta
    ? { onClick: onProfileCompletionCta }
    : null;
  const carouselItems = productCarouselItems?.filter((item) => item.image) ?? [];
  const ragResultItems = useMemo(
    () => (styleMatchSelections || [])
      .filter((item) => item?.image && item?.title)
      .slice(0, 8),
    [styleMatchSelections],
  );
  const showRagResultGarments = ragResultItems.length > 0;
  const [selectedRagItemKey, setSelectedRagItemKey] = useState<string | null>(null);
  const selectedRagItem = useMemo(() => (
    ragResultItems.find((item, index) => (
      `${item.productId || item.image}-${index}` === selectedRagItemKey
    )) || null
  ), [ragResultItems, selectedRagItemKey]);
  useEffect(() => {
    if (!selectedRagItemKey || selectedRagItem) return;
    setSelectedRagItemKey(null);
  }, [selectedRagItem, selectedRagItemKey]);
  const productCarouselNode = (
    <ProductPhotoCarouselCard
      photos={productImages}
      items={carouselItems}
      productTitle={productTitle}
      t={t}
    />
  );
  // Accessory products still affect layout hints (e.g. `renderRaw` in
  // SectionDetailView to stop the size table from scaling the product image).
  const isAccessory = isAccessoryProduct || measurementType === "face" || measurementType === "head" || measurementType === "wrist";
  const suppressFitOverlayActions = isAccessory || measurementType === "foot";
  // True when the backend couldn't find a size that fits the user. We treat
  // `undefined` as fit-found (older backends don't send the flag), so the
  // fail state only kicks in when the backend explicitly reports false —
  // avoids regressing products that worked fine before this release.
  const noFit = sizingResult?.found === false;
  const guideOnlyResult = !!((sizingResult as any)?.guideOnly || (sizingResult as any)?.oneSize);
  // Excluded when the user chose manual measurements (no photo upstream ->
  // no model image to send to Gemini) or no valid size was found. Shoe
  // try-on is allowed; the backend receives the shoe category prompt.
  const vtoExcluded = !tryOnAvailable || (noFit && !guideOnlyResult);
  const visibleTryOnError = tryOnErrorMessage && !tryOnProcessing && !resultImageUrl ? tryOnErrorMessage : null;

  // Free-measurement notice — shown whenever the backend's pre-flight check
  // decided the user-entered weight contradicts the photo and the pipeline
  // measured directly from the image. Rendered on every result panel so the
  // user sees it regardless of single/multi-section or device mode.
  const mismatchNotice = sizingResult?.mismatchDetected ? (
    <div style={{
      margin: isMobile ? "8px 0 0" : "0.4vw 0 0",
      padding: isMobile ? "10px 12px" : "0.6vw 0.85vw",
      borderRadius: isMobile ? "8px" : "0.45vw",
      background: "rgba(33, 84, 239, 0.06)",
      border: "1px solid rgba(33, 84, 239, 0.2)",
      display: "flex", alignItems: "flex-start", gap: isMobile ? "8px" : "0.4vw",
    }}>
      <svg viewBox="0 0 24 24" width={isMobile ? "16" : "0.9vw"} height={isMobile ? "16" : "0.9vw"} fill="none" stroke="#2154ef" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, marginTop: "2px" }} aria-hidden="true">
        <circle cx="12" cy="12" r="10" />
        <line x1="12" y1="16" x2="12" y2="12" />
        <line x1="12" y1="8" x2="12.01" y2="8" />
      </svg>
      <div style={{ fontSize: isMobile ? "12px" : "0.62vw", lineHeight: 1.45, color: "var(--ps-text-secondary)" }}>
        {t("We noticed your entered details didn't match your photo. To give you accurate sizing, we measured your body directly from the photo.")}
      </div>
    </div>
  ) : null;

  return (
    <div className="ps-tryon-sr">
      {/* Full-viewport lightbox for the try-on result. Click the result
       *  image (which already shows a zoom-in cursor) → opens this
       *  overlay sized to the BROWSER viewport. Portaled to document.body
       *  so it escapes the SDK modal's overflow / clip-path / transform
       *  ancestors — otherwise the overlay was clipped to the SDK frame
       *  instead of covering the user's whole screen.
       *  Close: click outside the image, press ESC, or the × button. */}
      {zoomImageUrl && typeof document !== "undefined" && createPortal(
        <div
          onClick={() => setZoomImageUrl(null)}
          style={{
            position: "fixed",
            inset: 0,
            zIndex: 2147483647,
            background: "rgba(0,0,0,0.92)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            cursor: "zoom-out",
          }}
        >
          <img
            src={zoomImageUrl}
            alt={productTitle}
            style={{
              maxWidth: "100vw",
              maxHeight: "100vh",
              width: "auto",
              height: "auto",
              objectFit: "contain",
              display: "block",
            }}
            onClick={(e) => e.stopPropagation()}
          />
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              setZoomImageUrl(null);
            }}
            aria-label={t("Close")}
            style={{
              position: "fixed",
              top: "1rem",
              right: "1rem",
              zIndex: 2147483647,
              background: "rgba(255,255,255,0.16)",
              border: "1px solid rgba(255,255,255,0.32)",
              color: "white",
              width: "2.4rem",
              height: "2.4rem",
              borderRadius: "50%",
              fontSize: "1.4rem",
              lineHeight: 1,
              cursor: "pointer",
              pointerEvents: "auto",
              touchAction: "manipulation",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            ×
          </button>
        </div>,
	        portalContainer ?? document.body
	      )}
      {visibleTryOnError && (
        <div
          className="ps-tryon-sr-vto-error"
          role="status"
          style={{
            margin: isMobile ? "0 16px 10px" : "0 0 0.7vw",
            padding: isMobile ? "10px 12px" : "0.55vw 0.75vw",
            borderRadius: isMobile ? "8px" : "0.45vw",
            border: "1px solid rgba(220, 38, 38, 0.2)",
            background: "rgba(220, 38, 38, 0.06)",
            color: "var(--ps-text-secondary)",
            fontSize: isMobile ? "12px" : "0.68vw",
            lineHeight: 1.45,
          }}
        >
          {visibleTryOnError}
        </div>
      )}
      {/* Mobile text-mode sizing — same fancy scanning UI as photo flow,
          just no MediaPipe skeleton (no photo to detect on) */}
      {isMobile && isSizingOnly && (
        <MobileScanningView
          productImage={productImage}
          bodyLandmarks={null}
          faceLandmarks={null}
          measurementType={measurementType}
          sizingDone={sizingDone}
          onSwitchToManual={() => setView("body-profile")}
          t={t}
        />
      )}
      {/* Desktop simple spinner when sizing without photo */}
      {!isMobile && isSizingOnly && (
        <div className="ps-tryon-sr-split">
          <div className="ps-tryon-sr-img-col">
            <img src={productImage} alt={productTitle} className="ps-tryon-sr-product-img" />
          </div>
          <div className="ps-tryon-sr-right-col" style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: "0.8vw" }}>
            <div className="ps-tryon-size-loading-spinner" />
            <p style={{ color: "var(--ps-text-muted)", fontSize: "0.8vw" }}>{t("Generating your suggested size...")}</p>
          </div>
        </div>
      )}
      {/* Mobile: dedicated scanning viewfinder UI matching the reference */}
      {isMobile && isSnapProcessing && previewUrl && (
        <MobileScanningView
          previewUrl={previewUrl}
          bodyLandmarks={bodyLandmarks ?? null}
          faceLandmarks={faceLandmarks ?? null}
          measurementType={measurementType}
          sizingDone={sizingDone}
          tryOnProcessing={tryOnProcessing}
          productMaterial={productMaterial}
          productDescription={productDescription}
          onSwitchToManual={() => setView("body-profile")}
          t={t}
        />
      )}
      {/* Desktop step cards (unchanged) */}
      {!isMobile && isSnapProcessing && !allDone && (
        <div className="ps-tryon-sr-split">
          <div className="ps-tryon-sr-img-col" style={{ position: "relative" }}>
            <img
              src={previewUrl || productImage}
              alt={productTitle}
              className="ps-tryon-sr-product-img"
              onLoad={handleImgLoad}
            />
            {/* Close-up head/eyewear photos render face mesh. Full-body
                head/eyewear photos render the body skeleton. */}
            {bodyLandmarks && (!(measurementType === "face" || measurementType === "head") || !faceLandmarks) && (
              <SkeletonOverlay landmarks={bodyLandmarks} imgWidth={imgDims.w} imgHeight={imgDims.h} />
            )}
            {(measurementType === "face" || measurementType === "head") && faceLandmarks && (
              <FaceOverlay landmarks={faceLandmarks} imgWidth={imgDims.w} imgHeight={imgDims.h} />
            )}
          </div>
          {(() => {
            const isFaceCategory = measurementType === "face" || measurementType === "head";
            const detectionDone = isFaceCategory ? !!faceLandmarks || !!bodyLandmarks : !!bodyLandmarks;
            const detectLabel = isFaceCategory
              ? (measurementType === "head" ? t("Detecting head") : t("Detecting face"))
              : t("Detecting body pose");

            // All snap flows now use the cycling stage text — body, face,
            // head, foot. The stacked step rows were replaced entirely.
            void detectionDone; void detectLabel;
            return (
              <div className="ps-tryon-sr-right-col" style={{ display: "flex", alignItems: "center", justifyContent: "center" }}>
                <StageCycler
                  category={(isFaceCategory ? measurementType : "body") as "body" | "face" | "head" | "foot"}
                  sizingDone={sizingDone}
                  t={t}
                />
              </div>
            );
          })()}
        </div>
      )}
      {(allDone || (sizingResult && !isSnapProcessing)) && (
        <>
          {/* ── MULTI-SECTION (tuxedo, suit) ── */}
          {isMultiSection ? (
            activeSection ? (
              /* ── Detail view for a single section ── */
              (() => {
                const entry = sectionEntries.find(e => e.name === activeSection);
                if (!entry) return null;
                // Mobile: render SectionDetailView directly (it owns its own
                // mobile layout with product image, fit blueprint, etc.)
                if (isMobile) {
                  return (
                    <>
                    {mismatchNotice && <div style={{ padding: "0 16px" }}>{mismatchNotice}</div>}
                    <SectionDetailView
                      sectionName={entry.name}
                      section={entry.section}
                      sectionResult={entry.secResult}
                      sectionFound={(entry.secResult as { found?: boolean })?.found}
                      userMeasurements={entry.userMeasurements}
                      unitLbl={unitLbl}
                      chartUnit={resultUnit}
                      onDisplayUnitChange={setSizingUnit}
                      lengthEntry={(() => {
                        const direct = findLengthForSection(entry.name, lengthEntries);
                        if (direct) return direct;
                        const hasInlineLengths = (entry.secResult as any)?.availableLengths?.length > 0;
                        if (!hasInlineLengths) return null;
                        return lengthEntries.find(le => le.section.headers.some(h => /height|altezza|estatura|\(cm\)/i.test(h))) || null;
                      })()}
                      onBack={() => setActiveSection(null)}
                      onContinue={handleContinueAction}
                      resultActionNode={(canAddToBag || isHistoryResult || !!resultImageUrl || vtoExcluded) ? renderShoppingCta({ showContinue: false }) : undefined}
                      internationalSizes={(entry.secResult as any)?.internationalSizes}
                      productImage={tryOnProcessing && previewUrl ? previewUrl : (resultImageUrl || productImage)}
                      productTitle={productTitle}
                      isMobile={true}
		                      isTryOnImage={resultImageAvailable}
	                      tryOnProcessing={tryOnProcessing}
                      tryOnStartedAt={tryOnStartedAt}
                      showLines={showLines}
                      onToggleLines={suppressFitOverlayActions ? undefined : () => setShowLines((value) => !value)}
		                      onOpenImage={resultImageAvailable ? () => setZoomImageUrl(resultImageUrl!) : undefined}
		                      onDownload={resultImageAvailable ? handleDownload : undefined}
                      onFeedbackSubmit={onTryOnFeedbackSubmit}
                      onImageLoad={handleImgLoad}
                      overlayNode={
                        tryOnProcessing ? (
                          <>
                            <TryOnGenerationBadge tryOnStartedAt={tryOnStartedAt ?? null} t={t} />
                            <RegenScanOverlay active />
                          </>
	                        ) : resultImageAvailable ? (
                          <MeasurementOverlay
                            lines={poseLines || FALLBACK_MEASUREMENT_LINES}
                            fitRows={(() => {
                              const all = [...(sizingResult?.matchDetails || [])];
                              if (sizingResult?.sections) {
                                for (const sec of Object.values(sizingResult.sections)) {
                                  if (sec.matchDetails) all.push(...sec.matchDetails);
                                }
                              }
                              const seen = new Set<string>();
                              return all.filter(m => { const k = m.measurement.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; })
                                .map(m => ({ area: m.measurement, userNum: parseFloat(m.userValue) || 0, chartLabel: m.chartRange || "", fit: m.fit }));
                            })()}
                            show={showLines}
                            imgWidth={imgDims.w}
                            imgHeight={imgDims.h}
                          />
                        ) : null
                      }
                      t={t}
                      isFootwear={measurementType === "foot"}
                      userGender={shoeUserGender}
	                      allSizes={(entry.secResult as any)?.allSizes ?? (sizingResult as any)?.allSizes}
                      onPendingCustomSizeChange={onPendingCustomSizeChange}
	                      profileCompletionCta={null}
	                    />
                    </>
                  );
                }
	                return (
	                  <div className="ps-tryon-v2">
                    {/* LEFT: product image, user photo (during try-on), or try-on result */}
                    <div className="ps-tryon-v2-media-stack">
	                      {resultImageInteractive && (
                        <ResultImageActionBar
                          showLines={!!showLines}
                          canShowFit={!suppressFitOverlayActions}
                          onToggleFit={() => setShowLines((value) => !value)}
                          onOpenImage={() => setZoomImageUrl(resultImageUrl!)}
                          onDownload={handleDownload}
                          t={t}
                        />
                      )}
		                      <div className="ps-tryon-v2-bg" style={{ position: "relative" }}>
		                        <img
		                          ref={resultImageRef}
		                          key={tryOnProcessing && previewUrl ? previewUrl : (resultImageUrl || productImage)}
	                          src={tryOnProcessing && previewUrl ? previewUrl : (resultImageUrl || productImage)}
	                          alt={productTitle}
	                          className={`ps-tryon-v2-bg-img${resultImageLoading ? " ps-result-image-pending" : ""}`}
	                          onLoad={handleResultAwareImgLoad}
	                          style={resultImageInteractive ? { cursor: "zoom-in" } : undefined}
	                          onClick={resultImageInteractive ? () => setZoomImageUrl(resultImageUrl!) : undefined}
	                        />
	                        {resultImageLoading && <ResultImageLoadingOverlay t={t} />}
                        {tryOnProcessing && (
                          <TryOnGenerationBadge tryOnStartedAt={tryOnStartedAt ?? null} t={t} />
                        )}
                        <RegenScanOverlay active={!!tryOnProcessing} />
	                      {resultImageInteractive && (
                          <div className="ps-tryon-sr-img-actions">
                            <TryOnExperienceFeedback t={t} onSubmit={onTryOnFeedbackSubmit} />
                          </div>
                        )}
		                    </div>
		                  </div>
                    {/* RIGHT: section detail */}
                    <div className="ps-tryon-v2-panel" key={`detail-${activeSection}`}>
                      {mismatchNotice}
                      <SectionDetailView
                        sectionName={entry.name}
                        section={entry.section}
                        sectionResult={entry.secResult}
                        sectionFound={(entry.secResult as { found?: boolean })?.found}
                        userMeasurements={entry.userMeasurements}
                        unitLbl={unitLbl}
                        chartUnit={resultUnit}
                        onDisplayUnitChange={setSizingUnit}
                        lengthEntry={(() => {
                        const direct = findLengthForSection(entry.name, lengthEntries);
                        if (direct) return direct;
                        const hasInlineLengths = (entry.secResult as any)?.availableLengths?.length > 0;
                        if (!hasInlineLengths) return null;
                        return lengthEntries.find(le => le.section.headers.some(h => /height|altezza|estatura|\(cm\)/i.test(h))) || null;
                      })()}
                        onBack={() => setActiveSection(null)}
                        onContinue={handleContinueAction}
                        resultActionNode={(canAddToBag || isHistoryResult || !!resultImageUrl || vtoExcluded) ? renderShoppingCta({ showContinue: false }) : undefined}
                        internationalSizes={(entry.secResult as any)?.internationalSizes}
                        tryOnProcessing={tryOnProcessing}
                        tryOnStartedAt={tryOnStartedAt}
                        onFeedbackSubmit={onTryOnFeedbackSubmit}
                        t={t}
                        isFootwear={measurementType === "foot"}
                        userGender={shoeUserGender}
	                        allSizes={(entry.secResult as any)?.allSizes ?? (sizingResult as any)?.allSizes}
                        onPendingCustomSizeChange={onPendingCustomSizeChange}
	                        profileCompletionCta={null}
	                      />
                    </div>
                  </div>
                );
              })()
            ) : isMobile ? (
              /* ── Mobile section picker: vertical list, section cards, try-on CTA ── */
              <MultiSectionMobile
                productImage={productImage}
                productTitle={productTitle}
                sizingResult={sizingResult!}
                sizeGuide={sizeGuide}
                sectionEntries={sectionEntries.map(({ name, secResult }) => {
                  const base = (secResult as any)?.size || secResult.recommendedSize || "";
                  return { name, secResult: { ...(secResult as any), recommendedSize: combinedSizeLabel(name, base) } as { recommendedSize: string } };
                })}
                pendingCustomSizes={pendingCustomSizes}
                onSelectSection={(name) => setActiveSection(name)}
                onTryOn={() => { setGuideFile(selectedFile || null); setShowPhotoGuide(true); }}
                tryOnProcessing={tryOnProcessing}
                tryOnStartedAt={tryOnStartedAt}
                resultImageUrl={resultImageUrl}
	                tryOnDone={resultImageInteractive}
                onTryAgain={() => {
                  onResetTryOn?.();
                  setGuideFile(selectedFile || null);
                  setShowPhotoGuide(true);
                }}
                onClose={handleContinueAction}
                resultActionNode={(canAddToBag || isHistoryResult) ? renderShoppingCta() : undefined}
                outfitRecommendationNode={productCarouselNode}
                showLines={showLines}
                onToggleLines={suppressFitOverlayActions ? undefined : () => setShowLines((value) => !value)}
	                onOpenImage={resultImageAvailable ? () => setZoomImageUrl(resultImageUrl!) : undefined}
	                onDownload={resultImageAvailable ? handleDownload : undefined}
		                imageActionBar={resultImageAvailable ? (
                  <ResultImageActionBar
                    showLines={!!showLines}
                    canShowFit={!suppressFitOverlayActions}
                    onToggleFit={() => setShowLines((value) => !value)}
                    onOpenImage={() => setZoomImageUrl(resultImageUrl!)}
                    onDownload={handleDownload}
                    t={t}
                  />
                ) : null}
                feedbackSlot={resultImageAvailable ? (
                  <div className="ps-msr-feedback-slot">
                    <TryOnExperienceFeedback t={t} isMobile onSubmit={onTryOnFeedbackSubmit} />
                  </div>
                ) : null}
                onImageLoad={handleImgLoad}
                profileCompletionCta={profileCompletionCta ? <ProfileCompletionCta onClick={profileCompletionCta.onClick} isMobile={true} t={t} /> : null}
                overlayNode={
	                  resultImageAvailable ? (
                    <MeasurementOverlay
                      lines={poseLines || FALLBACK_MEASUREMENT_LINES}
                      fitRows={(() => {
                        const all = [...(sizingResult?.matchDetails || [])];
                        if (sizingResult?.sections) {
                          for (const sec of Object.values(sizingResult.sections)) {
                            if (sec.matchDetails) all.push(...sec.matchDetails);
                          }
                        }
                        const seen = new Set<string>();
                        return all.filter(m => { const k = m.measurement.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; })
                          .map(m => ({ area: m.measurement, userNum: parseFloat(m.userValue) || 0, chartLabel: m.chartRange || "", fit: m.fit }));
                      })()}
                      show={showLines}
                      imgWidth={imgDims.w}
                      imgHeight={imgDims.h}
                    />
                  ) : null
                }
                t={t}
              />
            ) : (
              /* ── Desktop section picker: split layout — image left, image cards right ── */
              <div className="ps-tryon-v2">
                {/* Left — product image or try-on processing */}
                <div className="ps-tryon-v2-media-stack">
                  {resultImageInteractive && (
                    <ResultImageActionBar
                      showLines={!!showLines}
                      canShowFit={!suppressFitOverlayActions}
                      onToggleFit={() => setShowLines((value) => !value)}
                      onOpenImage={() => setZoomImageUrl(resultImageUrl!)}
                      onDownload={handleDownload}
                      t={t}
                    />
                  )}
		                  <div className="ps-tryon-v2-bg" style={{ position: "relative" }}>
		                    <img
		                          ref={resultImageRef}
		                          key={tryOnProcessing && previewUrl ? previewUrl : (resultImageUrl || productImage)}
	                          src={tryOnProcessing && previewUrl ? previewUrl : (resultImageUrl || productImage)}
	                          alt={productTitle}
	                          className={`ps-tryon-v2-bg-img${resultImageLoading ? " ps-result-image-pending" : ""}`}
	                          onLoad={handleResultAwareImgLoad}
	                          style={resultImageInteractive ? { cursor: "zoom-in" } : undefined}
	                          onClick={resultImageInteractive ? () => setZoomImageUrl(resultImageUrl!) : undefined}
	                        />
	                        {resultImageLoading && <ResultImageLoadingOverlay t={t} />}
                    {tryOnProcessing && (
                      <TryOnGenerationBadge tryOnStartedAt={tryOnStartedAt ?? null} t={t} />
                    )}
                    <RegenScanOverlay active={!!tryOnProcessing} />
	                    {resultImageInteractive && poseReady && poseLines && (
                      <MeasurementOverlay lines={poseLines} fitRows={(() => {
                        const all = [...(sizingResult?.matchDetails || [])];
                        if (sizingResult?.sections) {
                          for (const sec of Object.values(sizingResult.sections)) {
                            if (sec.matchDetails) all.push(...sec.matchDetails);
                          }
                        }
                        const seen = new Set<string>();
                        return all.filter(m => { const k = m.measurement.toLowerCase(); if (seen.has(k)) return false; seen.add(k); return true; })
                          .map(m => ({ area: m.measurement, userNum: parseFloat(m.userValue) || 0, chartLabel: m.chartRange || "", fit: m.fit }));
                      })()} show={showLines} imgWidth={imgDims.w} imgHeight={imgDims.h} />
                    )}
	                        {resultImageInteractive && (
                      <div className="ps-tryon-sr-img-actions">
                        <TryOnExperienceFeedback t={t} onSubmit={onTryOnFeedbackSubmit} />
                      </div>
                    )}
                  </div>
                </div>

                {/* Right — image cards stacked + actions */}
                <div className="ps-tryon-v2-panel ps-tryon-v2-result-panel ps-tryon-v2-multi-result-panel" key="panel-results">
                  {profileCompletionCta ? (
                    <div className="ps-tryon-v2-profile-head ps-expanded">
                      <ProfileCompletionCta onClick={profileCompletionCta.onClick} placement="header" t={t} />
                    </div>
                  ) : (
                    <div className="ps-tryon-v2-result-copy">
                      <h3 className="ps-tryon-v2-title">{t("Your Suggested Size")}</h3>
                      <p className="ps-tryon-v2-subtitle">{t("Tap any section for detailed breakdown")}</p>
                    </div>
                  )}
                  {mismatchNotice}
                  <div className="ps-tryon-v2-sep" />

                  <div className="ps-tryon-sr-cards-v2">
                    {sectionEntries.map(({ name, secResult }, idx) => {
                      const sectionImg = sizeGuide?.sectionImages?.[name];
                      const builtInIcon = garmentIconForSection(name);
                      const isLast = idx === sectionEntries.length - 1 && sectionEntries.length % 2 !== 0;
                      const sec = secResult as any;
                      // Per-section override: when set, the card shows the
                      // user's pick and a warning pill instead of the backend's
                      // recommendation. Cards without an override still show
                      // the suggested label as before.
                      const override = pendingCustomSizes[name];
                      const isOverridden = !!override;
                      const displayFull = isOverridden
                        ? override.displayLabel
                        : combinedSizeLabel(name, sec.size || secResult.recommendedSize || "");
                      return (
                        <button key={name} className={`ps-tryon-sr-card-v2${isLast ? " ps-full" : ""}${isOverridden ? " ps-overridden" : ""}`} onClick={() => setActiveSection(name)} style={{ animationDelay: `${idx * 0.07}s` }}>
                          <div className="ps-tryon-sr-card-v2-text">
                            <span className="ps-tryon-sr-card-v2-label">
                              {builtInIcon && <img src={builtInIcon} alt="" className="ps-tryon-sr-card-v2-label-icon" />}
                              {name.replace(/\s*[—–-]\s*.*/g, "")}
                            </span>
                            <span className="ps-tryon-sr-card-v2-value">
                              {sec.found === false ? t("No fit") : (() => {
                                const slashIdx = displayFull.indexOf(" / ");
                                if (slashIdx === -1) return displayFull;
                                return (
                                  <>
                                    <span>{displayFull.slice(0, slashIdx)}</span>
                                    <span className="ps-tryon-sr-card-v2-value-suffix">{displayFull.slice(slashIdx)}</span>
                                  </>
                                );
                              })()}
                            </span>
                            <span
                              className={`ps-tryon-sr-card-v2-rec-pill${isOverridden ? " is-overridden" : ""}`}
                            >
                              {isOverridden
                                ? t("YOUR SELECTION")
                                : t("SUGGESTED")}
                            </span>
                            <span className="ps-tryon-sr-card-v2-view">
                              {t("VIEW DETAILS")} <span aria-hidden="true">&rsaquo;</span>
                            </span>
                          </div>
                          {sectionImg && <img src={sectionImg} alt={name} className="ps-tryon-sr-card-v2-img" />}
                        </button>
                      );
                    })}
                  </div>
                  {productCarouselNode}

                  <div className="ps-tryon-v2-result-actions">
                    <button className="ps-bp-back-btn" onClick={() => setView("body-profile")} type="button">
                      <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {t("Back")}
                    </button>
                    {canAddToBag || isHistoryResult || (resultImageUrl && !tryOnProcessing) || vtoExcluded ? (
                      renderShoppingCta()
                    ) : (
                      <button
                        className="ps-tryon-v2-cta"
                        style={{ marginTop: 0 }}
                        disabled={tryOnProcessing}
                        onClick={() => {
                          setGuideFile(selectedFile || null);
                          setShowPhotoGuide(true);
                        }}
                      >
                        <CameraIcon size={14} /> {tryOnProcessing ? t("Processing...") : t("Try It On")}
                      </button>
                    )}
                  </div>
                </div>
              </div>
            )
          ) : (
            /* ── SINGLE-PIECE — rendered via SectionDetailView for visual parity with multi-garment ── */
            (() => {
              // Build a fake section entry from the flat size guide + sizing result.
              // If the backend returned non-garment sections like Standard/Italy,
              // collapse them into this single card instead of showing multiple
              // product cards for one garment.
              const fallbackSingleEntry = sectionEntries[0] || allSectionEntries.find(e => !isLengthSection(e.name));
              const fallbackSingleResult = fallbackSingleEntry?.secResult as any | undefined;
              const hasFlatChart = !!(sizeGuide?.found && sizeGuide.headers?.length && sizeGuide.rows?.length);
              const singleSection = hasFlatChart
                ? { headers: sizeGuide?.headers || [], rows: sizeGuide?.rows || [], requiredFields: sizeGuide?.requiredFields || [] }
                : (fallbackSingleEntry?.section || { headers: [], rows: [], requiredFields: [] });
              const singleResult = {
                recommendedSize: sizingResult?.recommendedSize || fallbackSingleResult?.recommendedSize || fallbackSingleResult?.size || "",
                matchDetails: sizingResult?.matchDetails?.length ? sizingResult.matchDetails : (fallbackSingleResult?.matchDetails || []),
                guideOnly: guideOnlyResult,
                oneSize: !!(sizingResult as any)?.oneSize,
                sizeLabel: (sizingResult as any)?.sizeLabel || fallbackSingleResult?.sizeLabel,
                availableSizes: (sizingResult as any)?.availableSizes || fallbackSingleResult?.availableSizes,
              };
              const singleInternationalSizes = sizingResult?.internationalSizes || fallbackSingleResult?.internationalSizes;
              const singleAllSizes = (sizingResult as any)?.allSizes || fallbackSingleResult?.allSizes;
              const singleUserMeasurements: Record<string, number> = {};
              if (singleResult.matchDetails) {
                for (const m of singleResult.matchDetails) singleUserMeasurements[m.measurement.toLowerCase()] = pNumFn(m.userValue);
              }
              const sectionName = guideOnlyResult ? t("Size Guide") : t("Your Fit");

              // Single-garment "Try It On" — if we already collected a photo
              // earlier (AI sizing mode), prefill the guide with it so the
              // user reviews their existing photo instead of being asked to
              // upload again. Otherwise show the empty upload step.
              const handleSingleTryOn = () => {
                if (resultImageUrl) return;     // try-on already happened — no-op
                setGuideFile(selectedFile || null);
                setShowPhotoGuide(true);
              };

              if (isMobile) {
                return (
                  <>
                  {mismatchNotice && <div style={{ padding: "0 16px" }}>{mismatchNotice}</div>}
                  <SectionDetailView
                    sectionName={sectionName}
                    section={singleSection}
                    sectionResult={singleResult}
                    sectionFound={sizingResult?.found}
                    userMeasurements={singleUserMeasurements}
                    unitLbl={unitLbl}
                    chartUnit={resultUnit}
                    onDisplayUnitChange={setSizingUnit}
                    lengthEntry={null}
                    onBack={() => {
                      if (resultImageUrl) onResetTryOn?.();
                      setView("body-profile");
                    }}
                    onContinue={handleContinueAction}
                    backLabel={t("Back")}
                    internationalSizes={singleInternationalSizes}
                    onTryOn={(canAddToBag || isHistoryResult || resultImageUrl || vtoExcluded) ? undefined : handleSingleTryOn}
                    resultActionNode={(canAddToBag || isHistoryResult || !!resultImageUrl || vtoExcluded) ? renderShoppingCta({ showContinue: false }) : undefined}
	                    continueLabel={resultImageUrl ? resolvedContinueShoppingLabel : undefined}
                    tryOnProcessing={tryOnProcessing} tryOnStartedAt={tryOnStartedAt}
                    productImage={resultImageUrl || productImage}
                    productTitle={productTitle}
                    isMobile={true}
                    renderRaw={isAccessory}
                    isFootwear={measurementType === "foot"}
                    userGender={shoeUserGender}
	                    allSizes={singleAllSizes}
                    onPendingCustomSizeChange={onPendingCustomSizeChange}
		                    onActiveFitChange={setActiveFitRows}
		                    profileCompletionCta={null}
                    outfitRecommendationNode={productCarouselNode}
			                    isTryOnImage={resultImageAvailable}
                    showLines={showLines}
                    onToggleLines={suppressFitOverlayActions ? undefined : () => setShowLines((value) => !value)}
		                    onOpenImage={resultImageAvailable ? () => setZoomImageUrl(resultImageUrl!) : undefined}
		                    onDownload={resultImageAvailable ? handleDownload : undefined}
                    onFeedbackSubmit={onTryOnFeedbackSubmit}
                    onImageLoad={handleImgLoad}
                    overlayNode={
	                      resultImageAvailable ? (
                        <MeasurementOverlay
                          lines={poseLines || FALLBACK_MEASUREMENT_LINES}
                          fitRows={(sizingResult?.matchDetails || []).map(m => ({ area: m.measurement, userNum: parseFloat(m.userValue) || 0, chartLabel: m.chartRange || "", fit: m.fit }))}
                          show={showLines}
                          imgWidth={imgDims.w}
                          imgHeight={imgDims.h}
                        />
                      ) : null
                    }
                    t={t}
                  />
                  </>
                );
              }

	              const isOutfitPieceDetail = activeSection === sectionName && !!selectedRagItem;
	              return (
	                <div className={`ps-tryon-v2${isOutfitPieceDetail ? " ps-outfit-piece-detail-layout" : ""}`}>
                  {/* LEFT: user photo with scan overlay during processing,
                      else the try-on result or the product image. */}
                  <div className="ps-tryon-v2-media-stack">
	                    {resultImageInteractive && (
                      <ResultImageActionBar
                        showLines={!!showLines}
                        canShowFit={!suppressFitOverlayActions}
                        onToggleFit={() => setShowLines((value) => !value)}
                        onOpenImage={() => setZoomImageUrl(resultImageUrl!)}
                        onDownload={handleDownload}
                        t={t}
                      />
                    )}
		                    <div className="ps-tryon-v2-bg" style={{ position: "relative" }}>
		                      <img
		                          ref={resultImageRef}
		                          key={tryOnProcessing && previewUrl ? previewUrl : (resultImageUrl || productImage)}
	                          src={tryOnProcessing && previewUrl ? previewUrl : (resultImageUrl || productImage)}
	                          alt={productTitle}
	                          className={`ps-tryon-v2-bg-img${resultImageLoading ? " ps-result-image-pending" : ""}`}
	                          onLoad={handleResultAwareImgLoad}
	                          style={resultImageInteractive ? { cursor: "zoom-in" } : undefined}
	                          onClick={resultImageInteractive ? () => setZoomImageUrl(resultImageUrl!) : undefined}
	                        />
	                        {resultImageLoading && <ResultImageLoadingOverlay t={t} />}
                      {tryOnProcessing && (
                        <TryOnGenerationBadge tryOnStartedAt={tryOnStartedAt ?? null} t={t} />
                      )}
                      <RegenScanOverlay active={!!tryOnProcessing} />
                    {resultImageInteractive && poseReady && poseLines && (
                        <MeasurementOverlay
                          lines={poseLines}
                          fitRows={activeFitRows.length > 0
                            ? activeFitRows
                            : (sizingResult?.matchDetails || []).map(m => ({ area: m.measurement, userNum: parseFloat(m.userValue) || 0, chartLabel: m.chartRange || "", fit: m.fit }))}
                          show={showLines}
                          imgWidth={imgDims.w}
                          imgHeight={imgDims.h}
                        />
                      )}
	                      {resultImageInteractive && (
                        <div className="ps-tryon-sr-img-actions">
                          <TryOnExperienceFeedback t={t} onSubmit={onTryOnFeedbackSubmit} />
                        </div>
                      )}
	                    </div>
	                    {isOutfitPieceDetail && (
	                      <div className="ps-outfit-piece-photo-note">
	                        {t("AI is refining drape, fit, and lighting.")}
	                      </div>
	                    )}
	                  </div>
                  {/* RIGHT: two-mode panel — CARD VIEW (default) shows a single
                      summary card + gallery photos below; clicking the card
                      flips to DETAIL VIEW which renders the full SectionDetailView.
                      Mirrors the multi-garment card→detail behavior. */}
                  <div className={`ps-tryon-v2-panel${activeSection ? "" : " ps-tryon-v2-result-panel"}`} key={`panel-single-${activeSection ? "detail" : "card"}`}>
                    {mismatchNotice}
                    {activeSection === sectionName ? (
                      /* DETAIL VIEW — full size table, same as multi-garment */
                      <SectionDetailView
                        sectionName={sectionName}
                        section={singleSection}
                        sectionResult={singleResult}
                        sectionFound={sizingResult?.found}
                        userMeasurements={singleUserMeasurements}
                        unitLbl={unitLbl}
                        chartUnit={resultUnit}
                        onDisplayUnitChange={setSizingUnit}
                        lengthEntry={null}
	                        onBack={() => {
	                          setActiveSection(null);
	                          setSelectedRagItemKey(null);
	                        }}
                        onContinue={handleContinueAction}
                        backLabel={t("Back")}
                        internationalSizes={singleInternationalSizes}
                        onTryOn={(canAddToBag || isHistoryResult || resultImageUrl || vtoExcluded) ? undefined : handleSingleTryOn}
                        resultActionNode={(canAddToBag || isHistoryResult || !!resultImageUrl || vtoExcluded) ? renderShoppingCta({ showContinue: false }) : undefined}
                        continueLabel={resultImageUrl ? resolvedContinueShoppingLabel : undefined}
                        tryOnProcessing={tryOnProcessing} tryOnStartedAt={tryOnStartedAt}
                        onFeedbackSubmit={onTryOnFeedbackSubmit}
                        t={t}
                        renderRaw={isAccessory}
                        isFootwear={measurementType === "foot"}
                        userGender={shoeUserGender}
	                        allSizes={singleAllSizes}
                        onPendingCustomSizeChange={onPendingCustomSizeChange}
		                        onActiveFitChange={setActiveFitRows}
		                        profileCompletionCta={null}
	                        outfitPiece={selectedRagItem}
	                      />
                    ) : false ? (
                      /* NO-FIT EMPTY STATE — TEMPORARILY DISABLED. Was gated
                         on `sizingResult?.found === false`. Restore that
                         condition when you want the error UI back. */
                      <div className="ps-tryon-nofit">
                        <div className="ps-tryon-nofit-icon" aria-hidden="true">
                          <svg width="36" height="36" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="12" cy="12" r="10" />
                            <line x1="15" y1="9" x2="9" y2="15" />
                            <line x1="9" y1="9" x2="15" y2="15" />
                          </svg>
                        </div>
                        <h3 className="ps-tryon-nofit-title">{t("No size matches your measurements")}</h3>
                        <p className="ps-tryon-nofit-sub">{t("This product's size chart doesn't carry a fit close enough to your body. Try another product or update your profile.")}</p>
                        <div className="ps-tryon-nofit-actions">
                          <button
                            className="ps-bp-back-btn"
                            onClick={() => {
                              if (resultImageUrl) onResetTryOn?.();
                              setView("body-profile");
                            }}
                            type="button"
                          >
                            <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {t("Back")}
                          </button>
	                          {renderShoppingCta({ allowAddToBag: false })}
	                        </div>
	                      </div>
	                    ) : (
                      /* CARD VIEW — clickable summary card + gallery strip */
                      <>
                        {showRagResultGarments ? (
                          <>
                            <div className="ps-tryon-v2-result-copy ps-single-result-copy">
                              <h3 className="ps-tryon-v2-title">{t("Your Try-On Pieces")}</h3>
                              <p className="ps-tryon-v2-subtitle">{t("Tap a garment for detailed size breakdown")}</p>
                            </div>
                            <div className="ps-rag-result-garments" aria-label={t("Selected try-on garments")}>
                              {ragResultItems.map((item, index) => {
                                const label = item.garmentType || item.title || t("Garment");
                                const color = item.color?.trim();
                                const size = item.recommendedSize?.trim();
	                                const itemKey = `${item.productId || item.image}-${index}`;
	                                return (
	                                  <button
	                                    key={itemKey}
	                                    className="ps-rag-result-garment-card"
	                                    type="button"
	                                    onClick={() => {
	                                      setSelectedRagItemKey(itemKey);
	                                      setActiveSection(sectionName);
	                                    }}
                                  >
                                    <img src={item.displayImage || item.image} alt={item.title || label} className="ps-rag-result-garment-img" />
                                    <span className="ps-rag-result-garment-overlay" aria-hidden="true" />
                                    <span className="ps-rag-result-garment-copy">
                                      {color && <span className="ps-rag-result-garment-color">{color}</span>}
                                      <span className="ps-rag-result-garment-title">{label}</span>
                                      {size && <span className="ps-rag-result-garment-size">{t("Size")}: {size}</span>}
                                      <span className="ps-rag-result-garment-details">{t("View size chart")} &rsaquo;</span>
                                    </span>
                                  </button>
                                );
                              })}
                            </div>
                          </>
                        ) : (() => {
                          const cardTitle = guideOnlyResult ? t("Size Guide") : t("Your Fit");
                          const rawLabelText = pendingCustomSizes[sectionName]?.displayLabel || singleResult.recommendedSize || singleResult.sizeLabel || (guideOnlyResult ? t("One Size") : "—");
                          const labelText = measurementType === "foot"
                            ? formatShoeSizeLabel(rawLabelText, shoeUserGender)
                            : rawLabelText;
                          const isLong = labelText.length > 12;
                          const badgeText = guideOnlyResult
                            ? t("SIZE GUIDE")
                            : pendingCustomSizes[sectionName]
                              ? t("YOUR SELECTION")
                              : t("SUGGESTED");
                          return (
                            <>
                        {profileCompletionCta ? (
                          <div className="ps-tryon-v2-result-head">
                            <div className="ps-tryon-v2-profile-head ps-expanded">
                              <ProfileCompletionCta onClick={profileCompletionCta.onClick} placement="header" t={t} />
		              </div>
		            </div>
                        ) : (
                          <div className="ps-tryon-v2-result-copy ps-single-result-copy">
                            <h3 className="ps-tryon-v2-title">{t("Your Suggested Size")}</h3>
                            <p className="ps-tryon-v2-subtitle">{t("Tap the card for detailed breakdown")}</p>
                          </div>
                        )}
                        {profileCompletionCta ? <div className="ps-tryon-v2-sep" /> : null}
                        <div className="ps-tryon-sr-cards-v2">
                          <button
                            className={`ps-tryon-sr-card-v2 ps-summary-card ps-full${pendingCustomSizes[sectionName] ? " ps-overridden" : ""}`}
                            onClick={() => setActiveSection(sectionName)}
                            type="button"
                          >
                            <div className="ps-tryon-sr-card-v2-text ps-summary-card-layout">
                              <span className="ps-tryon-sr-card-v2-summary-title">
                                {cardTitle}
                              </span>
                              <span className={`ps-tryon-sr-card-v2-value${isLong ? " ps-long" : ""}`}>
                                {labelText}
                              </span>
                              <span
                                className={`ps-tryon-sr-card-v2-rec-pill${pendingCustomSizes[sectionName] ? " is-overridden" : ""}`}
                              >
                                {badgeText}
                              </span>
                              <span className="ps-tryon-sr-card-v2-view">
                                {guideOnlyResult ? t("VIEW CHART") : t("VIEW DETAILS")} <span aria-hidden="true">&rsaquo;</span>
                              </span>
                            </div>
                          </button>
                        </div>
                            </>
                          );
                        })()}
                        {!showRagResultGarments && productCarouselNode}
                        <div className="ps-tryon-v2-result-actions">
                          <button
                            className="ps-bp-back-btn"
                            onClick={() => {
                              if (resultImageUrl) onResetTryOn?.();
                              setView("body-profile");
                            }}
                            type="button"
                          >
                            <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {t("Back")}
                          </button>
	                          {canAddToBag || isHistoryResult || (resultImageUrl && !tryOnProcessing) || vtoExcluded ? (
	                            renderShoppingCta()
	                          ) : (
	                            <button
	                              className="ps-tryon-v2-cta"
	                              style={{ marginTop: 0 }}
	                              disabled={tryOnProcessing}
	                              onClick={handleSingleTryOn}
	                              type="button"
	                            >
	                              <CameraIcon size={14} /> {tryOnProcessing ? t("Processing...") : t("Try It On")}
	                            </button>
	                          )}
                        </div>
	                      </>
	                    )}
	              </div>
	              {isOutfitPieceDetail && selectedRagItem && (
	                <OutfitPieceProductPanel piece={selectedRagItem} t={t} />
	              )}
	            </div>
              );
            })()
          )}

          {/* Photo guide overlay — hidden for footwear (no VTO flow). Eyewear
              and headwear get the same Do/Don't guide as apparel; the copy
              further in adapts to accessory photo variants. */}
          {showPhotoGuide && !vtoExcluded && (
            <div className="ps-tryon-sr-chart-overlay">
              {isMobile ? (
                /* ── Mobile: same layout as the AI-sizing photo step
                   (PhotoStepMobile) — title + subtitle, large preview,
                   checklist card, primary CTA + RETAKE secondary. ── */
                <div className="ps-bp-wrapper" style={{ padding: "16px 16px 0", background: "var(--ps-bg-primary)" }}>
                  <div className="ps-pm-root">
                    <div className="ps-pm-header">
                      <h2 className="ps-pm-title">{t("Review your photo")}</h2>
                      <p className="ps-pm-subtitle">
                        {measurementType === "face"
                          ? t("A clear, front-facing face photo — no glasses on — gives us the most accurate try-on.")
                          : measurementType === "head"
                          ? t("Face the camera with your head and shoulders in frame, leaving space above your head.")
                          : t("Ensure your full body is visible for the most accurate virtual try-on.")}
                      </p>
                    </div>

                    <input ref={guideInputRef} type="file" accept="image/jpeg,image/png,image/webp,image/avif" style={{ display: "none" }}
                      onChange={e => { const f = e.target.files?.[0]; if (f) setGuideFile(f); }} />

                    <div className="ps-pm-preview">
                      {guideFile && guidePreviewUrl ? (
                        <>
                          <img src={guidePreviewUrl} alt={t("Your photo")} className="ps-pm-preview-img" />
                          <button
                            type="button"
                            className="ps-pm-preview-remove"
                            onClick={() => setGuideFile(null)}
                            aria-label={t("Remove photo")}
                          >
                            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" width="14" height="14">
                              <line x1="18" y1="6" x2="6" y2="18" />
                              <line x1="6" y1="6" x2="18" y2="18" />
                            </svg>
                          </button>
                        </>
                      ) : (
                        <button
                          type="button"
                          className="ps-pm-preview-empty"
                          onClick={() => guideInputRef.current?.click()}
                        >
                          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" width="32" height="32">
                            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                            <polyline points="17 8 12 3 7 8" />
                            <line x1="12" y1="3" x2="12" y2="15" />
                          </svg>
                          <span className="ps-pm-preview-empty-title">
                            {measurementType === "face" || measurementType === "head" ? t("Upload front face") : t("Upload your photo")}
                          </span>
                          <span className="ps-pm-preview-empty-hint">
                            {measurementType === "face" || measurementType === "head" ? t("Click or drag a front-facing photo") : t("Click or drag a full-body photo")}
                          </span>
                          <span className="ps-pm-preview-empty-format">{t("JPEG, PNG, WebP, AVIF (max 10MB)")}</span>
                        </button>
                      )}
                    </div>

                    <div className="ps-pm-checklist">
                      <div className="ps-pm-checklist-icon">
                        <svg viewBox="0 0 24 24" fill="currentColor" width="14" height="14">
                          <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z" />
                        </svg>
                      </div>
                      <div className="ps-pm-checklist-body">
                        <div className="ps-pm-checklist-title">{t("Checklist for accuracy")}</div>
                        <ul className="ps-pm-checklist-items">
                          {measurementType === "face" ? (
                            <>
                              <li>{t("Face the camera directly at eye level")}</li>
                              <li>{t("Remove any glasses you're wearing")}</li>
                              <li>{t("Good lighting, plain background")}</li>
                            </>
                          ) : measurementType === "head" ? (
                            <>
                              <li>{t("Head and shoulders in frame")}</li>
                              <li>{t("Leave space above your head")}</li>
                              <li>{t("Good lighting, plain background")}</li>
                            </>
                          ) : (
                            <>
                              <li>{t("Wear fitted, simple clothing")}</li>
                              <li>{t("Standing 2-3 meters from camera")}</li>
                              <li>{t("Neutral background with good lighting")}</li>
                            </>
                          )}
                        </ul>
                      </div>
                    </div>
                    <div className="ps-bpm-spacer" />

                    <div className="ps-bpm-bottom">
                      <button
                        type="button"
                        className="ps-pm-primary-btn"
                        disabled={!guideFile}
                        onClick={() => {
                          if (!guideFile) return;
                          // Pass the file directly to handleTryOnSubmit so it
                          // never depends on parent state propagation. Also
                          // sync parent state so the result view sees the
                          // right preview/file.
                          handleFileSelect(guideFile);
                          handleTryOnSubmit(guideFile);
                          setShowPhotoGuide(false);
                          // Don't null guideFile here — that revokes the blob
                          // URL while the photo guide is still tearing down.
                          // Let the next openings re-set it.
                        }}
                      >
                        {t("START TRY-ON")}
                      </button>
                      {guideFile ? (
                        <button
                          type="button"
                          className="ps-pm-secondary-btn"
                          onClick={() => guideInputRef.current?.click()}
                        >
                          {t("RETAKE PHOTO")}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="ps-pm-secondary-btn"
                          onClick={() => { setShowPhotoGuide(false); setGuideFile(null); }}
                        >
                          {t("BACK")}
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
              <div style={{ display: "flex", flexDirection: "column", padding: "1.5vw", width: "100%", height: "100%", background: "var(--ps-bg-primary)", borderRadius: "0.8vw", overflow: "hidden" }}>
                <div style={{ display: "flex", gap: "1.2vw", flex: 1, alignItems: "stretch", minHeight: 0, overflow: "hidden" }}>
                {/* Left: Upload area — no border when photo uploaded, hover to change */}
                <div
                  onClick={() => guideFile ? guideInputRef.current?.click() : guideInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setGuideDragOver(true); }}
                  onDragLeave={() => setGuideDragOver(false)}
                  onDrop={e => { e.preventDefault(); setGuideDragOver(false); const f = e.dataTransfer.files[0]; if (f) setGuideFile(f); }}
                  style={{
                    flex: 1, display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
                    border: guideFile ? "none" : "2px dashed var(--ps-border-color)",
                    borderRadius: "0.5vw", cursor: "pointer", position: "relative",
                    background: "var(--ps-bg-secondary)", overflow: "hidden",
                  }}
                  className="ps-tryon-upload-hover"
                >
                  {guideFile && guidePreviewUrl ? (
                    <>
                      <img src={guidePreviewUrl} alt={t("preview")} style={{ width: "100%", height: "100%", objectFit: "contain" }} />
                      <div className="ps-tryon-upload-hover-overlay" style={{ position: "absolute", inset: 0, display: "flex", alignItems: "center", justifyContent: "center", background: "rgba(0,0,0,0.4)", opacity: 0, transition: "opacity 0.2s", borderRadius: "0.5vw" }}>
                        <span style={{ color: "#fff", fontSize: "0.8vw", fontWeight: 600 }}>{t("Click to change photo")}</span>
                      </div>
                    </>
                  ) : (
                    <>
                      <UploadIcon size={32} />
                      <span style={{ fontSize: "0.85vw", fontWeight: 600, color: "var(--ps-text-primary)", marginTop: "0.5vw" }}>{t("Upload your photo")}</span>
                      <span style={{ fontSize: "0.6vw", color: "var(--ps-text-muted)", marginTop: "0.2vw" }}>
                        {measurementType === "face"
                          ? t("Click or drag a close-up face photo")
                          : measurementType === "head"
                          ? t("Click or drag a head-and-shoulders photo")
                          : t("Click or drag a full-body photo")}
                      </span>
                    </>
                  )}
                  <input ref={guideInputRef} type="file" accept="image/jpeg,image/png,image/webp" style={{ display: "none" }}
                    onChange={e => { const f = e.target.files?.[0]; if (f) setGuideFile(f); }} />
                </div>

                {/* Right: Guidelines */}
                <div style={{ flex: 1, display: "flex", flexDirection: "column", justifyContent: "center", gap: "0.6vw" }}>
                  <div style={{ fontSize: "0.85vw", fontWeight: 700, color: "var(--ps-text-primary)", marginBottom: "0.3vw" }}>{t("How to take the best photo")}</div>
                  <div style={{ background: "#ddfbe7", borderRadius: "0.5vw", padding: "0.6vw 0.8vw" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.3vw", marginBottom: "0.3vw" }}>
                      <span style={{ color: "#1c9d4c", fontSize: "0.75vw", fontWeight: 700 }}>✓</span>
                      <span style={{ color: "#1c9d4c", fontSize: "0.65vw", fontWeight: 600 }}>{t("Do")}</span>
                    </div>
                    <div style={{ fontSize: "0.58vw", color: "var(--ps-text-primary)", lineHeight: 1.8 }}>
                      {measurementType === "face" ? (
                        <>
                          {t("Face the camera directly, centered in frame")}<br/>
                          {t("Use natural, even lighting (e.g. near a window)")}<br/>
                          {t("Keep hair away from your face and ears")}<br/>
                          {t("Choose a plain, light background")}
                        </>
                      ) : measurementType === "head" ? (
                        <>
                          {t("Face the camera with head and shoulders in frame")}<br/>
                          {t("Leave some space above your head")}<br/>
                          {t("Use natural, even lighting")}<br/>
                          {t("Choose a plain, light background")}
                        </>
                      ) : (
                        <>
                          {t("Stand facing the camera with your full body in frame")}<br/>
                          {t("Use natural or even lighting")}<br/>
                          {t("Wear fitted, simple clothing")}<br/>
                          {t("Stand straight and still, arms relaxed")}
                        </>
                      )}
                    </div>
                  </div>
                  <div style={{ background: "#ffe2e2", borderRadius: "0.5vw", padding: "0.6vw 0.8vw" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.3vw", marginBottom: "0.3vw" }}>
                      <span style={{ color: "#e7000b", fontSize: "0.75vw", fontWeight: 700 }}>✗</span>
                      <span style={{ color: "#e7000b", fontSize: "0.65vw", fontWeight: 600 }}>{t("Don't")}</span>
                    </div>
                    <div style={{ fontSize: "0.58vw", color: "var(--ps-text-primary)", lineHeight: 1.8 }}>
                      {measurementType === "face" ? (
                        <>
                          {t("Don't wear sunglasses or a hat in the photo")}<br/>
                          {t("Don't tilt or turn your head")}<br/>
                          {t("Don't use strong backlighting or flash")}<br/>
                          {t("Don't apply filters or edits")}
                        </>
                      ) : measurementType === "head" ? (
                        <>
                          {t("Don't wear a hat in the photo")}<br/>
                          {t("Don't crop out the top of your head")}<br/>
                          {t("Don't use strong backlighting or flash")}<br/>
                          {t("Don't apply filters or edits")}
                        </>
                      ) : (
                        <>
                          {t("Don't wear loose or baggy clothing")}<br/>
                          {t("Don't sit, pose, or bend")}<br/>
                          {t("Don't take mirror photos or selfies")}<br/>
                          {t("Don't apply filters or edits")}
                        </>
                      )}
                    </div>
                  </div>

                  {/* Pro Tip */}
                  <div style={{ background: "rgba(59,130,246,0.08)", border: "1px solid rgba(59,130,246,0.2)", borderRadius: "0.5vw", padding: "0.5vw 0.8vw" }}>
                    <div style={{ display: "flex", alignItems: "center", gap: "0.3vw", marginBottom: "0.2vw" }}>
                      <SparkleIcon size={12} />
                      <span style={{ color: "var(--ps-accent)", fontSize: "0.65vw", fontWeight: 700 }}>{t("Pro Tip")}</span>
                    </div>
                    <div style={{ fontSize: "0.55vw", color: "var(--ps-text-secondary)", lineHeight: 1.7 }}>
                      {measurementType === "face"
                        ? t("A clear, well-lit face photo gives the most accurate eyewear try-on.")
                        : measurementType === "head"
                        ? t("A clear head-and-shoulders photo with space above your head gives the most accurate headwear try-on.")
                        : t("Our AI works best with front-facing, full-body photos in fitted clothing. Better photos = more accurate virtual try-on!")}
                    </div>
                  </div>

                </div>
                </div>

                {/* Bottom: Back + Start Try-On */}
                <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginTop: "0.8vw", gap: "0.5vw", flexShrink: 0 }}>
                  <button className="ps-bp-back-btn" onClick={() => setShowPhotoGuide(false)} type="button">
                    <span className="ps-bp-back-arrow" aria-hidden="true"><ArrowLeftIcon /></span> {t("Back")}
                  </button>
                  <button className="ps-tryon-v2-cta" style={{ marginTop: 0 }} disabled={!guideFile} onClick={() => {
                    if (!guideFile) return;
                    // Pass the file directly so handleTryOnSubmit doesn't
                    // race against batched parent state updates and the
                    // blob URL stays valid until the photo guide unmounts.
                    handleFileSelect(guideFile);
                    handleTryOnSubmit(guideFile);
                    setShowPhotoGuide(false);
                  }}>
                    <CameraIcon size={14} /> {t("Start Try-On")}
                  </button>
                </div>
              </div>
              )}
            </div>
          )}

          {/* Full size chart overlay — all sections */}
          {showFullChart && sizeGuide && (
            <div className="ps-tryon-sr-chart-overlay">
              <div className="ps-tryon-sr-chart-modal">
                <div className="ps-tryon-sr-chart-header">
                  <h4>{t("Full Size Chart")}</h4>
                  <button className="ps-tryon-sr-chart-close" onClick={() => setShowFullChart(false)}>✕</button>
                </div>
                <div className="ps-tryon-sr-chart-scroll">
                  {/* If multi-section, show each section */}
                  {sizeGuide.sections && Object.keys(sizeGuide.sections).length > 1 ? (
                    Object.entries(sizeGuide.sections).map(([secName, sec]) => {
                      const secResult = sizingResult?.sections?.[secName];
                      const recSize = secResult?.recommendedSize || "";
                      const secSizeColIdx = sec.headers.findIndex(h => /size|taglia|größe|taille|fit|length/i.test(h.trim()));
                      const sIdx = secSizeColIdx >= 0 ? secSizeColIdx : 0;
                      const sHdr = sec.headers[sIdx] || "Size";
                      return (
                        <div key={secName} className="ps-tryon-chart-section">
                          <div className="ps-tryon-chart-section-title">
                            <span>{secName}</span>
                            {recSize && <span className="ps-tryon-chart-section-rec">{t("Your size")}: <strong>{recSize}</strong></span>}
                          </div>
                          <div className="ps-tryon-sr-chart-table-wrap">
                            <table className="ps-tryon-sr-chart-table">
                              <thead>
                                <tr>
                                  {sec.headers.map((h, i) => (
                                    <th key={i} className={i === sIdx ? "ps-size-col" : ""}>{h}</th>
                                  ))}
                                </tr>
                              </thead>
                              <tbody>
                                {sec.rows.map((row, ri) => {
                                  const size = cellValFn(row, sIdx, sHdr);
                                  const isActive = size === recSize;
                                  return (
                                    <tr key={ri} className={isActive ? "ps-active-row" : ""}>
                                      {sec.headers.map((h, ci) => (
                                        <td key={ci} className={ci === sIdx ? "ps-size-col" : ""}>{cellValFn(row, ci, h)}</td>
                                      ))}
                                    </tr>
                                  );
                                })}
                              </tbody>
                            </table>
                          </div>
                        </div>
                      );
                    })
                  ) : sizeGuide.headers && sizeGuide.rows ? (
                    /* Single section fallback */
                    <div className="ps-tryon-sr-chart-table-wrap">
                      <table className="ps-tryon-sr-chart-table">
                        <thead>
                          <tr>
                            {sizeGuide.headers.map((h, i) => {
                              const isActive = i === sizeColIdx;
                              return (
                                <th key={i} className={isActive ? "ps-size-col" : ""}>{h}</th>
                              );
                            })}
                          </tr>
                        </thead>
                        <tbody>
                          {sizeGuide.rows.map((row, ri) => {
                            const size = cellVal(row, sizeColIdx, sizeHeader);
                            const isActive = size === displaySize;
                            return (
                              <tr key={ri} className={isActive ? "ps-active-row" : ""}>
                                {sizeGuide.headers!.map((h, ci) => (
                                  <td key={ci} className={ci === sizeColIdx ? "ps-size-col" : ""}>{cellVal(row, ci, h)}</td>
                                ))}
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  ) : null}
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
